import { useState, useEffect, useRef, useMemo } from "react";

// ================================================================
// AL-AWAMA DISTINCTIVE TRADING EST. — COMPLETE WEBSITE v3
// Navy #0D1B2E | Amber #E8A020 | White #FFFFFF
// Navbar: Products with MEGA DROPDOWN showing all categories
// ================================================================

// ── Backend API ──────────────────────────────────────────────────
// FIX: this was hardcoded to localhost, which only works on the developer's
// own machine — deployed to a real host, every visitor's browser would try
// to reach "localhost:5000" on THEIR OWN computer and the whole site would
// look broken (nothing would load). It now reads REACT_APP_API_URL, a
// standard CRA build-time env var, and only falls back to localhost when
// that isn't set (i.e. local development). Before running `npm run build`
// for deployment, set REACT_APP_API_URL in a `.env.production.local` file
// (see the new `.env.production.example` in this folder) to the backend's
// real, public URL — e.g. https://api.alawamatrading.com/api.
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Thin fetch helper: JSON in, JSON out, optional bearer token, throws with
// the server's message on failure so callers can just try/catch.
const apiFetch = async (path, options = {}, token = null) => {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  let data;
  try { data = await res.json(); } catch { data = {}; }
  if (!res.ok) {
    const err = new Error(data.message || `Request failed (${res.status})`);
    err.data = data;
    throw err;
  }
  return data;
};

const C = {
  navy:       "#F0F5FA",
  navyLight:  "#FFFFFF",
  navyMid:    "#F8FAFC",
  navyBorder: "#DDE4ED",
  navyDeep:   "#0D1B2E",
  navyHero:   "#0D1B2E",
  amber: "#E8A020", amberDark: "#C8880A",
  amberGlow:   "rgba(232,160,32,0.12)",
  amberBorder: "rgba(232,160,32,0.35)",
  white:     "#FFFFFF",
  text:      "#1E293B",
  textMuted: "#5A6A80",
  textDim:   "#8FA3BC",
  green: "#16A34A", red: "#DC2626",
};

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{background:${C.navy};color:${C.text};font-family:'DM Sans',sans-serif;-webkit-font-smoothing:antialiased}
::-webkit-scrollbar{width:5px}
::-webkit-scrollbar-track{background:${C.skyLight}}
::-webkit-scrollbar-thumb{background:${C.navyBorder};border-radius:3px}
::-webkit-scrollbar-thumb:hover{background:${C.amber}}
::selection{background:${C.navyDeep};color:#FFFFFF}
input,select,textarea,button{font-family:'DM Sans',sans-serif}

@keyframes fadeUp{from{opacity:0;transform:translateY(26px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
@keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes counter{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes cardGlow{0%{box-shadow:0 12px 40px rgba(232,160,32,0.22)}50%{box-shadow:0 16px 50px rgba(232,160,32,0.35)}100%{box-shadow:0 12px 40px rgba(232,160,32,0.22)}}

.fu{animation:fadeUp .7s ease forwards}
.fu1{animation:fadeUp .7s .1s ease forwards;opacity:0}
.fu2{animation:fadeUp .7s .22s ease forwards;opacity:0}
.fu3{animation:fadeUp .7s .36s ease forwards;opacity:0}
.fu4{animation:fadeUp .7s .5s ease forwards;opacity:0}

.nav-link{
  background:none;border:none;cursor:pointer;
  transition:all .2s;font-family:'DM Sans',sans-serif;
  position:relative;
}
.nav-link:hover{
  color:${C.navyDeep}!important;
  transform:translateY(-1px);
}
.nav-link::after{
  content:'';position:absolute;bottom:-2px;left:50%;right:50%;
  height:2px;background:${C.amber};
  transition:all .22s ease;border-radius:2px;
}
.nav-link:hover::after{left:10%;right:10%}
.btn-amber{
  background:${C.amber};border:none;cursor:pointer;color:#FFFFFF;font-weight:800;
  transition:all .22s cubic-bezier(.175,.885,.32,1.275);
  font-family:'Barlow Condensed',sans-serif;letter-spacing:.8px;
  position:relative;overflow:hidden;
}
.btn-amber::after{
  content:'';position:absolute;inset:0;
  background:linear-gradient(135deg,rgba(255,255,255,0.15) 0%,transparent 60%);
  opacity:0;transition:opacity .2s;
}
.btn-amber:hover{
  background:${C.amberDark};
  transform:translateY(-3px) scale(1.04);
  box-shadow:0 8px 28px rgba(232,160,32,.45), 0 2px 8px rgba(0,0,0,.15);
}
.btn-amber:hover::after{opacity:1}
.btn-amber:active{transform:translateY(-1px) scale(1.01)}
.btn-ghost{
  background:none;cursor:pointer;
  transition:all .22s cubic-bezier(.175,.885,.32,1.275);
  font-family:'DM Sans',sans-serif;
}
.btn-ghost:hover{
  border-color:${C.amber}!important;
  color:${C.amberDark}!important;
  background:${C.amberGlow}!important;
  transform:translateY(-2px) scale(1.03);
  box-shadow:0 6px 20px rgba(232,160,32,.2);
}
.btn-ghost:active{transform:translateY(0) scale(1.01)}
.btn-navy{
  background:${C.navyLight};border:1.5px solid ${C.navyBorder};
  cursor:pointer;color:${C.textMuted};
  transition:all .22s cubic-bezier(.175,.885,.32,1.275);
  box-shadow:0 1px 3px rgba(0,0,0,0.06);
}
.btn-navy:hover{
  border-color:${C.navyDeep};
  color:${C.navyDeep};
  background:${C.skyLight};
  transform:translateY(-2px);
  box-shadow:0 5px 18px rgba(21,101,192,.18);
}
.btn-navy:active{transform:translateY(0)}
.card{
  transition:transform .22s ease, box-shadow .22s ease, border-color .22s ease;
  box-shadow:0 1px 6px rgba(0,0,0,0.08);
}
.card:hover{
  border-color:${C.amber}!important;
  transform:translateY(-5px) scale(1.025)!important;
  box-shadow:0 12px 40px rgba(232,160,32,0.22), 0 2px 12px rgba(0,0,0,0.10)!important;
}
.input-field{background:${C.navyLight};border:1.5px solid ${C.navyBorder};border-radius:8px;padding:12px 14px;color:${C.text};font-size:14px;outline:none;transition:all .2s;width:100%;box-shadow:inset 0 1px 3px rgba(0,0,0,0.04)}
.input-field:focus{border-color:${C.amber};box-shadow:0 0 0 3px rgba(232,160,32,0.12)}
.input-field::placeholder{color:${C.textDim}}
.tag{background:${C.amberGlow};border:1px solid ${C.amberBorder};color:${C.amberDark};font-size:10px;font-weight:700;padding:2px 8px;border-radius:4px;letter-spacing:.5px}

/* MEGA DROPDOWN */
.prod-slide{position:relative;overflow:hidden;flex-shrink:0}
.prod-slide-name{position:absolute;inset:0;background:linear-gradient(0deg,rgba(13,27,46,.88) 0%,rgba(13,27,46,.15) 60%,transparent 100%);color:#FFFFFF;display:flex;align-items:flex-end;padding:10px;font-size:11px;font-weight:700;opacity:0;transition:opacity .22s ease;text-align:left}
.prod-slide:hover .prod-slide-name{opacity:1}
.float-fab{transition:all .22s cubic-bezier(.175,.885,.32,1.275);box-shadow:0 4px 16px rgba(0,0,0,.25)}
.float-fab:hover{transform:translateY(-3px) scale(1.08);box-shadow:0 8px 24px rgba(0,0,0,.35)}

.dropdown-menu{
  position:absolute;top:calc(100% + 1px);left:50%;transform:translateX(-50%);
  background:#FFFFFF;border:1px solid ${C.navyBorder};border-top:3px solid ${C.navyDeep};
  border-radius:0 0 14px 14px;
  box-shadow:0 24px 60px rgba(0,0,0,.15);
  animation:slideDown .2s ease;
  min-width:820px;
  z-index:999;
  max-height:80vh;
  overflow-y:auto;
}
.drop-cat:hover{background:${C.amberGlow}!important;color:${C.amberDark}!important}
.drop-sub:hover{color:${C.amberDark}!important;padding-left:18px!important}
.svc-dropdown-menu{
  position:absolute;top:calc(100% + 1px);left:50%;transform:translateX(-50%);
  background:#FFFFFF;border:1px solid ${C.navyBorder};border-top:3px solid ${C.navyDeep};
  border-radius:0 0 12px 12px;
  box-shadow:0 20px 48px rgba(0,0,0,.15);
  animation:slideDown .2s ease;
  min-width:210px;
  z-index:999;
  padding:6px;
}
.svc-drop-item:hover{background:${C.amberGlow}!important;color:${C.amberDark}!important}

/* ── RESPONSIVE ──────────────────────────────────────────────
   Everything else on this site is inline-styled (no external CSS file),
   so real @media breakpoints live here and win over inline styles via
   !important. .rgrid-2 marks every "two columns side by side" section
   (hero, About, Contact, stat blocks, form field pairs) so it can stack
   to one column instead of squeezing on phones/tablets. */
html,body{overflow-x:hidden}
@media (max-width:900px){
  .rgrid-2{grid-template-columns:1fr!important}
  .footer-grid{grid-template-columns:1fr 1fr!important}
}
@media (max-width:640px){
  .rgrid-2{gap:20px!important}
  .footer-grid{grid-template-columns:1fr!important}
}
`;

// ================================================================
// DATA
// ================================================================
// Every category carries `subs` (English) and `subsAr` (Arabic) in the same
// order, index-for-index — subLabel(cat, sub, lang) below looks up the
// matching translation by position so the whole site (including subcategory
// tiles, search, and the services request form) can show Arabic without a
// second lookup table.
const CATEGORIES = [
  {
    id: 1, name: "Marine", nameAr: "البحرية", icon: "⚓", slug: "marine",
    subs: ["Engine Parts by Brand (Volvo Penta, Yamaha, MAN, MTU, Cummins, Perkins, Kohler, ZF)","Engines & Outboard Motors","Deck & Mooring Hardware","Navigation & Electronics","Pumps & Valves","Life Saving & Fire Fighting","Steering, Cranes & Propulsion","Electrical & Lighting","Deck Furniture, Doors & Windows","Refrigeration & Galley Equipment","Dredging & Underwater Equipment","Containers & Cargo Handling","Anodes & Corrosion Protection","Spare Parts & General Equipment"],
    subsAr: ["قطع غيار المحركات حسب العلامة التجارية (فولفو بنتا، ياماها، مان، إم تي يو، كامنز، بيركنز، كولر، زد إف)","المحركات والمحركات الخارجية","معدات السطح والإرساء","الملاحة والإلكترونيات","المضخات والصمامات","معدات الإنقاذ ومكافحة الحرائق","التوجيه والرافعات والدفع","الكهرباء والإضاءة","أثاث السطح والأبواب والنوافذ","التبريد ومعدات المطبخ","معدات الجرف وتحت الماء","الحاويات ومناولة البضائع","الأنودات والحماية من التآكل","قطع الغيار والمعدات العامة"],
  },
  {
    id: 2, name: "Solar Systems", nameAr: "الأنظمة الشمسية", icon: "☀️", slug: "solar",
    subs: ["Solar Panels & Kits","Inverters & Charge Controllers","Batteries & Power Storage","Solar Water Pumps & Heaters","Solar Street & Security Lighting","Off-Grid Power Systems"],
    subsAr: ["الألواح الشمسية والأطقم","العاكسات ومنظمات الشحن","البطاريات وتخزين الطاقة","مضخات وسخانات المياه الشمسية","إضاءة الشوارع والأمن الشمسية","أنظمة الطاقة خارج الشبكة"],
  },
  {
    id: 3, name: "Railways", nameAr: "السكك الحديدية", icon: "🚆", slug: "railways",
    subs: ["Wagons & Rolling Stock","Bogies & Wheelsets","Braking Systems","Track & Fastening Materials","Maintenance Machinery","Locomotive Engines & Spares"],
    subsAr: ["العربات والمعدات المتحركة","البوجيات ومجموعات العجلات","أنظمة الفرامل","مواد السكك والتثبيت","معدات الصيانة","محركات القاطرات وقطع الغيار"],
  },
  {
    id: 4, name: "Automobiles & Heavy Machinery", nameAr: "السيارات والمعدات الثقيلة", icon: "🚛", slug: "automobiles-heavy-machinery",
    subs: ["Heavy Construction Equipment","Commercial Vehicles","Engine & Drivetrain Parts","Hydraulic Systems","Tires & Undercarriage","Maintenance Tools"],
    subsAr: ["معدات البناء الثقيلة","المركبات التجارية","قطع غيار المحرك ونظام نقل الحركة","الأنظمة الهيدروليكية","الإطارات والهيكل السفلي","أدوات الصيانة"],
  },
  {
    id: 5, name: "Safety Equipment", nameAr: "معدات السلامة", icon: "🦺", slug: "safety-equipment",
    subs: ["Personal Protective Equipment (PPE)","Fire Fighting Equipment","Breathing Apparatus","First Aid & Rescue","Industrial Safety Signage","Confined Space Safety"],
    subsAr: ["معدات الحماية الشخصية","معدات مكافحة الحرائق","أجهزة التنفس","الإسعافات الأولية والإنقاذ","لافتات السلامة الصناعية","السلامة في الأماكن المغلقة"],
  },
  {
    id: 6, name: "Police & Military Tactical Gear", nameAr: "معدات الشرطة والجيش التكتيكية", icon: "🛡️", slug: "police-military-tactical",
    subs: ["Body Armor & Ballistic Protection","Tactical Vests & Gear","Traffic & Radar Equipment","Surveillance & Communication","Riot & Crowd Control","Drones & Monitoring","Shooting Ranges & Targets","Containers, Forklifts, Cranes & Generator Sets"],
    subsAr: ["الدروع الواقية والحماية من الرصاص","السترات والمعدات التكتيكية","معدات المرور والرادار","المراقبة والاتصالات","مكافحة الشغب والسيطرة على الحشود","الطائرات المسيرة والمراقبة","ميادين الرماية والأهداف","الحاويات والرافعات الشوكية والأوناش ومولدات الطاقة"],
  },
  {
    id: 7, name: "Uniforms & Shirts", nameAr: "الزي الموحد والقمصان", icon: "👕", slug: "uniforms-shirts",
    subs: ["Security Guard Uniforms","Military & Police Uniforms","Safety Workwear","Hospital & Medical Scrubs","Corporate Uniforms","Custom Embroidery & Badges"],
    subsAr: ["أزياء حراس الأمن","الأزياء العسكرية والشرطية","ملابس العمل الآمنة","أزياء المستشفيات والطاقم الطبي","الأزياء المؤسسية","التطريز والشارات المخصصة"],
  },
  {
    id: 8, name: "Medical Equipment", nameAr: "المعدات الطبية", icon: "⚕️", slug: "surgical-equipment",
    subs: ["Surgical Equipment","Dental Equipment","Medical Machines","Diagnostic Equipment","Sterilization Equipment","Disposable Medical Supplies","Operating Room Equipment"],
    subsAr: ["المعدات الجراحية","معدات طب الأسنان","الأجهزة الطبية","معدات التشخيص","معدات التعقيم","المستلزمات الطبية للاستخدام مرة واحدة","معدات غرفة العمليات"],
  },
  {
    id: 9, name: "Souvenirs & Medals", nameAr: "الهدايا التذكارية والميداليات", icon: "🏅", slug: "souvenirs-medals",
    subs: ["Acrylic Awards & Trophies","Etched Plaques & Gifts","3D Printed Metal Medals","Custom Corporate Souvenirs","Commemorative Coins","Engraved Nameplates"],
    subsAr: ["الجوائز والكؤوس الأكريليك","الدروع المحفورة والهدايا","الميداليات المعدنية المطبوعة ثلاثية الأبعاد","الهدايا التذكارية المؤسسية المخصصة","العملات التذكارية","لوحات الأسماء المحفورة"],
  },
];

// Looks up sub's Arabic translation by matching array position in cat.subs —
// falls back to the English string if lang isn't "ar" or no translation
// exists at that index (e.g. a subcategory added via the admin panel, which
// has no built-in Arabic translation yet).
const subLabel = (cat, sub, lang) => {
  if (lang !== "ar" || !cat) return sub;
  const idx = (cat.subs || []).indexOf(sub);
  return (idx >= 0 && cat.subsAr && cat.subsAr[idx]) ? cat.subsAr[idx] : sub;
};

// Looks up a category's Arabic name from its plain English name (as stored
// on a product's `category` field, or the backend's Category collection —
// both use the same English names as CATEGORIES). Falls back to the English
// name when not in Arabic mode or no match is found (e.g. a category only
// present in the admin DB, not in the static CATEGORIES list).
const catNameAr = (englishName, lang) => {
  if (lang !== "ar" || !englishName) return englishName;
  const cat = CATEGORIES.find(c => c.name === englishName);
  return cat?.nameAr || englishName;
};

const SHOP_PRODUCTS = [
  { id:1, name:"Marine Diesel Injector Pump", sku:"MDP-4421", brand:"Bosch Marine", price:890, originalPrice:1050, category:"engine", stock:14, rating:4.8, reviews:124, tags:["OEM","Diesel"], desc:"High-performance marine diesel injector pump for CAT C7, C9, C12 engines." },
  { id:2, name:"Electric Bilge Pump 2000 GPH", sku:"BPE-2000", brand:"Rule", price:145, originalPrice:175, category:"pumps", stock:3, rating:4.5, reviews:156, tags:["12V","Auto"], desc:"High-capacity submersible bilge pump with automatic float switch." },
  { id:3, name:"VHF Marine Radio 25W DSC", sku:"VHF-5520", brand:"Standard Horizon", price:385, originalPrice:420, category:"nav", stock:7, rating:4.7, reviews:67, tags:["DSC","GPS"], desc:"Fixed-mount VHF with integrated GPS. SASO/CITC approved." },
  { id:4, name:"LED Navigation Light Set", sku:"NLS-LED3", brand:"Perko", price:195, originalPrice:230, category:"lighting", stock:22, rating:4.6, reviews:92, tags:["LED","USCG"], desc:"USCG approved LED navigation lights for vessels up to 65ft." },
  { id:5, name:"Life Ring 30\" USCG Approved", sku:"LR-30CG", brand:"Plastimo", price:89, originalPrice:null, category:"safety", stock:34, rating:5.0, reviews:48, tags:["USCG","SOLAS"], desc:"USCG Type IV throwable device. High-visibility orange with retroreflective tape." },
  { id:6, name:"Stainless Deck Cleat 8-inch", sku:"DCL-3308", brand:"Whitecap", price:45, originalPrice:null, category:"deck", stock:89, rating:4.9, reviews:213, tags:["316 SS"], desc:"Grade 316 stainless steel mooring cleat with polished finish." },
  { id:7, name:"Autopilot Linear Drive", sku:"APL-ST4000", brand:"Simrad", price:1250, originalPrice:1400, category:"nav", stock:5, rating:4.9, reviews:34, tags:["NMEA 2000"], desc:"Heavy-duty autopilot actuator for vessels 30-55ft." },
  { id:8, name:"Electric Windlass 1500W", sku:"EWL-1500V", brand:"Maxwell", price:1850, originalPrice:2100, category:"deck", stock:4, rating:4.9, reviews:29, tags:["Vertical"], desc:"Vertical electric windlass for vessels 40-65ft with chain counter." },
  { id:9, name:"Marine Battery 105Ah AGM", sku:"BAT-105A", brand:"Victron", price:320, originalPrice:380, category:"electrical", stock:12, rating:4.8, reviews:89, tags:["AGM","12V"], desc:"Deep cycle AGM battery. Maintenance-free, vibration resistant." },
  { id:10, name:"Anchor Chain Galv. 3/8\"", sku:"ACH-3816", brand:"Acco Chain", price:4.80, originalPrice:null, category:"mooring", stock:500, rating:4.8, reviews:341, tags:["Per Foot"], desc:"Grade 43 hi-test galvanized anchor chain. Hot-dipped finish." },
  { id:11, name:"Propeller Shaft Seal Kit", sku:"PSS-8832", brand:"Vetus", price:285, originalPrice:null, category:"engine", stock:6, rating:4.6, reviews:88, tags:["Seals"], desc:"Premium shaft seal kit for inboard motor shafts up to 60mm." },
  { id:12, name:"Stainless Seacock 3/4\"", sku:"SCK-75SS", brand:"Groco", price:175, originalPrice:null, category:"pumps", stock:18, rating:4.8, reviews:56, tags:["316 SS","ABYC"], desc:"Ball valve seacock for thru-hull installations." },
];

// Marine engine brands we carry genuine parts catalogs for, our own in-house
// product lines, and well-known industry names for the sectors we supply.
// Presented as brands/standards we work with — not a claim of official
// certified distributorship for the third-party names.
const PARTNERS = [
  "Volvo Penta","Yamaha","Cummins","Perkins","Kohler","MTU","MAN","ZF",
  "AL-AWAMA UNIFORMS","AL-AWAMA SURGICAL & DENTAL","AL-AWAMA SOUVENIRS & MEDALS",
  "Jinko Solar","Canadian Solar","Growatt",
  "Vossloh","Knorr-Bremse","Wabtec",
  "Caterpillar","Komatsu","JCB",
  "3M","Honeywell","MSA Safety",
  "5.11 Tactical","Condor Outdoor","Blackhawk",
];
const SA_CITIES = ["Riyadh","Jeddah","Dammam","Mecca","Medina","Khobar","Jubail","Yanbu","Tabuk","Abha","Jizan","Najran"];

// ================================================================
// TRANSLATIONS (EN / AR)
// ================================================================
const STR = {
  en: {
    dir: "ltr",
    nav: { home:"Home", products:"Products", services:"Services", about:"About Us", contact:"Contact Us", viewAll:"VIEW ALL →" },
    hero: {
      kicker: "TRUSTED SINCE 2013 · JEDDAH, SAUDI ARABIA",
      title1: "AL-AWAMA DISTINCTIVE",
      title2: "TRADING COMPANY",
      subtitle: "Supplying Marine, Solar, Railway, Automobile & Heavy Machinery, Safety, Police & Military Tactical, Uniforms, Surgical and Custom Souvenir equipment across the Kingdom of Saudi Arabia and the Gulf.",
      cta1: "BROWSE PRODUCTS", cta2: "REQUEST A QUOTE",
    },
    about: {
      kicker: "// WHO WE ARE",
      title: "ABOUT AL-AWAMA\nDISTINCTIVE TRADING CO.",
      intro: "Al-Awama Distinctive Trading Company is a Saudi-based multi-sector supplier delivering marine, solar, railway, automotive & heavy machinery, safety, police & military tactical, uniform, surgical, and custom souvenir products to government and private clients across the Kingdom.",
      stats: [
        {icon:"🏆", title:"18+ Years", sub:"Of hands-on experience supplying the defense and industrial sectors in Saudi Arabia."},
        {icon:"🇸🇦", title:"Saudi Defense Partner", sub:"Long-standing relationships supplying quality equipment to Saudi Arabia's defense and government sectors."},
        {icon:"⏱️", title:"Before-Deadline Delivery", sub:"A track record of delivering certified, quality equipment ahead of schedule, every time."},
      ],
      missionKicker: "// OUR MISSION",
      missionTitle: "MISSION & VISION",
      missionP1: "Our mission is to be the most reliable multi-sector trading partner in Saudi Arabia, delivering genuine, certified equipment across marine, solar, railway, automotive, safety, tactical, uniform, surgical and souvenir categories, on time and to specification, every time.",
      missionP2: "Our vision is to grow as the Kingdom's most trusted distinctive trading house, known for quality before deadlines, deep sector expertise built over 18+ years, and long-term partnerships with government, defense, and private-sector clients across Saudi Arabia and the Gulf.",
      ctaBtn: "CONTACT US →",
      metrics: [["18+","Years of Experience"],["9","Industry Sectors Served"],["100%","Quality Before Deadline"],["24hr","Quote Response"]],
      certsKicker: "// LICENSED & REGISTERED",
      certsTitle: "OUR CERTIFICATIONS",
      certRegTitle: "Company Registration Certificate",
      certRegDesc: "Official commercial registration issued to Al-Awama Distinctive Trading Company.",
      certTaxTitle: "Company Tax Certificate",
      certTaxDesc: "Official VAT / tax registration certificate for Al-Awama Distinctive Trading Company.",
      certComingSoon: "Certificate image coming soon",
    },
    contact: {
      kicker: "// REACH US", title: "CONTACT US",
      subtitle: "Have a question about a product or need a bulk order quote? Our team is ready to help.",
      getInTouch: "GET IN TOUCH",
      items: [
        {icon:"📍",title:"Address",lines:["JBSC8373, City 4549, PO 22234","Jeddah, Saudi Arabia"]},
        {icon:"📞",title:"Phone / WhatsApp",lines:["+966 560 264 386"]},
        {icon:"📧",title:"Email",lines:["info@alawamatrading.com","abdulmalik.int@gmail.com"]},
        {icon:"💬",title:"WeChat",lines:["Tap to scan our QR code"],wechat:true},
      ],
    },
    footer: {
      tagline: "A distinctive multi-sector trading company serving Marine, Solar, Railway, Automotive, Safety, Tactical, Uniform, Surgical and Souvenir industries across Saudi Arabia.",
      navigate: "Navigate", categories: "Categories", support: "Support",
      navItems: [["Home","home"],["All Products","products"],["Inspection & Repair","services"],["About Us","about"],["Contact","contact"]],
      supportItems: [["Request a Quote","contact"],["FAQ","contact"],["Track Order","login"],["Admin","admin-login"]],
      rights: "© 2026 Al-Awama Distinctive Trading Company · Jeddah, Saudi Arabia",
    },
    services: {
      kicker: "// INSPECTION & REPAIR SERVICES",
      title: "MAINTENANCE, INSPECTION\n& REPAIR SERVICES",
      intro1: "Beyond supplying genuine parts and equipment, Al-Awama Distinctive Trading Company fields a dedicated team of maintenance engineers across every sector we serve: marine, industrial, automotive, defense, and safety. Whether it's a vessel propulsion system, an industrial generator, a fleet of heavy machinery, or defense and security equipment, our qualified technicians carry out precision inspection and repair work you can trust.",
      intro2: "Our engineers handle vessel repair and technical inspection, and the repair and overhaul of all types of engines — marine, automotive, industrial, and generator engines — plus generator sets, propulsion systems, and industrial and automotive machinery (cranes, forklifts, compressors, hydraulic systems), and defense systems, safety, and technical maintenance, including electrical, electronic, and mechanical repair for security and firefighting equipment. Precision repairs you can trust, at fair prices, with the same quality-before-deadline standard that has defined our work since 2013.",
      formTitle: "REQUEST INSPECTION OR REPAIR",
      formSubtitle: "Fill in the details below and our team will get back to you shortly.",
      fields: {
        industry: "Industry", industryPh: "Select an industry…", other: "Other",
        category: "Category / Equipment Type", categoryPh: "Select a category…", categoryOtherPh: "Please specify the category / equipment type",
        serviceType: "Service Type", inspection: "Inspection", repair: "Repair",
        description: "Description of the Issue / Request", descriptionPh: "Describe the equipment, symptoms, or scope of work…",
        upload: "Upload File (PDF, Excel, Word or Image)", uploadNote: "Attach a photo, spec sheet, or report if you have one.",
        submit: "SUBMIT REQUEST →",
        emailNote: "Your default email app will open with your request pre-filled and addressed to info@alawamatrading.com; please attach your uploaded file before sending, since email links can't carry attachments automatically.",
      },
      successTitle: "REQUEST READY TO SEND",
      successBody: "We've opened your email app with the details filled in. Attach your file (if any) and hit send; our engineers will be in touch shortly.",
    },
  },
  ar: {
    dir: "rtl",
    nav: { home:"الرئيسية", products:"المنتجات", services:"الفحص والصيانة", about:"من نحن", contact:"اتصل بنا", viewAll:"عرض الكل ←" },
    hero: {
      kicker: "موثوقون منذ 2013 · جدة، المملكة العربية السعودية",
      title1: "شركة العوامة المميزة",
      title2: "للتجارة",
      subtitle: "نورّد معدات القطاع البحري، الطاقة الشمسية، السكك الحديدية، السيارات والمعدات الثقيلة، السلامة، المعدات التكتيكية للشرطة والجيش، الزي الموحد، المعدات الجراحية والهدايا التذكارية المخصصة في جميع أنحاء المملكة العربية السعودية ودول الخليج.",
      cta1: "تصفح المنتجات", cta2: "اطلب عرض سعر",
    },
    about: {
      kicker: "// من نحن",
      title: "عن شركة العوامة المميزة\nللتجارة",
      intro: "شركة العوامة المميزة للتجارة هي شركة سعودية متعددة القطاعات، تقوم بتوريد منتجات بحرية وطاقة شمسية وسكك حديدية وسيارات ومعدات ثقيلة ومعدات سلامة ومعدات تكتيكية للشرطة والجيش وأزياء موحدة ومعدات جراحية وهدايا تذكارية مخصصة لعملاء حكوميين وخاصين في جميع أنحاء المملكة.",
      stats: [
        {icon:"🏆", title:"+18 سنة", sub:"من الخبرة العملية في توريد القطاعين الدفاعي والصناعي في المملكة العربية السعودية."},
        {icon:"🇸🇦", title:"شريك القطاع الدفاعي السعودي", sub:"علاقات طويلة الأمد في توريد معدات عالية الجودة لقطاع الدفاع والجهات الحكومية في المملكة."},
        {icon:"⏱️", title:"التسليم قبل الموعد المحدد", sub:"سجل حافل بتسليم معدات معتمدة وعالية الجودة قبل الموعد النهائي في كل مرة."},
      ],
      missionKicker: "// مهمتنا",
      missionTitle: "الرسالة والرؤية",
      missionP1: "مهمتنا أن نكون الشريك التجاري الأكثر موثوقية متعدد القطاعات في المملكة العربية السعودية، من خلال توريد معدات أصلية ومعتمدة في مجالات البحرية والطاقة الشمسية والسكك الحديدية والسيارات والسلامة والمعدات التكتيكية والزي الموحد والمعدات الجراحية والهدايا التذكارية، في الوقت المحدد وبالمواصفات المطلوبة، في كل مرة.",
      missionP2: "رؤيتنا هي أن ننمو لنكون بيت التجارة المميز الأكثر ثقة في المملكة، معروفين بالجودة قبل المواعيد النهائية، وخبرة قطاعية عميقة اكتسبناها على مدى أكثر من 18 عامًا، وشراكات طويلة الأمد مع الجهات الحكومية والدفاعية والقطاع الخاص في المملكة العربية السعودية ودول الخليج.",
      ctaBtn: "اتصل بنا ←",
      metrics: [["+18","سنة خبرة"],["9","قطاعات صناعية"],["100%","الجودة قبل الموعد"],["24 ساعة","الرد على طلب العرض"]],
      certsKicker: "// مرخصون ومسجلون",
      certsTitle: "شهاداتنا",
      certRegTitle: "شهادة السجل التجاري",
      certRegDesc: "السجل التجاري الرسمي الصادر لشركة العوامة المميزة للتجارة.",
      certTaxTitle: "الشهادة الضريبية",
      certTaxDesc: "شهادة التسجيل الضريبي / ضريبة القيمة المضافة الرسمية لشركة العوامة المميزة للتجارة.",
      certComingSoon: "صورة الشهادة ستُضاف قريبًا",
    },
    contact: {
      kicker: "// تواصل معنا", title: "اتصل بنا",
      subtitle: "هل لديك سؤال حول منتج أو تحتاج عرض سعر لطلب بالجملة؟ فريقنا جاهز لمساعدتك.",
      getInTouch: "تواصل معنا",
      items: [
        {icon:"📍",title:"العنوان",lines:["JBSC8373، المدينة 4549، ص.ب 22234","جدة، المملكة العربية السعودية"]},
        {icon:"📞",title:"الهاتف / واتساب",lines:["+966 560 264 386"]},
        {icon:"📧",title:"البريد الإلكتروني",lines:["info@alawamatrading.com","abdulmalik.int@gmail.com"]},
        {icon:"💬",title:"WeChat",lines:["اضغط لمسح رمز الاستجابة السريعة"],wechat:true},
      ],
    },
    footer: {
      tagline: "شركة تجارية مميزة متعددة القطاعات تخدم قطاعات البحرية والطاقة الشمسية والسكك الحديدية والسيارات والسلامة والمعدات التكتيكية والزي الموحد والمعدات الجراحية والهدايا التذكارية في جميع أنحاء المملكة العربية السعودية.",
      navigate: "روابط", categories: "الفئات", support: "الدعم",
      navItems: [["الرئيسية","home"],["كل المنتجات","products"],["الفحص والصيانة","services"],["من نحن","about"],["اتصل بنا","contact"]],
      supportItems: [["طلب عرض سعر","contact"],["الأسئلة الشائعة","contact"],["تتبع الطلب","login"],["الإدارة","admin-login"]],
      rights: "© 2026 شركة العوامة المميزة للتجارة · جدة، المملكة العربية السعودية",
    },
    services: {
      kicker: "// خدمات الفحص والصيانة",
      title: "خدمات الصيانة والفحص\nوالإصلاح",
      intro1: "إلى جانب توريد قطع الغيار والمعدات الأصلية، تمتلك شركة العوامة المميزة للتجارة فريقًا متخصصًا من مهندسي الصيانة في جميع القطاعات التي نخدمها: البحرية والصناعية والسيارات والدفاعية والسلامة. سواء كان الأمر يتعلق بنظام دفع لسفينة، أو مولد صناعي، أو أسطول من المعدات الثقيلة، أو معدات الدفاع والأمن، فإن فنيينا المؤهلين ينفذون أعمال الفحص والإصلاح الدقيقة التي يمكنكم الوثوق بها.",
      intro2: "يتولى مهندسونا إصلاح السفن والفحص الفني، وإصلاح وإعادة تأهيل جميع أنواع المحركات — البحرية والسيارات والصناعية ومحركات المولدات — بالإضافة إلى مولدات الطاقة وأنظمة الدفع، وإصلاح المعدات الصناعية والسيارات (الرافعات، الرافعات الشوكية، الضواغط، الأنظمة الهيدروليكية)، وصيانة الأنظمة الدفاعية والسلامة والصيانة الفنية، بما في ذلك الإصلاح الكهربائي والإلكتروني والميكانيكي لمعدات الأمن ومكافحة الحرائق. إصلاحات دقيقة يمكنكم الوثوق بها، بأسعار عادلة، وبنفس معيار الجودة قبل الموعد النهائي الذي يميز عملنا منذ عام 2013.",
      formTitle: "طلب فحص أو إصلاح",
      formSubtitle: "املأ البيانات أدناه وسيتواصل معك فريقنا في أقرب وقت.",
      fields: {
        industry: "القطاع", industryPh: "اختر القطاع…", other: "أخرى",
        category: "الفئة / نوع المعدة", categoryPh: "اختر الفئة…", categoryOtherPh: "يرجى تحديد الفئة / نوع المعدة",
        serviceType: "نوع الخدمة", inspection: "فحص", repair: "إصلاح",
        description: "وصف المشكلة / الطلب", descriptionPh: "صف المعدة أو الأعراض أو نطاق العمل…",
        upload: "رفع ملف (PDF أو إكسل أو وورد أو صورة)", uploadNote: "أرفق صورة أو ورقة مواصفات أو تقريرًا إن وجد.",
        submit: "إرسال الطلب ←",
        emailNote: "سيفتح تطبيق البريد الإلكتروني الافتراضي لديك مع تعبئة تفاصيل طلبك وموجهًا إلى info@alawamatrading.com، يرجى إرفاق ملفك المرفوع قبل الإرسال، لأن روابط البريد لا يمكنها حمل المرفقات تلقائيًا.",
      },
      successTitle: "الطلب جاهز للإرسال",
      successBody: "لقد فتحنا تطبيق البريد الإلكتروني لديك مع تعبئة التفاصيل. أرفق ملفك (إن وجد) واضغط إرسال، سيتواصل معك مهندسونا في أقرب وقت.",
    },
  },
};

// ================================================================
// UTILS
// ================================================================
const fmt = p => p < 10 ? `$${p.toFixed(2)}` : `$${p.toLocaleString()}`;
const fmtSAR = p => `SAR ${(p * 3.75).toFixed(0)}`;

// Shared labeled text input for forms (AuthPage, CheckoutPage). Defined at
// module scope — NOT inside a component body — so its identity stays stable
// across re-renders. Defining a component inline inside another component's
// render function makes React remount it on every keystroke (new function
// reference each render), which is what was causing focus to drop after
// every character typed on the Create Account / Checkout forms.
const FormInp = ({label,value,onChange,type="text",placeholder="",padding="11px 12px"}) => (
  <div>
    <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{label}</label>
    <input type={type} value={value} placeholder={placeholder} onChange={e=>onChange(e.target.value)} className="input-field" style={{fontSize:13,padding}}/>
  </div>
);

const Stars = ({ r = 5, size = 13 }) => (
  <span>{[...Array(5)].map((_,i) =>
    <span key={i} style={{fontSize:size,color:i<Math.floor(r)?C.amber:"#D1D5DB"}}>
      {i<Math.floor(r)?"★":"☆"}
    </span>
  )}</span>
);

const StockBadge = ({ stock, lang="en" }) => {
  const s = stock===0 ? {l:lang==="ar"?"نفد المخزون":"Out of Stock",c:C.red,bg:"rgba(239,68,68,.1)"}
    : stock<=5 ? {l:lang==="ar"?`تبقّى ${stock} فقط`:`Only ${stock} left`,c:"#B45309",bg:"rgba(245,158,11,.12)"}
    : {l:lang==="ar"?"متوفر":"In Stock",c:"#15803D",bg:"rgba(34,197,94,.12)"};
  return <span style={{fontSize:11,fontWeight:700,color:s.c,background:s.bg,padding:"3px 8px",borderRadius:4}}>{s.l}</span>;
};

const CAT_EMOJI = {"Marine":"⚓","Solar Systems":"☀️","Railways":"🚆","Automobiles & Heavy Machinery":"🚛","Safety Equipment":"🦺","Police & Military Tactical Gear":"🛡️","Uniforms & Shirts":"👕","Medical Equipment":"⚕️","Souvenirs & Medals":"🏅"};
const ProdImg = ({ p, h=180 }) => {
  if (p.image) {
    return (
      <div style={{width:"100%",height:h,background:"#FFFFFF",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
        <img src={p.image} alt={p.name} style={{width:"100%",height:"100%",objectFit:"contain",padding:6}}/>
      </div>
    );
  }
  return (
    <div style={{width:"100%",height:h,background:`linear-gradient(135deg,#1565C0,#0B3D91)`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",inset:0,backgroundImage:`radial-gradient(circle at 40% 50%,rgba(232,160,32,.07) 0%,transparent 60%)`}}/>
      <span style={{fontSize:h*.2,filter:"drop-shadow(0 4px 10px rgba(0,0,0,.5))"}}>{CAT_EMOJI[p.category]||"🔩"}</span>
      <span style={{fontSize:9,letterSpacing:"2px",color:C.textMuted,fontFamily:"monospace"}}>{p.sku}</span>
    </div>
  );
};

// Real product photo (white background, uniform 600x600) with graceful
// fallback to a neutral icon tile when a product has no photo on file.
const RealProdImg = ({ src, name, h=170, folder="marine" }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div style={{width:"100%",height:h,background:"#FFFFFF",display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden",borderBottom:`1px solid ${C.navyBorder}`}}>
        <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt={name||"Al-Awama Distinctive Trading Company"} style={{width:"46%",maxWidth:110,height:"auto",objectFit:"contain",opacity:0.9}}/>
      </div>
    );
  }
  return (
    <div style={{width:"100%",height:h,background:"#FFFFFF",position:"relative",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",borderBottom:`1px solid ${C.navyBorder}`}}>
      <img
        src={`${process.env.PUBLIC_URL}/images/${folder}/${src}`}
        alt={name}
        loading="lazy"
        onError={()=>setFailed(true)}
        style={{width:"100%",height:"100%",objectFit:"contain",padding:8}}
      />
    </div>
  );
};

// Static, pre-photographed catalogs (built from the owner's product photo
// folders + spreadsheets) — one JSON file + image folder per industry.
// Marine's is the largest/original; Solar, Railways and Police & Military
// were added the same way. Any category not listed here falls back to the
// backend admin-uploaded catalog (catImageCards) instead.
const STATIC_CATALOG_MAP = {
  "marine":                  { file: "marine-categories.json",          folder: "marine" },
  "solar":                   { file: "solar-categories.json",           folder: "solar" },
  "railways":                { file: "railway-categories.json",         folder: "railway" },
  "police-military-tactical":{ file: "police-military-categories.json", folder: "police-military" },
};

// Generic, navigational placeholder cards for industries that don't have a
// real photographed catalog or admin-entered products behind them yet
// (unlike Marine/Solar/Railways/Police & Military above, which do). These
// are deliberately generic sub-category labels, not invented product names,
// brands, prices or specs — each card just routes into the Request a Quote
// flow. Shown only when the backend catalog for that category has nothing
// on file (catImageCards is empty), so they step aside automatically once
// real products are added.
const PLACEHOLDER_SUBS = {
  "automobiles-heavy-machinery": ["Heavy Equipment Parts","Commercial Vehicle Parts","Hydraulic Components","Tires & Undercarriage"],
  "safety-equipment":            ["Fire Safety Equipment","PPE & Protective Gear","First Aid Supplies","Safety Signage"],
  "uniforms-shirts":              ["Security & Corporate Uniforms","Workwear & Coveralls","Embroidery & Badges","Custom Apparel"],
  "surgical-equipment":           ["Diagnostic Equipment","Surgical Supplies","Sterilization Equipment","Disposable Medical Supplies"],
  "souvenirs-medals":             ["Custom Awards & Trophies","Engraved Plaques","Corporate Souvenirs","Commemorative Medals"],
};

// A clean, uniform-size illustrative tile for real catalog items that don't
// have a photo on file yet. It is deliberately generic (subcategory icon,
// not a stand-in photo of a specific product) and labeled as such — we never
// substitute another company's product photography for our own inventory.
const IllustrativeImg = ({ category, h=170 }) => (
  <div style={{width:"100%",height:h,background:"#FFFFFF",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,position:"relative",overflow:"hidden",borderBottom:`1px solid ${C.navyBorder}`}}>
    <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt="Al-Awama Distinctive Trading Company" style={{width:"40%",maxWidth:96,height:"auto",objectFit:"contain",opacity:0.9}}/>
    <span style={{fontSize:9,letterSpacing:"1px",color:C.textDim,textTransform:"uppercase",fontWeight:700}}>Representative Image</span>
  </div>
);

// ================================================================
// CATEGORY ICONS — clean original line icons (fallback only, no emoji, no scraped art)
// ================================================================
const PART_COUNT_LABEL = { marine: "10,000+" };
const DEFAULT_PART_COUNT_LABEL = "1,000+";

// Real representative photo per industry (all supplied directly by the user)
const CATEGORY_IMAGE = {
  "marine": "marine.jpg",
  "solar": "solar.jpg",
  "railways": "railways.jpg",
  "automobiles-heavy-machinery": "automobiles-heavy-machinery.jpg",
  "safety-equipment": "safety-equipment.jpg",
  "police-military-tactical": "police-military-tactical.jpg",
  "uniforms-shirts": "uniforms-shirts.jpg",
  "surgical-equipment": "surgical-equipment.jpg",
  "souvenirs-medals": "souvenirs-medals.jpg",
};
// Admin-editable category tile images (Categories page in the admin panel):
// fetched once and shared across every CategoryPhoto instance, falling back
// to the bundled static photo above, and to the hand-drawn icon after that,
// so the site still looks right if the backend is unreachable.
let _categoryImagePromise = null;
const getCategoryImageMap = () => {
  if (!_categoryImagePromise) {
    _categoryImagePromise = fetch(`${API_BASE}/categories`).then(r => r.json()).then(d => {
      const map = {};
      (d.categories || []).forEach(c => { if (c.image) map[c.slug] = c.image; });
      return map;
    }).catch(() => ({}));
  }
  return _categoryImagePromise;
};
const useCategoryImageOverrides = () => {
  const [map, setMap] = useState({});
  useEffect(() => { let alive = true; getCategoryImageMap().then(m => { if (alive) setMap(m); }); return () => { alive = false; }; }, []);
  return map;
};

// Runtime category list = the 9 built-in CATEGORIES (unchanged, same ids/
// slugs/subs/behavior) + any additional ACTIVE category the owner creates in
// the admin panel's Categories page, appended after them. Any admin category
// whose slug matches one of the 9 built-ins is skipped (the built-in wins —
// it already carries the hand-tuned subs/subsAr/icon/static catalog wiring).
// Fetched once (same cached-promise pattern as getCategoryImageMap above)
// and shared across every component that needs "the categories" for
// navigation/lookup, so a category added via the admin panel shows up on
// the site (home grid, Products page, footer, etc.) without a redeploy.
const BUILTIN_SLUGS = new Set(CATEGORIES.map(c => c.slug));
let _allCategoriesPromise = null;
const getAllCategories = () => {
  if (!_allCategoriesPromise) {
    _allCategoriesPromise = fetch(`${API_BASE}/categories`).then(r => r.json()).then(d => {
      const extra = (d.categories || [])
        .filter(c => c.active !== false && c.slug && !BUILTIN_SLUGS.has(c.slug))
        .map((c, i) => ({
          id: `dyn-${c.slug}`,
          name: c.name,
          nameAr: c.nameAr || c.name,
          icon: c.icon || "📦",
          slug: c.slug,
          subs: c.subs || [],
          subsAr: [],
        }));
      return [...CATEGORIES, ...extra];
    }).catch(() => CATEGORIES);
  }
  return _allCategoriesPromise;
};
const useAllCategories = () => {
  const [cats, setCats] = useState(CATEGORIES);
  useEffect(() => { let alive = true; getAllCategories().then(c => { if (alive) setCats(c); }); return () => { alive = false; }; }, []);
  return cats;
};

const CategoryPhoto = ({ slug, size=48, radius=12, style={} }) => {
  const overrides = useCategoryImageOverrides();
  const dbImage = overrides[slug];
  return (
    <div style={{width:size,height:size,borderRadius:radius,overflow:"hidden",flexShrink:0,background:"#EEF2F7",...style}}>
      {dbImage
        ? <img src={dbImage} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        : CATEGORY_IMAGE[slug]
        ? <img src={`${process.env.PUBLIC_URL}/images/categories/${CATEGORY_IMAGE[slug]}`} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}><CategoryIcon slug={slug} size={Math.round(size*0.55)}/></div>}
    </div>
  );
};

const CategoryIcon = ({ slug, size=36, color=C.navyDeep, accent=C.amber }) => {
  const common = { width:size, height:size, viewBox:"0 0 48 48", fill:"none" };
  switch (slug) {
    case "marine":
      return (
        <svg {...common}>
          <circle cx="24" cy="24" r="15" stroke={color} strokeWidth="3"/>
          {[0,45,90,135,180,225,270,315].map(a=>(
            <line key={a} x1="24" y1="24"
              x2={24+19*Math.cos(a*Math.PI/180)} y2={24+19*Math.sin(a*Math.PI/180)}
              stroke={color} strokeWidth="3" strokeLinecap="round"/>
          ))}
          <circle cx="24" cy="24" r="5" fill={accent}/>
        </svg>
      );
    case "solar":
      return (
        <svg {...common}>
          <rect x="7" y="13" width="30" height="21" rx="2" stroke={color} strokeWidth="3"/>
          <line x1="7" y1="20" x2="37" y2="20" stroke={color} strokeWidth="2"/>
          <line x1="7" y1="27" x2="37" y2="27" stroke={color} strokeWidth="2"/>
          <line x1="17" y1="13" x2="17" y2="34" stroke={color} strokeWidth="2"/>
          <line x1="27" y1="13" x2="27" y2="34" stroke={color} strokeWidth="2"/>
          <path d="M22 38 L26 38 L28 43 L20 43 Z" fill={accent}/>
        </svg>
      );
    case "railways":
      return (
        <svg {...common}>
          <line x1="6" y1="18" x2="42" y2="18" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          <line x1="6" y1="30" x2="42" y2="30" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          {[10,17,24,31,38].map(x=>(
            <line key={x} x1={x} y1="14" x2={x} y2="34" stroke={accent} strokeWidth="3"/>
          ))}
        </svg>
      );
    case "automobiles-heavy-machinery":
      return (
        <svg {...common}>
          <line x1="13" y1="41" x2="13" y2="9" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          <line x1="13" y1="11" x2="39" y2="19" stroke={color} strokeWidth="3" strokeLinecap="round"/>
          <line x1="39" y1="19" x2="39" y2="29" stroke={accent} strokeWidth="3" strokeLinecap="round"/>
          <circle cx="39" cy="31" r="2.5" fill={accent}/>
          <rect x="7" y="39" width="14" height="4" rx="1.5" fill={color}/>
          <circle cx="10" cy="43" r="2" fill={color}/>
          <circle cx="18" cy="43" r="2" fill={color}/>
        </svg>
      );
    case "safety-equipment":
      return (
        <svg {...common}>
          <path d="M24 6 L40 12 V22 C40 32 33 40 24 43 C15 40 8 32 8 22 V12 Z" stroke={color} strokeWidth="3" strokeLinejoin="round"/>
          <path d="M16 23 L21 29 L33 16" stroke={accent} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      );
    case "police-military-tactical":
      return (
        <svg {...common}>
          <path d="M24 6 L40 12 V22 C40 32 33 40 24 43 C15 40 8 32 8 22 V12 Z" stroke={color} strokeWidth="3" strokeLinejoin="round"/>
          <path d="M24 15 L26.3 21.2 L33 21.7 L27.8 25.9 L29.5 32.5 L24 28.7 L18.5 32.5 L20.2 25.9 L15 21.7 L21.7 21.2 Z" fill={accent}/>
        </svg>
      );
    case "uniforms-shirts":
      return (
        <svg {...common}>
          <path d="M17 8 L24 12.5 L31 8 L40 15 L34.5 21 L31 18.5 V40 H17 V18.5 L13.5 21 L8 15 Z" stroke={color} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round"/>
        </svg>
      );
    case "surgical-equipment":
      return (
        <svg {...common}>
          <circle cx="19" cy="20" r="13" stroke={color} strokeWidth="3"/>
          <line x1="19" y1="13" x2="19" y2="27" stroke={accent} strokeWidth="4" strokeLinecap="round"/>
          <line x1="12" y1="20" x2="26" y2="20" stroke={accent} strokeWidth="4" strokeLinecap="round"/>
          <path d="M34 26 C30 26 28 30 30 34 C32 38 38 40 40 36 C42 32 38 26 34 26 Z" stroke={color} strokeWidth="2.5" strokeLinejoin="round"/>
        </svg>
      );
    case "souvenirs-medals":
      return (
        <svg {...common}>
          <path d="M17 6 L21 21 L14.5 21 Z" fill={color}/>
          <path d="M31 6 L27 21 L33.5 21 Z" fill={color}/>
          <circle cx="24" cy="30" r="12" stroke={accent} strokeWidth="3"/>
          <circle cx="24" cy="30" r="4.5" fill={color}/>
        </svg>
      );
    default:
      return <span style={{fontSize:size*.8}}>⚓</span>;
  }
};

// ================================================================
// SHARED QUOTE MODAL — used by ProductsPage and HomePage's Featured Products
// ================================================================
const QuoteModal = ({ product, onClose, lang="en" }) => {
  const [qForm, setQForm] = useState({name:"",company:"",email:"",phone:"",qty:"1",notes:""});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  if (!product) return null;

  // FIX: this previously never sent the request anywhere — it just showed
  // a fake "submitted" message and closed. Every quote request site-wide
  // (this modal is shared by Home, Shop Now, and Product Catalogue) was
  // silently discarded and never reached the admin panel's Quotes page.
  // Now it actually posts to the existing, working POST /api/quotes route.
  const submitQuote = async () => {
    if (submitting) return; // guard against double-submit
    if (!qForm.name.trim() || !qForm.email.trim() || !qForm.phone.trim()) {
      setError(lang==="ar" ? "الاسم والبريد الإلكتروني والهاتف مطلوبة" : "Name, email and phone are required");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await apiFetch("/quotes", { method: "POST", body: JSON.stringify({
        customer: { name: qForm.name, email: qForm.email, phone: qForm.phone, company: qForm.company || "Personal" },
        product: product.name,
        partNumber: product.sku || "",
        qty: parseInt(qForm.qty, 10) || 1,
        notes: qForm.notes,
      })});
      setSubmitted(true);
      setTimeout(()=>{ onClose(); setSubmitted(false); setQForm({name:"",company:"",email:"",phone:"",qty:"1",notes:""}); }, 2500);
    } catch (e) {
      setError(e.message || (lang==="ar" ? "تعذر إرسال الطلب. حاول مرة أخرى." : "Couldn't send the request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
      <div onClick={onClose} style={{position:"absolute",inset:0,background:"rgba(5,10,20,.8)",backdropFilter:"blur(6px)"}}/>
      <div style={{position:"relative",background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:16,width:520,maxWidth:"100%",padding:"30px",boxShadow:"0 24px 64px rgba(0,0,0,.6)",animation:"fadeUp .3s ease",maxHeight:"90vh",overflowY:"auto"}}>
        {submitted ? (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:56,marginBottom:14}}>✅</div>
            <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:900,color:C.navyDeep,marginBottom:8}}>{lang==="ar"?"تم إرسال طلب عرض السعر!":"QUOTE SUBMITTED!"}</h3>
            <p style={{color:C.textMuted,fontSize:14}}>{lang==="ar"?"سنتواصل معك خلال 24 ساعة بالتسعير.":"We'll contact you within 24 hours with pricing."}</p>
          </div>
        ):(
          <>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
              <div>
                <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:4}}>{lang==="ar"?"طلب عرض سعر لـ":"REQUEST A QUOTE FOR"}</div>
                <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:C.navyDeep,lineHeight:1.2}}>{product.name}</h3>
                {product.sku && <div style={{fontSize:10,color:C.textMuted,fontFamily:"monospace",marginTop:3}}>{lang==="ar"?"رقم القطعة":"PN"}: {product.sku}</div>}
              </div>
              <button onClick={onClose} style={{background:C.navyBorder,border:"none",borderRadius:6,width:30,height:30,cursor:"pointer",color:C.text,fontSize:16,flexShrink:0}}>×</button>
            </div>
            <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {(lang==="ar"
                ? [["الاسم الكامل *","name","text"],["الشركة / السفينة","company","text"],["البريد الإلكتروني *","email","email"],["الهاتف *","phone","tel"],["الكمية المطلوبة","qty","number"]]
                : [["Full Name *","name","text"],["Company / Vessel","company","text"],["Email *","email","email"],["Phone *","phone","tel"],["Quantity Needed","qty","number"]]
              ).map(([lbl,k,type])=>(
                <div key={k}>
                  <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{lbl}</label>
                  <input type={type} value={qForm[k]} onChange={e=>setQForm(f=>({...f,[k]:e.target.value}))} className="input-field" style={{fontSize:13,padding:"10px 12px"}}/>
                </div>
              ))}
              <div style={{gridColumn:"1 / span 2"}}>
                <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{lang==="ar"?"ملاحظات إضافية":"Additional Notes"}</label>
                <textarea rows={2} className="input-field" style={{resize:"vertical",fontSize:13,padding:"10px 12px"}} value={qForm.notes} onChange={e=>setQForm(f=>({...f,notes:e.target.value}))}/>
              </div>
            </div>
            {error && <div style={{color:"#C0392B",fontSize:12,marginTop:10}}>{error}</div>}
            <button onClick={submitQuote} disabled={submitting} className="btn-amber" style={{width:"100%",borderRadius:8,padding:"13px",fontSize:15,marginTop:18,opacity:submitting?0.7:1,cursor:submitting?"default":"pointer"}}>
              {submitting ? (lang==="ar"?"جارٍ الإرسال...":"SENDING...") : (lang==="ar"?"إرسال طلب عرض السعر ←":"SUBMIT QUOTE REQUEST →")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// A handful of real, photographed Marine products used for the homepage
// product slider and Featured Products strip (curated for variety).
const HOME_REAL_PRODUCTS = [
  {image:"steering-wheel.jpg", name:"Marine Steering Wheel", category:"Steering, Cranes & Propulsion"},
  {image:"marine-cranes.jpg", name:"Marine Cranes", category:"Steering, Cranes & Propulsion"},
  {image:"fire-hydrant.jpg", name:"Fire Hydrant", category:"Life Saving & Fire Fighting"},
  {image:"life-floats.jpg", name:"Life Floats", category:"Life Saving & Fire Fighting"},
  {image:"anchor-chain.jpg", name:"Anchor Chain", category:"Deck & Mooring Hardware"},
  {image:"navigation-signal-light.jpg", name:"Navigation Signal Light", category:"Navigation & Electronics"},
  {image:"propellers.jpg", name:"Propellers", category:"Engines & Outboard Motors"},
  {image:"search-light.jpg", name:"Search Light", category:"Electrical & Lighting"},
  {image:"marine-winch.jpg", name:"Marine Winch", category:"Deck & Mooring Hardware"},
  {image:"wire-rope.jpg", name:"Wire Rope", category:"Deck & Mooring Hardware"},
  {image:"spot-light.jpg", name:"Spot Light", category:"Electrical & Lighting"},
  {image:"shackles.jpg", name:"Shackles", category:"Deck & Mooring Hardware"},
  {image:"gate-valve.jpg", name:"Gate Valve", category:"Pumps & Valves"},
  {image:"fresh-water-generator.jpg", name:"Fresh Water Generator", category:"Engines & Outboard Motors"},
  {image:"inflatable-boat.jpg", name:"Inflatable Boat", category:"Life Saving & Fire Fighting"},
  {image:"explosion-proof-light.jpg", name:"Explosion Proof Light", category:"Electrical & Lighting"},
  {image:"marine-rudder.jpg", name:"Marine Rudder", category:"Steering, Cranes & Propulsion"},
  {image:"free-fall-lifeboat.jpg", name:"Free Fall Lifeboat", category:"Life Saving & Fire Fighting"},
  {image:"azimuth-thruster.jpg", name:"Azimuth Thruster", category:"Steering, Cranes & Propulsion"},
  {image:"fire-hose.jpg", name:"Fire Hose", category:"Life Saving & Fire Fighting"},
  {image:"steering-gear.jpg", name:"Steering Gear", category:"Steering, Cranes & Propulsion"},
  {image:"chain-wheel.jpg", name:"Chain Wheel", category:"Deck & Mooring Hardware"},
];

// ================================================================
// ANIMATED COUNTER
// ================================================================
const Counter = ({ target, suffix="" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const steps = 60, duration = 1800;
        let step = 0;
        const timer = setInterval(() => {
          step++;
          setCount(Math.floor((step / steps) * target));
          if (step >= steps) { setCount(target); clearInterval(timer); }
        }, duration / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// ================================================================
// NAVBAR with MEGA DROPDOWN
// ================================================================
const Navbar = ({ page, setPage, cart, setShowCart, user, setUser, onLogout, lang, setLang }) => {
  const ALL_CATS = useAllCategories();
  const [scrolled, setScrolled] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const [dropTimeout, setDropTimeout] = useState(null);
  const [showSvcDrop, setShowSvcDrop] = useState(false);
  const [svcTimeout, setSvcTimeout] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  // No CSS media queries in this codebase (everything is inline styles), so
  // the mobile nav breakpoint is tracked in JS via a resize listener instead.
  const [vw, setVw] = useState(typeof window!=="undefined" ? window.innerWidth : 1280);
  const isMobile = vw <= 900;
  const cartCount = cart.reduce((a,i) => a+i.qty, 0);
  const t = STR[lang];

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close the mobile drawer whenever the page changes or the screen widens
  // back past the breakpoint, so it never gets stuck open.
  useEffect(() => { setMobileOpen(false); }, [page]);
  useEffect(() => { if (!isMobile) setMobileOpen(false); }, [isMobile]);

  const openDrop = () => { clearTimeout(dropTimeout); setShowDrop(true); };
  const closeDrop = () => {
    const t = setTimeout(() => setShowDrop(false), 180);
    setDropTimeout(t);
  };
  const openSvcDrop = () => { clearTimeout(svcTimeout); setShowSvcDrop(true); };
  const closeSvcDrop = () => {
    const t = setTimeout(() => setShowSvcDrop(false), 180);
    setSvcTimeout(t);
  };
  const svcMenuItems = lang==="ar"
    ? [["فحص","services"],["صيانة","services"],["إصلاح","services"]]
    : [["Inspection","services"],["Maintenance","services"],["Repair","services"]];

  return (
    <nav style={{
      position:"fixed",top:0,left:0,right:0,zIndex:1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.95)",
      backdropFilter:"blur(20px)",
      borderBottom:`1px solid ${C.navyBorder}`,
      transition:"all .3s",
      boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,.1)" : "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      <div style={{maxWidth:1300,margin:"0 auto",padding:"0 24px",height:68,display:"flex",alignItems:"center",gap:20}}>

        {/* LOGO */}
        <button onClick={() => setPage("home")} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:10,flexShrink:0,transition:"all .2s",borderRadius:8,padding:"4px 6px"}}
onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.04)"}}
onMouseLeave={e=>{e.currentTarget.style.transform=""}}>
          <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt="Al-Awama Distinctive Trading Company" style={{width:62,height:62,objectFit:"contain",flexShrink:0}}/>
          <div style={{textAlign: lang==="ar" ? "right" : "left"}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:lang==="ar"?25:23,fontWeight:900,color:C.navyDeep,lineHeight:1.15,letterSpacing:lang==="ar"?"0":".3px"}}>{lang==="ar" ? "شركة العوامة المميزة" : "AL-AWAMA"}</div>
            <div style={{fontSize:lang==="ar"?12.5:10.5,letterSpacing:lang==="ar"?"0":"1.3px",color:C.amber,fontWeight:700,textTransform:lang==="ar"?"none":"uppercase"}}>{lang==="ar" ? "للتجارة" : "Distinctive Trading Co."}</div>
          </div>
        </button>

        {/* NAV LINKS (desktop only — mobile uses the hamburger drawer below) */}
        {!isMobile && <div style={{display:"flex",gap:0,flex:1,justifyContent:"center",alignItems:"center"}}>

          {/* HOME */}
          <button onClick={() => setPage("home")} className="nav-link" style={{
            color:page==="home"?C.amberDark:C.textMuted, fontWeight:page==="home"?700:500,
            fontSize:14,padding:"8px 16px",
            borderBottom:`2px solid ${page==="home"?C.amber:"transparent"}`,
          }}>{t.nav.home}</button>

          {/* PRODUCTS with MEGA DROPDOWN */}
          <div style={{position:"relative"}} onMouseEnter={openDrop} onMouseLeave={closeDrop}>
            <button
              onClick={() => setPage("products")}
              className="nav-link"
              style={{
                color:page==="products"?C.amberDark:C.textMuted,
                fontWeight:page==="products"?700:500,
                fontSize:14,padding:"8px 16px",
                borderBottom:`2px solid ${page==="products"?C.amber:"transparent"}`,letterSpacing:".2px",
                display:"flex",alignItems:"center",gap:4,
              }}>
              {t.nav.products}
              <span style={{
                fontSize:9, color:showDrop?C.amber:C.textDim,
                transition:"transform .2s, color .2s",
                transform:showDrop?"rotate(180deg)":"rotate(0deg)",
                display:"inline-block",
              }}>▼</span>
            </button>

            {/* MEGA DROPDOWN MENU */}
            {showDrop && (
              <div className="dropdown-menu" onMouseEnter={openDrop} onMouseLeave={closeDrop}>
                {/* Top bar */}
                <div style={{
                  padding:"14px 24px",
                  background:`linear-gradient(90deg, #EBF5FF 0%, #FFFFFF 100%)`,
                  borderBottom:`1px solid ${C.navyBorder}`,
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  borderRadius:"0",
                }}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>📦</span>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:800,color:C.navyDeep,letterSpacing:".5px"}}>ALL PRODUCT CATEGORIES</span>
                  </div>
                  <button onClick={() => { setPage("products"); setShowDrop(false); }} className="btn-amber" style={{borderRadius:6,padding:"6px 14px",fontSize:12}}>
                    {t.nav.viewAll}
                  </button>
                </div>

                {/* Grid of categories */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:0}}>
                  {ALL_CATS.map((cat, ci) => (
                    <div key={cat.id}
                      style={{
                        borderRight: ci%3<2 ? `1px solid ${C.navyBorder}` : "none",
                        borderBottom: ci<6 ? `1px solid ${C.navyBorder}` : "none",
                        padding:"16px 18px",
                      }}>
                      {/* Category header */}
                      <button
                        onClick={() => { setPage("products"); setShowDrop(false); }}
                        className="drop-cat"
                        style={{
                          background:"none",border:"none",cursor:"pointer",
                          display:"flex",alignItems:"center",gap:7,
                          marginBottom:10,width:"100%",textAlign:"left",
                          padding:"5px 7px",borderRadius:6,transition:"all .15s",
                        }}>
                        <CategoryPhoto slug={cat.slug} size={22} radius={5}/>
                        <span style={{fontWeight:700,fontSize:13,color:C.navyDeep}}>{lang==="ar" ? cat.nameAr : cat.name}</span>
                      </button>

                      {/* Subcategories */}
                      <div style={{display:"flex",flexDirection:"column",gap:2}}>
                        {cat.subs.slice(0,5).map(sub => (
                          <button
                            key={sub}
                            onClick={() => { setPage("products"); setShowDrop(false); }}
                            className="drop-sub"
                            style={{
                              background:"none",border:"none",cursor:"pointer",
                              textAlign:"left",fontSize:12,color:C.textDim,
                              padding:"3px 7px",borderRadius:4,
                              transition:"all .15s",
                            }}>
                            › {subLabel(cat, sub, lang)}
                          </button>
                        ))}
                        {cat.subs.length > 5 && (
                          <button
                            onClick={() => { setPage("products"); setShowDrop(false); }}
                            style={{background:"none",border:"none",cursor:"pointer",textAlign:"left",fontSize:11,color:C.amber,padding:"3px 7px",fontWeight:600}}>
                            +{cat.subs.length-5} more
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom bar */}
                <div style={{
                  padding:"12px 24px",
                  background:"#EBF5FF",
                  borderTop:`1px solid ${C.navyBorder}`,
                  borderRadius:"0 0 14px 14px",
                  display:"flex",gap:20,
                }}>
                  {[["📋 Request a Quote","Don't see your part? Send us a request."],["🚢 OEM Parts","Genuine manufacturer components"],["⚡ Fast Delivery","Saudi Arabia wide shipping"]].map(([title,sub],i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:8,flex:1,paddingRight:i<2?20:0,borderRight:i<2?`1px solid ${C.navyBorder}`:"none"}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:C.navyDeep}}>{title}</div>
                        <div style={{fontSize:10,color:C.textMuted}}>{sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SHOP NOW */}
          <button onClick={() => setPage("shop")} className="nav-link" style={{
            color:page==="shop"?C.amberDark:C.textMuted, fontWeight:page==="shop"?700:500,
            fontSize:14,padding:"8px 16px",
            borderBottom:`2px solid ${page==="shop"?C.amber:"transparent"}`,
            display:"flex",alignItems:"center",gap:5,
          }}>
            Shop Now
            <span style={{background:C.amber,color:"#FFFFFF",fontSize:8,fontWeight:800,padding:"1px 5px",borderRadius:3,verticalAlign:"middle"}}>BUY</span>
          </button>

          {/* SERVICES with hover dropdown: Inspection / Maintenance / Repair */}
          <div style={{position:"relative"}} onMouseEnter={openSvcDrop} onMouseLeave={closeSvcDrop}>
            <button onClick={() => setPage("services")} className="nav-link" style={{
              color:page==="services"?C.amberDark:C.textMuted,fontWeight:page==="services"?700:500,
              fontSize:14,padding:"8px 16px",display:"flex",alignItems:"center",gap:5,
              borderBottom:`2px solid ${page==="services"?C.amber:"transparent"}`,
            }}>
              {t.nav.services}
              <span style={{fontSize:9,color:showSvcDrop?C.amber:C.textDim,transition:"transform .2s",transform:showSvcDrop?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
            </button>
            {showSvcDrop && (
              <div className="svc-dropdown-menu" onMouseEnter={openSvcDrop} onMouseLeave={closeSvcDrop}>
                {svcMenuItems.map(([lbl],i)=>(
                  <button key={i} onClick={()=>{ setPage("services"); setShowSvcDrop(false); }} className="svc-drop-item"
                    style={{display:"block",width:"100%",textAlign: lang==="ar"?"right":"left",background:"none",border:"none",cursor:"pointer",padding:"10px 14px",borderRadius:8,fontSize:13,fontWeight:600,color:C.navyDeep,transition:"all .15s"}}>
                    {lbl}
                  </button>
                ))}
              </div>
            )}
          </div>

          {[[t.nav.about,"about"],[t.nav.contact,"contact"]].map(([lbl,p])=>(
            <button key={p} onClick={() => setPage(p)} className="nav-link" style={{
              color:page===p?C.amberDark:C.textMuted,fontWeight:page===p?700:500,
              fontSize:14,padding:"8px 16px",
              borderBottom:`2px solid ${page===p?C.amber:"transparent"}`,
            }}>{lbl}</button>
          ))}
        </div>}

        {/* RIGHT ACTIONS (desktop) */}
        {!isMobile && <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <button onClick={() => setLang(lang==="en"?"ar":"en")} title="Switch language / تبديل اللغة" style={{background:C.navy,border:`1.5px solid ${C.navyBorder}`,borderRadius:7,padding:"7px 12px",fontSize:12,fontWeight:800,color:C.navyDeep,cursor:"pointer",transition:"all .2s"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=C.amber;e.currentTarget.style.color=C.amberDark}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=C.navyBorder;e.currentTarget.style.color=C.navyDeep}}>
            {lang==="en" ? "العربية" : "English"}
          </button>
          {user ? (
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <div onClick={()=>setPage("orders")} title="My Orders" style={{width:32,height:32,borderRadius:"50%",background:C.amberGlow,border:`1.5px solid ${C.amber}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:C.amber,cursor:"pointer"}}>
                {user.name[0].toUpperCase()}
              </div>
              <div style={{fontSize:12}}>
                <button onClick={()=>setPage("orders")} style={{background:"none",border:"none",cursor:"pointer",color:C.navyDeep,fontWeight:600,lineHeight:1,padding:0,fontSize:12}}>{user.name.split(" ")[0]}</button>
                <div><button onClick={onLogout} style={{background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:10,padding:0}}>Logout</button></div>
              </div>
            </div>
          ) : (
            <button onClick={() => setPage("login")} style={{background:"#FFFFFF",border:`2px solid ${C.navyDeep}`,borderRadius:7,padding:"8px 18px",fontSize:13,fontWeight:700,color:C.navyDeep,cursor:"pointer",transition:"all .22s",boxShadow:"0 1px 4px rgba(0,0,0,0.1)"}}
onMouseEnter={e=>{e.currentTarget.style.background=C.navyDeep;e.currentTarget.style.color="#FFFFFF";e.currentTarget.style.borderColor=C.navyDeep;e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 5px 18px rgba(21,101,192,.3)"}}
onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=C.textMuted;e.currentTarget.style.borderColor=C.navyBorder;e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=""}}>
              Login
            </button>
          )}
          <button onClick={() => setShowCart(true)} style={{background:"#FFFFFF",border:`2px solid ${C.navyDeep}`,borderRadius:8,padding:"9px 18px",fontSize:14,fontWeight:700,color:C.navyDeep,cursor:"pointer",transition:"all .22s",boxShadow:"0 1px 4px rgba(0,0,0,0.1)",position:"relative",display:"flex",alignItems:"center",gap:6,fontFamily:"'DM Sans',sans-serif"}} onMouseEnter={e=>{e.currentTarget.style.background=C.navyDeep;e.currentTarget.style.color="#FFFFFF";e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 5px 18px rgba(21,101,192,.3)"}} onMouseLeave={e=>{e.currentTarget.style.background="#FFFFFF";e.currentTarget.style.color=C.navyDeep;e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.1)"}}>
            <span>🛒</span><span>Cart</span>
            {cartCount>0 && <span style={{position:"absolute",top:-7,right:-7,background:C.red,color:C.white,width:18,height:18,borderRadius:"50%",fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${C.navy}`}}>{cartCount}</span>}
          </button>
        </div>}

        {/* MOBILE: cart icon + hamburger toggle (screens ≤900px) */}
        {isMobile && <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0,marginLeft:"auto"}}>
          <button onClick={() => setShowCart(true)} aria-label="Cart" style={{background:"#FFFFFF",border:`1.5px solid ${C.navyDeep}`,borderRadius:8,width:40,height:40,cursor:"pointer",position:"relative",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17}}>
            🛒
            {cartCount>0 && <span style={{position:"absolute",top:-6,right:-6,background:C.red,color:C.white,width:17,height:17,borderRadius:"50%",fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",border:`2px solid ${C.navy}`}}>{cartCount}</span>}
          </button>
          <button onClick={()=>setMobileOpen(o=>!o)} aria-label="Menu" aria-expanded={mobileOpen} style={{background:mobileOpen?C.navyDeep:"#FFFFFF",border:`1.5px solid ${C.navyDeep}`,borderRadius:8,width:40,height:40,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:mobileOpen?"#FFFFFF":C.navyDeep}}>
            {mobileOpen ? "✕" : "☰"}
          </button>
        </div>}
      </div>

      {/* MOBILE DRAWER — stacked links + language/login/cart, replaces the
          desktop mega-menu/dropdowns with a simple flat list since a
          hover-driven mega-menu has no equivalent on touch screens. */}
      {isMobile && mobileOpen && (
        <div style={{background:"#FFFFFF",borderTop:`1px solid ${C.navyBorder}`,maxHeight:"calc(100vh - 68px)",overflowY:"auto",padding:"8px 0 18px"}}>
          {[[t.nav.home,"home"],[t.nav.products,"products"],["Shop Now","shop"],[t.nav.services,"services"],[t.nav.about,"about"],[t.nav.contact,"contact"]].map(([lbl,p])=>(
            <button key={p} onClick={()=>setPage(p)} style={{
              display:"block",width:"100%",textAlign:lang==="ar"?"right":"left",
              background:page===p?C.amberGlow:"none",border:"none",borderBottom:`1px solid ${C.navyBorder}`,
              padding:"14px 24px",fontSize:15,fontWeight:page===p?800:600,
              color:page===p?C.amberDark:C.navyDeep,cursor:"pointer",
            }}>{lbl}</button>
          ))}
          {user ? (
            <button onClick={()=>setPage("orders")} style={{display:"block",width:"100%",textAlign:lang==="ar"?"right":"left",background:"none",border:"none",borderBottom:`1px solid ${C.navyBorder}`,padding:"14px 24px",fontSize:15,fontWeight:600,color:C.navyDeep,cursor:"pointer"}}>
              👤 {user.name.split(" ")[0]} — {lang==="ar"?"طلباتي":"My Orders"}
            </button>
          ) : (
            <button onClick={()=>setPage("login")} style={{display:"block",width:"100%",textAlign:lang==="ar"?"right":"left",background:"none",border:"none",borderBottom:`1px solid ${C.navyBorder}`,padding:"14px 24px",fontSize:15,fontWeight:600,color:C.navyDeep,cursor:"pointer"}}>
              {lang==="ar"?"تسجيل الدخول":"Login"}
            </button>
          )}
          {user && (
            <button onClick={onLogout} style={{display:"block",width:"100%",textAlign:lang==="ar"?"right":"left",background:"none",border:"none",borderBottom:`1px solid ${C.navyBorder}`,padding:"14px 24px",fontSize:14,fontWeight:600,color:C.textMuted,cursor:"pointer"}}>
              {lang==="ar"?"تسجيل الخروج":"Logout"}
            </button>
          )}
          <button onClick={() => setLang(lang==="en"?"ar":"en")} style={{display:"block",width:"100%",textAlign:lang==="ar"?"right":"left",background:"none",border:"none",padding:"14px 24px",fontSize:15,fontWeight:700,color:C.amber,cursor:"pointer"}}>
            🌐 {lang==="en" ? "العربية" : "English"}
          </button>
        </div>
      )}
    </nav>
  );
};

// ================================================================
// PAGE: HOME — Al-Awama Style
// ================================================================
const HomePage = ({ setPage, lang="en" }) => {
  const ALL_CATS = useAllCategories();
  const t = STR[lang];
  const [heroSlide, setHeroSlide] = useState(0);
  const [quoteModal, setQuoteModal] = useState(null);

  // Admin-editable Featured Products (Products page → ★ Featured toggle,
  // requires the product to also have a photo uploaded) and Home Gallery
  // (Site Content page). Both fall back to the bundled real Marine photo
  // set until the admin has curated real data for them.
  const [dbFeatured, setDbFeatured] = useState(null);
  const [dbGallery, setDbGallery] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/products?featured=true&limit=12`).then(r => r.json()).then(d => {
      const withPhotos = (d.products || []).filter(p => p.images && p.images.length > 0);
      setDbFeatured(withPhotos.slice(0, 8));
    }).catch(() => setDbFeatured([]));
    fetch(`${API_BASE}/settings`).then(r => r.json()).then(d => {
      setDbGallery(d.settings?.homeGallery || []);
    }).catch(() => setDbGallery([]));
  }, []);

  const featuredProducts = (dbFeatured && dbFeatured.length > 0)
    ? dbFeatured.map(p => ({ id:p._id, image:p.images[0], name:p.name, nameAr:p.nameAr, category:p.category, sku:p.sku, isDb:true }))
    : HOME_REAL_PRODUCTS.slice(0,8);
  const galleryItems = (dbGallery && dbGallery.length > 0)
    ? dbGallery.map(g => ({ ...g, isDb:true }))
    : HOME_REAL_PRODUCTS;
  const photoSrc = p => p.isDb ? p.image : `${process.env.PUBLIC_URL}/images/marine/${p.image}`;
  const heroSlides = lang==="ar" ? [
    { title:"شركة العوامة", title2:"المميزة للتجارة", sub:"مورّد موثوق لمعدات القطاعات البحرية والشمسية والسكك الحديدية والدفاعية في المملكة العربية السعودية منذ أكثر من 18 عامًا.", bg:"linear-gradient(135deg,#0B3D91 0%,#1565C0 60%,#0D47A1 100%)", icon:"⚓" },
    { title:"9 قطاعات صناعية", title2:"تحت سقف واحد", sub:"من المعدات البحرية والطاقة الشمسية إلى المعدات العسكرية والزي الموحد والمعدات الجراحية: كل ما تحتاجه منظمتك.", bg:"linear-gradient(135deg,#1565C0 0%,#0B3D91 60%,#1976D2 100%)", icon:"⚙️" },
    { title:"الجودة قبل", title2:"الموعد النهائي", sub:"سجل حافل في العمل مع القطاع الدفاعي السعودي: تسليم معتمد وعالي الجودة قبل الموعد النهائي في كل مرة.", bg:"linear-gradient(135deg,#0D47A1 0%,#1565C0 50%,#0B3D91 100%)", icon:"🚢" },
  ] : [
    { title:"Al-Awama Distinctive", title2:"Trading Company", sub:"A trusted multi-sector supplier of marine, solar, railway and defense-grade equipment across Saudi Arabia for over 18 years.", bg:"linear-gradient(135deg,#0B3D91 0%,#1565C0 60%,#0D47A1 100%)", icon:"⚓" },
    { title:"9 Industry Sectors", title2:"Under One Roof", sub:"From marine and solar equipment to police & military tactical gear, uniforms, and surgical equipment: everything your organization needs.", bg:"linear-gradient(135deg,#1565C0 0%,#0B3D91 60%,#1976D2 100%)", icon:"⚙️" },
    { title:"Quality Before", title2:"The Deadline", sub:"A proven track record supplying Saudi Arabia's defense sector: certified, quality equipment delivered ahead of schedule, every time.", bg:"linear-gradient(135deg,#0D47A1 0%,#1565C0 50%,#0B3D91 100%)", icon:"🚢" },
  ];

  useEffect(() => {
    const t = setInterval(() => setHeroSlide(s => (s+1)%heroSlides.length), 5000);
    return () => clearInterval(t);
  }, []);

  const slide = heroSlides[heroSlide];

  return (
    <div>
      {/* ── HERO SLIDER ── */}
      <section style={{
        minHeight:"100vh",display:"flex",alignItems:"center",
        background:slide.bg,position:"relative",overflow:"hidden",paddingTop:68,
        transition:"background 1s ease", color:C.white,
      }}>
        {/* Grid overlay */}
        <div style={{position:"absolute",inset:0,backgroundImage:`linear-gradient(rgba(255,255,255,.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.1) 1px,transparent 1px)`,backgroundSize:"55px 55px",pointerEvents:"none"}}/>
        {/* Glow */}
        <div style={{position:"absolute",inset:0,backgroundImage:`radial-gradient(ellipse at 70% 50%,rgba(232,160,32,.07) 0%,transparent 55%)`,pointerEvents:"none"}}/>
        {/* Logo watermark */}
        <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt="" aria-hidden="true"
          style={{position:"absolute",right:"-6%",top:"50%",transform:"translateY(-50%)",width:"58%",maxWidth:640,opacity:0.09,filter:"brightness(0) invert(1)",pointerEvents:"none",userSelect:"none"}}/>

        <div className="rgrid-2" style={{maxWidth:1300,margin:"0 auto",padding:"80px 24px",width:"100%",display:"grid",gridTemplateColumns:"1.1fr 1fr",gap:64,alignItems:"center",position:"relative"}}>
          {/* Text */}
          <div key={heroSlide}>
            <div className="fu" style={{display:"inline-flex",alignItems:"center",gap:8,background:C.amberGlow,border:`1px solid ${C.amberBorder}`,borderRadius:100,padding:"5px 16px",marginBottom:20}}>
              <span style={{width:6,height:6,borderRadius:"50%",background:C.amber,display:"block",animation:"pulse 2s infinite"}}/>
              <span style={{fontSize:11,fontWeight:700,color:C.amber,letterSpacing:"1px"}}>{t.hero.kicker}</span>
            </div>

            <h1 className="fu1" style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:"clamp(40px,6vw,72px)",fontWeight:900,lineHeight:.98,letterSpacing:"-1px",marginBottom:20}}>
              <span style={{display:"block",color:C.white}}>{slide.title}</span>
              <span style={{display:"block",color:C.amber}}>{slide.title2}</span>
            </h1>

            <p className="fu2" style={{fontSize:16,color:"rgba(255,255,255,0.85)",lineHeight:1.75,marginBottom:32,maxWidth:460}}>{slide.sub}</p>

            <div className="fu3" style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              <button onClick={() => setPage("products")} className="btn-amber" style={{borderRadius:8,padding:"14px 28px",fontSize:16}}>{t.hero.cta1}</button>
              <button onClick={() => setPage("contact")}
                className="btn-amber"
                style={{borderRadius:8,padding:"14px 28px",fontSize:16}}>
                {t.hero.cta2}
              </button>
            </div>

            {/* Slide dots */}
            <div className="fu4" style={{display:"flex",gap:8,marginTop:36}}>
              {heroSlides.map((_,i) => (
                <button key={i} onClick={() => setHeroSlide(i)} style={{width:i===heroSlide?28:8,height:8,borderRadius:4,background:i===heroSlide?C.amber:"rgba(255,255,255,0.35)",border:"none",cursor:"pointer",transition:"all .3s"}}/>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <div className="fu2 rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:20,padding:16,backdropFilter:"blur(6px)",boxShadow:"0 24px 60px rgba(0,0,0,.25)"}}>
            {ALL_CATS.slice(0,4).map((cat,i) => (
              <div key={cat.id} onClick={() => setPage("products")} className="card"
                style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden",cursor:"pointer",animationDelay:`${.6+i*.1}s`}}>
                <div style={{width:"100%",height:78,background:"#FFFFFF",borderBottom:`1px solid ${C.navyBorder}`}}>
                  <CategoryPhoto slug={cat.slug} size="100%" radius={0} style={{height:"100%"}}/>
                </div>
                <div style={{padding:"14px 16px 18px"}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.navyDeep,marginBottom:3}}>{lang==="ar"?cat.nameAr:cat.name}</div>
                  <div style={{fontSize:11,color:C.textMuted}}>{PART_COUNT_LABEL[cat.slug]||DEFAULT_PART_COUNT_LABEL} {lang==="ar"?"قطعة":"Parts"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom arrow */}
        <div style={{position:"absolute",bottom:32,left:"50%",transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer",animation:"float 2s ease-in-out infinite"}} onClick={() => window.scrollBy({top:600,behavior:"smooth"})}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",letterSpacing:"2px",textTransform:"uppercase"}}>Scroll</div>
          <div style={{fontSize:20,color:C.amber}}>↓</div>
        </div>
      </section>

      {/* ── STATS COUNTER STRIP ── */}
      <section style={{background:C.amber,padding:"0"}}>
        <div style={{maxWidth:1300,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))"}}>
          {[
            {val:18,suf:"+",label:lang==="ar"?"سنة خبرة":"Years Experience"},
            {val:9,suf:"",label:lang==="ar"?"قطاعات صناعية":"Industry Sectors"},
            {text:true,label:lang==="ar"?"أفضل الأسعار":"BEST PRICE", sub:lang==="ar"?"أسعار تنافسية مضمونة":"Guaranteed Competitive Rates"},
            {val:100,suf:"%",label:lang==="ar"?"الجودة قبل الموعد":"Quality Before Deadline"},
          ].map((s,i) => (
            <div key={i} style={{padding:"28px 20px",textAlign:"center",borderRight:i<3?`1px solid rgba(0,0,0,.1)`:"none"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:s.text?28:40,fontWeight:900,color:"#0B3D91",lineHeight:1}}>
                {s.text ? s.label : <Counter target={s.val} suffix={s.suf}/>}
              </div>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(0,40,100,0.75)",marginTop:4,textTransform:"uppercase",letterSpacing:"1px"}}>{s.text ? s.sub : s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── REAL PRODUCT PHOTO SLIDER ── */}
      <section style={{padding:"48px 0",background:C.navyMid,borderBottom:`1px solid ${C.navyBorder}`,overflow:"hidden"}}>
        <div style={{maxWidth:1300,margin:"0 auto 20px",padding:"0 24px",textAlign:"center"}}>
          <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:6}}>{lang==="ar"?"// من كتالوجنا البحري الحقيقي":"// FROM OUR REAL MARINE CATALOG"}</div>
          <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:C.navyDeep}}>{lang==="ar"?"صور حقيقية لمنتجاتنا":"REAL PRODUCT PHOTOS"}</h3>
        </div>
        <div style={{overflow:"hidden",position:"relative"}}>
          <div style={{display:"flex",gap:14,width:"max-content",animation:"marquee 38s linear infinite"}}>
            {[...galleryItems,...galleryItems].map((p,i) => (
              <div key={i} onClick={()=>setPage("products")} className="prod-slide" style={{width:130,height:130,borderRadius:10,border:`1px solid ${C.navyBorder}`,cursor:"pointer",background:"#FFFFFF"}}>
                <img src={photoSrc(p)} alt={p.name} loading="lazy" style={{width:"100%",height:"100%",objectFit:"contain",padding:10}}/>
                <div className="prod-slide-name">{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT US SECTION ── */}
      <section style={{padding:"80px 24px",background:C.navyLight,borderBottom:`1px solid ${C.navyBorder}`,boxShadow:"inset 0 -1px 0 rgba(0,0,0,0.04)"}}>
        <div className="rgrid-2" style={{maxWidth:1300,margin:"0 auto",display:"grid",gridTemplateColumns:"1fr 1fr",gap:60,alignItems:"center"}}>
          {/* Left: text */}
          <div>
            <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:12,textTransform:"uppercase"}}>{lang==="ar"?"// من نحن":"// ABOUT US"}</div>
            <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:44,fontWeight:900,color:C.amber,lineHeight:1.05,marginBottom:20}}>{lang==="ar"?<>شريكك الموثوق<br/>متعدد القطاعات<br/>في المملكة</>:<>YOUR TRUSTED<br/>MULTI-SECTOR<br/>PARTNER IN KSA</>}</h2>
            <p style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:14}}>{STR[lang].about.intro}</p>
            <p style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:28}}>{STR[lang].about.missionP1}</p>
            <div style={{display:"flex",gap:20,marginBottom:32,flexWrap:"wrap"}}>
              {(lang==="ar" ? ["✅ +18 سنة خبرة","🇸🇦 شريك القطاع الدفاعي","⏱️ الجودة قبل الموعد"] : ["✅ 18+ Years Experience","🇸🇦 Saudi Defense Partner","⏱️ Quality Before Deadline"]).map((tx,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13,color:C.amber}}>●</span>
                  <span style={{fontSize:13,fontWeight:600,color:C.navyDeep}}>{tx}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setPage("about")} className="btn-amber" style={{borderRadius:8,padding:"13px 26px",fontSize:15}}>{lang==="ar"?"اعرف المزيد عنا ←":"LEARN MORE ABOUT US →"}</button>
          </div>

          {/* Right: feature boxes */}
          <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            {STR[lang].about.stats.concat([lang==="ar"?{icon:"👷",title:"فريق من المحترفين",sub:"مهندسون وفنيون مؤهلون في كل قطاع نخدمه، لأعمال الفحص والصيانة والإصلاح."}:{icon:"👷",title:"Team of Professionals",sub:"Qualified engineers and technicians across every sector we serve, for inspection, maintenance and repair."}]).map((f,i) => (
              <div key={i} className="card" style={{background:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"24px 20px"}}>
                <div style={{fontSize:32,marginBottom:12,animation:`float ${3+i*.4}s ease-in-out infinite`}}>{f.icon}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:800,color:C.navyDeep,marginBottom:6}}>{f.title}</div>
                <div style={{fontSize:12,color:"#4B5563",lineHeight:1.6}}>{f.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCT CATEGORIES ── */}
      <section style={{padding:"80px 24px"}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:40}}>
            <div>
              <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:10}}>{lang==="ar"?"// تصفح حسب الفئة":"// BROWSE BY CATEGORY"}</div>
              <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:44,fontWeight:900,color:C.amber,lineHeight:1}} >{lang==="ar"?<>فئات منتجاتنا</>:<>OUR PRODUCT<br/>CATEGORIES</>}</h2>
            </div>
            <button onClick={() => setPage("products")} className="btn-ghost" style={{border:`1.5px solid ${C.navyBorder}`,borderRadius:8,padding:"11px 20px",color:C.textMuted,fontSize:13,fontWeight:600}}>
              {lang==="ar"?"عرض الكتالوج الكامل ←":"View Full Catalog →"}
            </button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:18}}>
            {ALL_CATS.map(cat => (
              <div key={cat.id} onClick={() => setPage("products")} className="card"
                style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"26px 20px",cursor:"pointer"}}>
                <div style={{marginBottom:14}}><CategoryPhoto slug={cat.slug} size={54} radius={10}/></div>
                <div style={{fontWeight:800,fontSize:15,color:C.navyDeep,marginBottom:5}}>{lang==="ar"?cat.nameAr:cat.name}</div>
                <div style={{fontSize:12,color:C.textMuted,marginBottom:12}}>{cat.subs.length} {lang==="ar"?"فئة فرعية":"subcategories"} · {PART_COUNT_LABEL[cat.slug]||DEFAULT_PART_COUNT_LABEL} {lang==="ar"?"قطعة":"parts"}</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {cat.subs.slice(0,3).map(s=>(
                    <span key={s} style={{fontSize:9,background:C.navyBorder,color:C.textDim||C.textMuted,padding:"2px 7px",borderRadius:4}}>{subLabel(cat, s, lang)}</span>
                  ))}
                </div>
                <div style={{marginTop:12,fontSize:12,color:C.amberDark,fontWeight:700}}>{lang==="ar"?`تصفح ${cat.nameAr} ←`:`Browse ${cat.name} →`}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TWO CTA CARDS ── */}
      <section style={{padding:"0 24px 80px"}}>
        <div className="rgrid-2" style={{maxWidth:1300,margin:"0 auto",display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          <div onClick={() => setPage("products")} className="card" style={{background:C.navyLight,border:`2px solid ${C.amber}`,borderRadius:16,padding:"40px 36px",cursor:"pointer",boxShadow:"0 4px 24px rgba(232,160,32,0.18)"}}>
            <div style={{fontSize:44,marginBottom:16}}>📋</div>
            <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:C.navyDeep,marginBottom:10}}>{lang==="ar"?"اطلب عرض سعر":"Request a Quote"}</h3>
            <p style={{fontSize:14,color:"#374151",lineHeight:1.7,marginBottom:22}}>{lang==="ar"?"تصفح كتالوجنا الكامل الذي يضم أكثر من 5,000 قطعة. لا تجد قطعتك؟ أرسل طلب عرض سعر وسيرد فريقنا خلال 24 ساعة.":"Browse our complete 5,000+ part catalog. Don't see your part? Submit a quote request and our team responds within 24 hours."}</p>
            <button className="btn-amber" style={{borderRadius:8,padding:"12px 22px",fontSize:14}}>{lang==="ar"?"تصفح الكتالوج ←":"BROWSE CATALOG →"}</button>
          </div>
          <div onClick={() => setPage("shop")} className="card" style={{background:"#FFFFFF",border:`2px solid ${C.amber}`,borderRadius:16,padding:"40px 36px",cursor:"pointer",boxShadow:"0 4px 24px rgba(232,160,32,0.18)"}}>
            <div style={{fontSize:44,marginBottom:16}}>🛒</div>
            <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:30,fontWeight:900,color:C.navyDeep,marginBottom:10}}>{lang==="ar"?"تسوق الآن":"Shop Now"}</h3>
            <p style={{fontSize:14,color:"#374151",lineHeight:1.7,marginBottom:22}}>{lang==="ar"?"منتجات متوفرة بأسعار ثابتة. اطلب أونلاين لتوصيل فوري داخل المملكة العربية السعودية. دفع آمن عبر البطاقة، مدى وApple Pay.":"In-stock items with fixed prices. Order online for immediate delivery across Saudi Arabia. Secure payment via Card, Mada & Apple Pay."}</p>
            <button className="btn-amber" style={{borderRadius:8,padding:"12px 22px",fontSize:14}}>{lang==="ar"?"تسوق الآن ←":"SHOP NOW →"}</button>
          </div>
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ── */}
      <section style={{padding:"0 24px 80px",background:C.navy,paddingTop:80,borderTop:`1px solid ${C.navyBorder}`,borderBottom:`1px solid ${C.navyBorder}`}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:40}}>
            <div>
              <div style={{fontSize:11,letterSpacing:"3px",color:C.amberDark,fontWeight:700,marginBottom:10}}>{lang==="ar"?"// متوفر الآن":"// AVAILABLE NOW"}</div>
              <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:46,fontWeight:900,lineHeight:1.02,color:C.amber}}>{lang==="ar"?<>المنتجات<br/>المميزة</>:<>FEATURED<br/>PRODUCTS</>}</h2>
            </div>
            <button onClick={() => setPage("products")} className="btn-ghost" style={{border:`1.5px solid ${C.navyBorder}`,borderRadius:8,padding:"11px 20px",color:C.text,fontSize:13,fontWeight:600}}>
              {lang==="ar"?"عرض الكل ←":"View All →"}
            </button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
            {featuredProducts.map((p,idx) => {
              const displayName = (lang==="ar" && p.isDb && p.nameAr) ? p.nameAr : p.name;
              const categoryLabel = p.isDb ? catNameAr(p.category, lang) : (lang==="ar" ? `${catNameAr("Marine",lang)} · ${p.category}` : `MARINE · ${p.category}`);
              return (
              <div key={idx} onClick={()=>setQuoteModal({name:p.name, sku:p.sku||"MARINE"})} className="card" style={{background:"#FFFFFF",border:`2px solid rgba(21,101,192,.35)`,borderRadius:12,overflow:"hidden",cursor:"pointer"}}>
                <div style={{width:"100%",height:160,background:"#FFFFFF",display:"flex",alignItems:"center",justifyContent:"center",borderBottom:`1px solid ${C.navyBorder}`}}>
                  <img src={photoSrc(p)} alt={displayName} loading="lazy" style={{width:"100%",height:"100%",objectFit:"contain",padding:10}}/>
                </div>
                <div style={{padding:"14px"}}>
                  <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:3}}>{categoryLabel}</div>
                  <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,marginBottom:4,lineHeight:1.35}}>{displayName}</div>
                  {p.isDb && p.sku && <div style={{fontSize:10,color:C.textMuted,fontFamily:"monospace",marginBottom:8}}>{lang==="ar"?"رقم القطعة":"Part No"}: {p.sku}</div>}
                  <button onClick={(e)=>{e.stopPropagation();setQuoteModal({name:p.name, sku:p.sku||"MARINE"});}} className="btn-amber" style={{width:"100%",borderRadius:7,padding:"9px",fontSize:12,marginTop:p.isDb&&p.sku?0:10}}>{lang==="ar"?"اطلب عرض سعر":"REQUEST QUOTE"}</button>
                </div>
              </div>
            );})}
          </div>
        </div>
      </section>

      <QuoteModal product={quoteModal} onClose={()=>setQuoteModal(null)} lang={lang}/>

      {/* ── PARTNER BRANDS ── */}
      <section style={{padding:"64px 0",overflow:"hidden",background:C.navyDeep,position:"relative"}}>
        <div style={{position:"absolute",inset:0,backgroundImage:`radial-gradient(ellipse at 30% 30%,rgba(232,160,32,.06) 0%,transparent 55%)`,pointerEvents:"none"}}/>
        <div style={{maxWidth:1300,margin:"0 auto 28px",padding:"0 24px",textAlign:"center",position:"relative"}}>
          <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:8}}>{lang==="ar"?"// علاماتنا التجارية":"// OUR BRANDS"}</div>
          <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:32,fontWeight:900,color:"#FFFFFF"}}>{lang==="ar"?"العلامات التجارية والمعايير التي نعمل بها":"BRANDS & STANDARDS WE WORK WITH"}</h3>
        </div>
        {/* Marquee */}
        <div style={{overflow:"hidden",position:"relative"}}>
          <div style={{display:"flex",gap:0,width:"max-content",animation:"marquee 75s linear infinite"}}>
            {[...PARTNERS,...PARTNERS].map((b,i) => (
              <div key={i} style={{
                minWidth:180,padding:"16px 24px",margin:"0 12px",
                background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.14)",
                borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",
                fontWeight:700,fontSize:14,color:"rgba(255,255,255,0.85)",
                letterSpacing:".5px",whiteSpace:"nowrap",
              }}>
                <span style={{marginRight:8,color:C.amber}}>●</span>{b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section style={{padding:"80px 24px"}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:52}}>
            <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:10}}>{lang==="ar"?"// لماذا العوامة":"// WHY AL-AWAMA"}</div>
            <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:46,fontWeight:900,color:C.navyDeep}}>{lang==="ar"?"لماذا تختارنا؟":"WHY CHOOSE US?"}</h2>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:24}}>
            {(lang==="ar" ? [
              {icon:"🏆",title:"جودة معتمدة",desc:"جميع المنتجات مصدرها مصنعون معتمدون وتخضع لفحص جودة صارم قبل الشحن."},
              {icon:"📞",title:"دعم متخصص",desc:"فريقنا من المهندسين المؤهلين متاح لمساعدتك في إيجاد القطعة المناسبة أو جدولة فحص."},
              {icon:"💰",title:"أسعار تنافسية",desc:"نقدم أفضل أسعار السوق دون المساس بالجودة. خصومات متاحة عند الشراء بالجملة."},
              {icon:"🤝",title:"شركاء على المدى الطويل",desc:"نحن لسنا مجرد مورّد: نحن شريكك في المشتريات متعددة القطاعات على المدى الطويل."},
            ] : [
              {icon:"🏆",title:"Certified Quality",desc:"All products are sourced from certified manufacturers and undergo rigorous quality inspection before dispatch."},
              {icon:"📞",title:"Expert Support",desc:"Our team of qualified engineers is available to help you find the right part or schedule an inspection."},
              {icon:"💰",title:"Competitive Pricing",desc:"We offer the best market prices without compromising on quality. Volume discounts available."},
              {icon:"🤝",title:"Long-Term Partners",desc:"We are not just a supplier: we are your long-term multi-sector procurement partner."},
            ]).map((item,i) => (
              <div key={i} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"30px 24px",textAlign:"center"}}>
                <div style={{fontSize:40,marginBottom:14}}>{item.icon}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:C.navyDeep,marginBottom:10}}>{item.title}</div>
                <div style={{fontSize:13,color:"#4B5563",lineHeight:1.75}}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT CTA BANNER ── */}
      <section style={{padding:"72px 24px",background:"linear-gradient(135deg,#0B3D91 0%,#1565C0 100%)",borderTop:`1px solid #BAD6F0`,borderBottom:`1px solid #BAD6F0`}}>
        <div style={{maxWidth:800,margin:"0 auto",textAlign:"center"}}>
          <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:52,fontWeight:900,color:C.navyDeep,lineHeight:1,marginBottom:16}}>{lang==="ar"?<>هل تحتاج قطعة؟<br/><span style={{color:C.amber}}>لنتحدث.</span></>:<>NEED A PART?<br/><span style={{color:C.amber}}>LET'S TALK.</span></>}</h2>
          <p style={{fontSize:15,color:"rgba(255,255,255,0.82)",lineHeight:1.7,marginBottom:32,maxWidth:520,margin:"0 auto 32px"}}>
            {lang==="ar"
              ? "لا تجد ما تبحث عنه؟ سيقوم خبراؤنا البحريون بتوفيره لك. تواصل معنا لعروض أسعار مخصصة أو طلبات بالجملة أو الدعم الفني."
              : "Can't find what you're looking for? Our marine experts will source it for you. Contact us for custom quotes, bulk orders, or technical support."}
          </p>
          <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={() => setPage("contact")} className="btn-amber" style={{borderRadius:8,padding:"15px 32px",fontSize:17}}>{lang==="ar"?"تواصل معنا ←":"CONTACT US →"}</button>
            <a href="tel:+966560264386" className="btn-amber" style={{borderRadius:8,padding:"15px 32px",fontSize:17,textDecoration:"none",display:"inline-flex",alignItems:"center"}}>📞 {lang==="ar"?"اتصل الآن":"Call Now"}</a>
          </div>
        </div>
      </section>

      <Footer setPage={setPage} lang={lang}/>
    </div>
  );
};

// ================================================================
// PAGE: ALL PRODUCTS — global search across every product & subcategory
// ================================================================
const AllProductsPage = ({ setPage, lang="en" }) => {
  const ALL_CATS = useAllCategories();
  const [search, setSearch] = useState("");
  const [marineCats, setMarineCats] = useState(null);
  const dir = lang==="ar" ? "rtl" : "ltr";

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/marine-categories.json`)
      .then(r => r.json()).then(setMarineCats).catch(() => setMarineCats([]));
  }, []);

  const q = search.trim().toLowerCase();

  const marineItems = useMemo(() => {
    if (!marineCats) return [];
    return marineCats.flatMap(c => c.items.map(it => ({ ...it, subcategory: c.category })));
  }, [marineCats]);
  const filteredMarine = q ? marineItems.filter(it => it.name.toLowerCase().includes(q)) : marineItems;

  // Every subcategory across all 9 industries — real items only exist for Marine so
  // far; the rest are shown as browsable subcategory tiles until their own catalogs
  // are built, rather than inventing fake per-item listings. Keeps the full `cat`
  // object (not just its English name) so tiles can show the Arabic category name
  // and subLabel() can translate the subcategory name when lang==="ar".
  const allSubTiles = useMemo(() => ALL_CATS.flatMap(c => c.subs.map(s => ({ cat:c, catSlug:c.slug, catName:c.name, sub:s }))), [ALL_CATS]);
  const filteredSubs = q ? allSubTiles.filter(t => t.sub.toLowerCase().includes(q) || t.catName.toLowerCase().includes(q)) : allSubTiles;

  const goToSub = (catSlug, sub) => {
    try {
      sessionStorage.setItem("pendingCategorySlug", catSlug);
      sessionStorage.setItem("pendingSub", sub);
    } catch (e) {}
    setPage("products");
  };

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}} dir={dir}>
      <div style={{background:"linear-gradient(135deg,#0B3D91,#1565C0)",borderBottom:"1px solid #BAD6F0",padding:"48px 24px",color:C.white}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          <div style={{fontSize:12.5,fontWeight:600,color:"rgba(255,255,255,0.85)",marginBottom:10}}>
            <span style={{cursor:"pointer",color:C.amber}} onClick={()=>setPage("home")}>{lang==="ar"?"الرئيسية":"Home"}</span>
            <span style={{color:"rgba(255,255,255,0.5)"}}> › </span>
            <span style={{cursor:"pointer",color:C.amber}} onClick={()=>setPage("products")}>{lang==="ar"?"المنتجات":"Products"}</span>
            <span style={{color:"rgba(255,255,255,0.5)"}}> › </span>
            <span style={{color:"#FFFFFF"}}>{lang==="ar"?"جميع المنتجات":"All Products"}</span>
          </div>
          <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:40,fontWeight:900,color:"#FFFFFF"}}>{lang==="ar"?"جميع المنتجات":"ALL PRODUCTS"}</h1>
          <p style={{color:"rgba(255,255,255,0.82)",fontWeight:600,fontSize:13.5,marginTop:6}}>{lang==="ar"?"ابحث في جميع المنتجات والفئات الفرعية، في كل قطاع نخدمه.":"Search across every product and subcategory, in every industry we serve."}</p>
          <div style={{position:"relative",maxWidth:560,marginTop:20}}>
            <span style={{position:"absolute",left:lang==="ar"?"auto":14,right:lang==="ar"?14:"auto",top:"50%",transform:"translateY(-50%)",fontSize:16,color:"rgba(255,255,255,.6)"}}>🔍</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={lang==="ar"?"ابحث عن منتجات، فئات فرعية، قطاعات…":"Search products, subcategories, industries…"}
              style={{width:"100%",padding:lang==="ar"?"14px 40px 14px 16px":"14px 16px 14px 40px",borderRadius:10,border:"1px solid rgba(255,255,255,.25)",background:"rgba(255,255,255,.08)",color:"#FFFFFF",fontSize:14,outline:"none"}}/>
          </div>
        </div>
      </div>

      <div style={{maxWidth:1300,margin:"0 auto",padding:"40px 24px"}}>
        {!marineCats && <div style={{textAlign:"center",padding:"50px 0",color:C.textMuted}}>{lang==="ar"?"جارٍ تحميل المنتجات…":"Loading products…"}</div>}

        {marineCats && (
          <>
            {filteredMarine.length>0 && (
              <div style={{marginBottom:48}}>
                <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:14}}>{lang==="ar"?`// البحرية · ${filteredMarine.length.toLocaleString()} منتج`:`// MARINE · ${filteredMarine.length.toLocaleString()} PRODUCTS`}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:14}}>
                  {filteredMarine.slice(0,120).map((p,i)=>(
                    <div key={i} onClick={()=>goToSub("marine", p.subcategory)} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:10,overflow:"hidden",cursor:"pointer"}}>
                      {p.image ? <RealProdImg src={p.image} name={p.name} h={100}/> : <IllustrativeImg category={p.subcategory} h={100}/>}
                      <div style={{padding:"8px 10px"}}>
                        <div style={{fontSize:11,fontWeight:700,color:C.navyDeep,lineHeight:1.3}}>{p.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {filteredMarine.length>120 && <div style={{marginTop:14,fontSize:12,color:C.textMuted}}>{lang==="ar"?`+${(filteredMarine.length-120).toLocaleString()} أخرى — يرجى تحسين بحثك أو تصفح البحرية حسب الفئة الفرعية.`:`+${(filteredMarine.length-120).toLocaleString()} more — refine your search or browse Marine by subcategory.`}</div>}
              </div>
            )}

            {filteredSubs.length>0 && (
              <div>
                <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:14}}>{lang==="ar"?"// تصفح حسب الفئة الفرعية · جميع القطاعات":"// BROWSE BY SUBCATEGORY · ALL INDUSTRIES"}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:14}}>
                  {filteredSubs.slice(0,80).map((t,i)=>(
                    <div key={i} onClick={()=>goToSub(t.catSlug,t.sub)} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:10,cursor:"pointer",display:"flex",alignItems:"center",gap:10,padding:10}}>
                      <CategoryPhoto slug={t.catSlug} size={44} radius={8}/>
                      <div>
                        <div style={{fontSize:9,color:C.amber,fontWeight:700,letterSpacing:".5px"}}>{(lang==="ar"?t.cat.nameAr:t.catName).toUpperCase()}</div>
                        <div style={{fontSize:12,fontWeight:700,color:C.navyDeep,lineHeight:1.3}}>{subLabel(t.cat, t.sub, lang)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredMarine.length===0 && filteredSubs.length===0 && (
              <div style={{textAlign:"center",padding:"50px 0",color:C.textMuted}}>{lang==="ar"?`لا توجد نتائج مطابقة لـ "${search}".`:`No matches for "${search}".`}</div>
            )}
          </>
        )}
      </div>
      <Footer setPage={setPage} lang={lang}/>
    </div>
  );
};

// ================================================================
// PAGE: PRODUCTS — Categories → Subcategories → Items + Quote
// ================================================================
const BRAND_ORDER = ["Volvo Penta","Yamaha","Cummins","Perkins","Kohler","MTU","MAN","ZF","Other Brands"];
const BRAND_ICON = {"Volvo Penta":"⚙️","Yamaha":"🛥️","Cummins":"🔧","Perkins":"⚡","Kohler":"🔌","MTU":"🚤","MAN":"⚓","ZF":"🧰","Other Brands":"🛠️"};

const ProductsPage = ({ setPage, lang }) => {
  const ALL_CATS = useAllCategories();
  const [activeCat, setActiveCat] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [quoteModal, setQuoteModal] = useState(null);
  const [qForm, setQForm] = useState({name:"",company:"",email:"",phone:"",qty:"1",notes:""});
  const [submitted, setSubmitted] = useState(false);
  // Pick up a search term left by the Shop Now page's "Search the Product
  // Catalogue" link, so a search on one page can jump straight to the other.
  const [search, setSearch] = useState(() => {
    try { const s = sessionStorage.getItem("pendingProductSearch"); if (s) { sessionStorage.removeItem("pendingProductSearch"); return s; } } catch (e) {}
    return "";
  });
  const cat = activeCat ? ALL_CATS.find(c=>c.id===activeCat) : null;
  const isMarine = cat?.slug === "marine"; // Marine-only: gates the "Engine Parts by Brand" hub
  const staticCatalog = cat ? STATIC_CATALOG_MAP[cat.slug] : null;
  const hasStaticCatalog = !!staticCatalog; // true for Marine, Solar, Railways, Police & Military
  const isBrandHub = !!activeSub && activeSub.startsWith("Engine Parts by Brand");

  // Real per-industry photo catalogs — fetched lazily (and cached per
  // category) once a category with a static catalog is opened.
  const [staticCatsBySlug, setStaticCatsBySlug] = useState({});
  const marineCats = hasStaticCatalog ? (staticCatsBySlug[cat.slug] ?? null) : null;
  const [marineBrands, setMarineBrands] = useState(null);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [activeBrand, setActiveBrand] = useState(null);
  const [brandSearch, setBrandSearch] = useState("");
  const [brandPage, setBrandPage] = useState(1);

  useEffect(() => {
    if (hasStaticCatalog && staticCatsBySlug[cat.slug] === undefined) {
      const slug = cat.slug;
      fetch(`${process.env.PUBLIC_URL}/data/${staticCatalog.file}`)
        .then(r => r.json())
        .then(d => setStaticCatsBySlug(s => ({...s, [slug]: d})))
        .catch(() => setStaticCatsBySlug(s => ({...s, [slug]: []})));
    }
  }, [hasStaticCatalog, cat, staticCatalog, staticCatsBySlug]);

  // Pick up a deep link left by AllProductsPage (or anywhere else) asking us
  // to open straight into a specific category / subcategory.
  useEffect(() => {
    try {
      const slug = sessionStorage.getItem("pendingCategorySlug");
      const sub = sessionStorage.getItem("pendingSub");
      if (slug) {
        const target = ALL_CATS.find(c => c.slug === slug);
        if (target) {
          setActiveCat(target.id);
          if (sub) setActiveSub(sub);
        }
        sessionStorage.removeItem("pendingCategorySlug");
        sessionStorage.removeItem("pendingSub");
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (isBrandHub && !marineBrands && !brandsLoading) {
      setBrandsLoading(true);
      fetch(`${process.env.PUBLIC_URL}/data/marine-engine-brands.json`)
        .then(r => r.json()).then(d => { setMarineBrands(d); setBrandsLoading(false); })
        // FIX: on failure this used to only clear brandsLoading, leaving
        // marineBrands still null — the effect's own guard (!marineBrands &&
        // !brandsLoading) would then be true again the instant brandsLoading
        // flipped back to false, re-firing the fetch forever and flickering
        // the "Loading ... parts catalog..." text. Set marineBrands to an
        // empty object (a stable "tried, nothing found" sentinel) so the
        // guard is permanently satisfied and the UI settles into its normal
        // empty state instead of looping.
        .catch(() => { setMarineBrands({}); setBrandsLoading(false); });
    }
  }, [isBrandHub, marineBrands, brandsLoading]);

  useEffect(() => { setActiveBrand(null); setBrandSearch(""); setBrandPage(1); }, [activeSub]);
  useEffect(() => { setBrandPage(1); }, [brandSearch, activeBrand]);

  // Global search across our full backend catalog (every category, both
  // catalog-only and buyable/priced items) — shown when the top search bar
  // has a query and no category has been drilled into yet. This is what
  // lets a search here also surface items that are for sale in Shop Now.
  const [globalResults, setGlobalResults] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  useEffect(() => {
    if (activeCat || !search.trim()) { setGlobalResults([]); return; }
    setGlobalLoading(true);
    const term = search.trim();
    const timer = setTimeout(() => {
      fetch(`${API_BASE}/products?search=${encodeURIComponent(term)}&limit=40`)
        .then(r => r.json())
        .then(d => setGlobalResults(d.success ? (d.products||[]) : []))
        .catch(() => setGlobalResults([]))
        .finally(() => setGlobalLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, activeCat]);

  const goBuyInShop = (p) => {
    try { sessionStorage.setItem("pendingShopSearch", p.sku || p.name); } catch (e) {}
    setPage("shop");
  };

  const q = search.trim().toLowerCase();
  const visibleCategories = ALL_CATS.filter(c =>
    !q || c.name.toLowerCase().includes(q) || (c.nameAr||"").includes(q) || c.subs.some(s=>s.toLowerCase().includes(q))
  );
  const visibleSubs = cat ? cat.subs.filter(s => !q || s.toLowerCase().includes(q)) : [];

  // FIX: Railway/Solar/Police & Military each have their real photographed
  // catalog stored as ONE flat bucket (e.g. "Railway Equipment & Rolling
  // Stock Parts"), while the site's navigation offers several finer-grained
  // subcategory names (e.g. "Wagons & Rolling Stock", "Bogies & Wheelsets"...)
  // that don't literally match that bucket's name. Matching by exact name
  // alone (as before) meant every one of those subcategory clicks found
  // nothing and showed "No products match". Marine has real per-subcategory
  // buckets that DO match exactly, so it's unaffected; for a single-bucket
  // static catalog, any subcategory click now falls back to that one bucket
  // so its real photos actually show.
  const marineCatData = (hasStaticCatalog && marineCats && !isBrandHub)
    ? (marineCats.find(c => c.category === activeSub) || (marineCats.length === 1 ? marineCats[0] : null))
    : null;
  const marineItems = marineCatData ? marineCatData.items.filter(it => !q || it.name.toLowerCase().includes(q) || (it.sku||"").toLowerCase().includes(q)) : [];
  const marineItemsWithImg = marineItems.filter(it => it.image);
  const marineItemsNoImg = marineItems.filter(it => !it.image);

  const brandItemsAll = (activeBrand && marineBrands && marineBrands[activeBrand]) || [];
  const bq = brandSearch.trim().toLowerCase();
  const brandItemsFiltered = bq ? brandItemsAll.filter(it => (it.n||"").toLowerCase().includes(bq) || (it.p||"").toLowerCase().includes(bq)) : brandItemsAll;
  const BRAND_PAGE_SIZE = 60;
  const brandTotalPages = Math.max(1, Math.ceil(brandItemsFiltered.length / BRAND_PAGE_SIZE));
  const brandPageItems = brandItemsFiltered.slice((brandPage-1)*BRAND_PAGE_SIZE, brandPage*BRAND_PAGE_SIZE);

  // Admin-managed product photos for the active category, aggregated across
  // ALL of its subcategories (not filtered to one) — these come from the
  // Shop's backend catalog (Categories/Products admin panel), so as the
  // owner adds or swaps product photos for any subcategory, they show up
  // here automatically. Shown under the subcategory list, before the user
  // drills into one specific subcategory. This is the only source for the
  // 8 non-Marine industries; Marine additionally has its own large real
  // photographed catalog (marineCats, loaded above) which is aggregated
  // separately below since it predates/exceeds the backend catalog.
  const [catImageCards, setCatImageCards] = useState([]);
  const [catImageCardsLoading, setCatImageCardsLoading] = useState(false);
  useEffect(() => {
    if (!activeCat || activeSub || !cat) { setCatImageCards([]); return; }
    setCatImageCardsLoading(true);
    fetch(`${API_BASE}/products?category=${encodeURIComponent(cat.name)}&limit=100`)
      .then(r => r.json())
      .then(d => setCatImageCards((d.products||[]).filter(p => p.images && p.images.length>0)))
      .catch(() => setCatImageCards([]))
      .finally(() => setCatImageCardsLoading(false));
  }, [activeCat, activeSub, cat]);

  // Marine's own real photographed catalog, combined across every
  // subcategory (13 sections, hundreds of items, many already photographed)
  // — this is what actually answers "show me every Marine product photo
  // in one place", independent of the backend admin catalog above.
  const MARINE_GALLERY_CAP = 60;
  const marineGalleryAll = (hasStaticCatalog && marineCats)
    ? marineCats.flatMap(c => c.items.map(it => ({ ...it, subcategory: c.category }))) // include items without a photo too — they render with the logo placeholder
        .sort((a,b) => (b.image?1:0) - (a.image?1:0)) // real photos first in the top-level preview roll-up; the full list (incl. placeholders) is always available one click in, per subcategory
    : [];
  const marineGalleryItems = marineGalleryAll.slice(0, MARINE_GALLERY_CAP);
  const marineGalleryMore = Math.max(0, marineGalleryAll.length - marineGalleryItems.length);

  // FIX: this used to fabricate 8 fake placeholder products per subcategory
  // (invented model names, invented SKUs, and real brand names like "Bosch"
  // / "Vetus" attached to products that don't exist) whenever a category
  // has no real photographed catalog yet. That presented made-up inventory
  // to real customers as if it were genuine stock. Now it fetches whatever
  // real products the admin has actually entered for this exact
  // category+subcategory, and shows an honest "not listed yet, contact us"
  // state instead of inventing anything when there are none.
  const [subItems, setSubItems] = useState([]);
  const [subItemsLoading, setSubItemsLoading] = useState(false);
  useEffect(() => {
    if (!activeSub || hasStaticCatalog || !cat) { setSubItems([]); return; }
    setSubItemsLoading(true);
    fetch(`${API_BASE}/products?category=${encodeURIComponent(cat.name)}&subcategory=${encodeURIComponent(activeSub)}&limit=100`)
      .then(r => r.json())
      .then(d => setSubItems((d.products||[]).map(p => ({
        id: p._id, sku: p.sku, brand: p.brand || `Al-Awama ${cat.name}`,
        category: cat.slug, name: (lang==="ar" && p.nameAr) ? p.nameAr : p.name,
        description: (lang==="ar" && p.descriptionAr) ? p.descriptionAr : (p.description || ""),
        image: p.images?.[0] || "",
      }))))
      .catch(() => setSubItems([]))
      .finally(() => setSubItemsLoading(false));
  }, [activeSub, hasStaticCatalog, cat, lang]);

  // FIX: same bug as the shared QuoteModal component — this Product
  // Catalogue page has its own separate inline quote form, and it also
  // never actually sent the request anywhere. Now it posts to the real
  // POST /api/quotes route with validation, a loading state, and error
  // feedback instead of silently discarding every request.
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const submitQuote = async () => {
    if (quoteSubmitting) return;
    if (!qForm.name.trim() || !qForm.email.trim() || !qForm.phone.trim()) {
      setQuoteError(lang==="ar" ? "الاسم والبريد الإلكتروني والهاتف مطلوبة" : "Name, email and phone are required");
      return;
    }
    setQuoteError("");
    setQuoteSubmitting(true);
    try {
      await fetch(`${API_BASE}/quotes`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({
        customer: { name: qForm.name, email: qForm.email, phone: qForm.phone, company: qForm.company || "Personal" },
        product: quoteModal?.name || "",
        partNumber: quoteModal?.sku || "",
        qty: parseInt(qForm.qty, 10) || 1,
        notes: qForm.notes,
      })}).then(r => r.json()).then(d => { if (d.success === false) throw new Error(d.message || "Request failed"); });
      setSubmitted(true);
      setTimeout(()=>{setQuoteModal(null);setSubmitted(false);setQForm({name:"",company:"",email:"",phone:"",qty:"1",notes:""});},2500);
    } catch (e) {
      setQuoteError(e.message || (lang==="ar" ? "تعذر إرسال الطلب. حاول مرة أخرى." : "Couldn't send the request. Please try again."));
    } finally {
      setQuoteSubmitting(false);
    }
  };

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#0B3D91,#1565C0)",borderBottom:`1px solid #BAD6F0`,padding:"28px 24px",color:C.white}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          {/* Breadcrumb */}
          <div style={{fontSize:12.5,fontWeight:600,color:"rgba(255,255,255,0.85)",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
            <span style={{cursor:"pointer",color:C.amber}} onClick={()=>setPage("home")}>Home</span>
            {activeCat && <><span style={{color:"rgba(255,255,255,0.5)"}}>›</span><span style={{cursor:"pointer",color:(activeSub&&!activeBrand)?C.amber:"#FFFFFF"}} onClick={()=>setActiveSub(null)}>{cat?.name}</span></>}
            {activeSub && <><span style={{color:"rgba(255,255,255,0.5)"}}>›</span><span style={{cursor:activeBrand?"pointer":"default",color:activeBrand?C.amber:"#FFFFFF"}} onClick={()=>activeBrand&&setActiveBrand(null)}>{activeSub}</span></>}
            {activeBrand && <><span style={{color:"rgba(255,255,255,0.5)"}}>›</span><span style={{color:"#FFFFFF"}}>{activeBrand}</span></>}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
            <div>
              <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:38,fontWeight:900,color:"#FFFFFF",lineHeight:1}}>
                {activeBrand || activeSub || (lang==="ar" ? (cat?.nameAr||cat?.name) : cat?.name) || (lang==="ar"?"كتالوج المنتجات":"PRODUCT CATALOG")}
              </h1>
              <p style={{color:"rgba(255,255,255,0.82)",fontWeight:600,fontSize:13.5,marginTop:6}}>
                {activeBrand ? (lang==="ar" ? `${brandItemsAll.length.toLocaleString()} قطعة أصلية متوفرة، قابلة للبحث بالاسم أو رقم القطعة` : `${brandItemsAll.length.toLocaleString()} genuine parts on file, searchable by name or part number`)
                  : isBrandHub ? (lang==="ar" ? "اختر ماركة المحرك لتصفح القطع الأصلية" : "Select an engine brand to browse genuine parts")
                  : activeSub ? (lang==="ar" ? "اطلب عرض سعر للتوفر والتسعير" : "Request a quote for pricing & availability")
                  : activeCat ? (lang==="ar" ? `${cat.subs.length} فئات فرعية` : `${cat.subs.length} subcategories`) : (lang==="ar" ? `${ALL_CATS.length} فئات · أكثر من 40,000 قطعة` : `${ALL_CATS.length} categories · 40,000+ parts`)}
              </p>
            </div>
            <div style={{background:"#FFFBEB",border:`1px solid ${C.amberBorder}`,borderRadius:8,padding:"10px 16px",display:"flex",alignItems:"center",gap:7}}>
              <span>💬</span>
              <div>
                <div style={{fontSize:11,color:C.amber,fontWeight:700}}>{lang==="ar"?"السعر عند الطلب":"PRICE ON REQUEST"}</div>
                <div style={{fontSize:10,color:C.textMuted}}>{lang==="ar"?"انقر على أي منتج ← اطلب عرض سعر":"Click any product → Request Quote"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{maxWidth:1300,margin:"0 auto",padding:"32px 24px"}}>

        {/* SEARCH BAR */}
        <div style={{position:"relative",maxWidth:480,marginBottom:28}}>
          <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:15,color:C.textDim}}>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={activeCat ? (lang==="ar" ? `البحث في ${lang==="ar"?(cat?.nameAr||cat?.name):cat?.name}...` : `Search in ${cat?.name}...`) : (lang==="ar" ? "ابحث في جميع الفئات والمنتجات..." : "Search all categories & products...")} className="input-field" style={{paddingLeft:38}}/>
        </div>

        {/* GLOBAL PRODUCT SEARCH RESULTS — searches our full backend
            catalog across every category, so a name/SKU typed here also
            surfaces items that are for sale in Shop Now (with a "Buy in
            Shop" link) alongside catalog-only, request-a-quote items. */}
        {!activeCat && search.trim() && (
          <div style={{marginBottom:36}}>
            <div style={{fontSize:11,letterSpacing:"2px",color:C.amber,fontWeight:700,marginBottom:12,textTransform:"uppercase"}}>{lang==="ar"?"// المنتجات المطابقة":"// Matching Products"}</div>
            {globalLoading ? (
              <div style={{padding:"16px 0",color:C.textMuted,fontSize:13}}>{lang==="ar"?"جارٍ البحث…":"Searching…"}</div>
            ) : globalResults.length===0 ? (
              <div style={{padding:"16px 0",color:C.textMuted,fontSize:13}}>{lang==="ar"?`لا توجد منتجات مطابقة لـ "${search}" حتى الآن — تصفح الفئات أدناه أو تواصل معنا.`:`No products match "${search}" yet — browse categories below, or contact us.`}</div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
                {globalResults.map(p=>{
                  const displayName = (lang==="ar" && p.nameAr) ? p.nameAr : p.name;
                  const buyable = p.price>0;
                  return (
                    <div key={p._id} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden",display:"flex",flexDirection:"column"}}>
                      <ProdImg p={{image:p.images?.[0],category:p.category,sku:p.sku}} h={140}/>
                      <div style={{padding:"12px",flex:1,display:"flex",flexDirection:"column",gap:5}}>
                        <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px"}}>{p.category}{p.subcategory?` · ${p.subcategory}`:""}</div>
                        <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,lineHeight:1.3,flex:1}}>{displayName}</div>
                        {buyable ? (
                          <>
                            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:C.amber}}>{fmt(p.price)}</div>
                            <button onClick={()=>goBuyInShop(p)} className="btn-amber" style={{borderRadius:6,padding:"8px",fontSize:12,width:"100%"}}>{lang==="ar"?"🛒 اشترِ في المتجر الآن ←":"🛒 Buy in Shop Now →"}</button>
                          </>
                        ) : (
                          <button onClick={()=>setQuoteModal({name:p.name,sku:p.sku,brand:p.brand||p.category})} className="btn-navy" style={{borderRadius:6,padding:"8px",fontSize:12,width:"100%"}}>{lang==="ar"?"اطلب عرض سعر":"Request Quote"}</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ALL CATEGORIES */}
        {!activeCat && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:18}}>
            {visibleCategories.map(cat=>(
              <div key={cat.id} onClick={()=>setActiveCat(cat.id)} className="card"
                style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"26px 20px",cursor:"pointer"}}>
                <div style={{marginBottom:14}}><CategoryPhoto slug={cat.slug} size={54} radius={10}/></div>
                <div style={{fontWeight:800,fontSize:15,color:C.navyDeep,marginBottom:5}}>{lang==="ar"?(cat.nameAr||cat.name):cat.name}</div>
                <div style={{fontSize:12,color:"#4B5563",marginBottom:10}}>{lang==="ar"?`${cat.subs.length} فئات فرعية`:`${cat.subs.length} subcategories`}</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:12}}>
                  {cat.subs.slice(0,3).map(s=>(
                    <span key={s} style={{fontSize:9,background:C.navyBorder,color:C.textMuted,padding:"2px 6px",borderRadius:3}}>{subLabel(cat,s,lang)}</span>
                  ))}
                  {cat.subs.length>3 && <span style={{fontSize:9,color:C.amber,fontWeight:600}}>+{cat.subs.length-3}</span>}
                </div>
                <div style={{fontSize:12,color:C.amber,fontWeight:600}}>{lang==="ar"?"استكشف ←":"Explore →"}</div>
              </div>
            ))}
          </div>
        )}

        {/* SEE ALL PRODUCTS */}
        {!activeCat && (
          <div style={{textAlign:"center",marginTop:36}}>
            <button onClick={()=>setPage("all-products")} className="btn-navy" style={{borderRadius:9,padding:"14px 32px",fontSize:14,fontWeight:700,letterSpacing:".5px"}}>
              {lang==="ar"?"عرض جميع المنتجات ←":"SEE ALL PRODUCTS →"}
            </button>
          </div>
        )}

        {/* SUBCATEGORIES */}
        {activeCat && !activeSub && cat && (
          <div>
            <button onClick={()=>setActiveCat(null)} className="btn-navy" style={{borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:600,marginBottom:24}}>
              {lang==="ar"?"← جميع الفئات":"← All Categories"}
            </button>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:14}}>
              {visibleSubs.map(sub=>{
                const isBrandTile = isMarine && sub.startsWith("Engine Parts by Brand");
                const mc = hasStaticCatalog && marineCats ? (marineCats.find(c=>c.category===sub) || (marineCats.length===1 ? marineCats[0] : null)) : null;
                const countLabel = isBrandTile ? (lang==="ar" ? "9 كتالوجات قطع أصلية · أكثر من 40,000 قطعة" : "9 genuine parts catalogs · 40,000+ parts")
                  : mc ? (lang==="ar" ? `${mc.items.length} منتج${mc.items.filter(i=>i.image).length ? ` · ${mc.items.filter(i=>i.image).length} بصور` : ""}` : `${mc.items.length} products${mc.items.filter(i=>i.image).length ? ` · ${mc.items.filter(i=>i.image).length} with photos` : ""}`)
                  : (lang==="ar" ? "انقر لتصفح القطع" : "Click to browse parts");
                return (
                <div key={sub} onClick={()=>setActiveSub(sub)} className="card"
                  style={{background:isBrandTile?"#FFFBEB":C.navyLight,border:`1px solid ${isBrandTile?C.amberBorder:C.navyBorder}`,borderRadius:12,padding:"22px 20px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:3}}>{isBrandTile ? (lang==="ar"?"⚙️ قطع المحركات حسب الماركة":"⚙️ Engine Parts by Brand") : subLabel(cat,sub,lang)}</div>
                    <div style={{fontSize:11,color:"#4B5563"}}>{countLabel}</div>
                  </div>
                  <span style={{fontSize:20,color:C.amber}}>›</span>
                </div>
              );})}
            </div>

            {/* AGGREGATED IMAGE CARDS — every product across all subcategories
                of this industry, combined into one gallery. Marine, Solar,
                Railways and Police & Military each have their own real,
                pre-built photo catalog (STATIC_CATALOG_MAP); every other
                industry falls back to the backend admin-uploaded catalog. */}
            {hasStaticCatalog ? (
              marineGalleryItems.length>0 && (
                <div style={{marginTop:40}}>
                  <div style={{fontSize:11,letterSpacing:"2px",color:C.amber,fontWeight:700,marginBottom:6,textTransform:"uppercase"}}>{lang==="ar"?"// معرض المنتجات":"// Product Gallery"}</div>
                  <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.navyDeep,marginBottom:16}}>{lang==="ar"?`جميع صور منتجات ${cat?.nameAr||cat?.name}`:`All ${cat?.name} Product Photos`}</h3>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
                    {marineGalleryItems.map((it,idx)=>(
                      <div key={idx} onClick={()=>setActiveSub(it.subcategory)} className="card"
                        style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden",cursor:"pointer"}}>
                        <RealProdImg src={it.image} name={it.name} h={150} folder={staticCatalog.folder}/>
                        <div style={{padding:"14px"}}>
                          <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:3}}>{subLabel(cat,it.subcategory,lang).toUpperCase()}</div>
                          <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,lineHeight:1.35}}>{it.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {marineGalleryMore>0 && (
                    <div style={{textAlign:"center",marginTop:16,fontSize:12,color:C.textMuted}}>{lang==="ar"?`+${marineGalleryMore} أخرى — انقر على فئة فرعية أعلاه لعرض الكتالوج الكامل`:`+${marineGalleryMore} more — click a subcategory above to see its full catalog`}</div>
                  )}
                </div>
              )
            ) : (catImageCardsLoading || catImageCards.length>0 || PLACEHOLDER_SUBS[cat?.slug]) && (
              <div style={{marginTop:40}}>
                <div style={{fontSize:11,letterSpacing:"2px",color:C.amber,fontWeight:700,marginBottom:6,textTransform:"uppercase"}}>{lang==="ar"?"// معرض المنتجات":"// Product Gallery"}</div>
                <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.navyDeep,marginBottom:16}}>{lang==="ar"?`جميع صور منتجات ${cat?.nameAr||cat?.name}`:`All ${cat?.name} Product Photos`}</h3>
                {catImageCardsLoading ? (
                  <div style={{padding:"20px 0",color:C.textMuted,fontSize:13}}>{lang==="ar"?"جارٍ تحميل صور المنتجات…":"Loading product photos…"}</div>
                ) : catImageCards.length>0 ? (
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
                    {catImageCards.map(p=>(
                      <div key={p._id} onClick={()=>setQuoteModal({name:p.name,sku:p.sku,brand:p.brand||cat?.name})} className="card"
                        style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden",cursor:"pointer"}}>
                        <ProdImg p={{image:p.images?.[0],category:p.category,sku:p.sku}} h={150}/>
                        <div style={{padding:"14px"}}>
                          {p.subcategory && <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:3}}>{p.subcategory.toUpperCase()}</div>}
                          <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,lineHeight:1.35,marginBottom:4}}>{p.name}</div>
                          <div style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>{lang==="ar"?"رقم القطعة":"Part No"}: {p.sku}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // No admin-entered products on file for this industry yet —
                  // generic, navigational placeholder cards (same card shape
                  // as Marine's engine-brand tiles) instead of showing
                  // nothing. Each one routes straight into Request a Quote,
                  // never a fake product list.
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:16}}>
                    {PLACEHOLDER_SUBS[cat.slug].map(name=>(
                      <div key={name} onClick={()=>setQuoteModal({name:`${cat?.name||""} — ${name}`, sku:"", brand:`Al-Awama ${cat?.name||""}`})} className="card"
                        style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"24px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:14}}>
                        <span style={{fontSize:30}}>{cat.icon}</span>
                        <div>
                          <div style={{fontWeight:800,fontSize:15,color:C.navyDeep}}>{name}</div>
                          <div style={{fontSize:11,color:"#4B5563"}}>{lang==="ar"?"اطلب عرض سعر":"Request a quote"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* MARINE  ENGINE BRAND HUB */}
        {activeSub && isBrandHub && !activeBrand && (
          <div>
            <button onClick={()=>setActiveSub(null)} className="btn-navy" style={{borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:600,marginBottom:24}}>
              ← {lang==="ar"?(cat?.nameAr||cat?.name):cat?.name}
            </button>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:16}}>
              {BRAND_ORDER.map(b=>(
                <div key={b} onClick={()=>setActiveBrand(b)} className="card"
                  style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"24px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:14}}>
                  <span style={{fontSize:30}}>{BRAND_ICON[b]}</span>
                  <div>
                    <div style={{fontWeight:800,fontSize:15,color:C.navyDeep}}>{b}</div>
                    <div style={{fontSize:11,color:"#4B5563"}}>
                      {brandsLoading && !marineBrands ? (lang==="ar"?"جارٍ تحميل الكتالوج…":"Loading catalog…") : marineBrands && marineBrands[b] ? (lang==="ar"?`${marineBrands[b].length.toLocaleString()} قطعة أصلية`:`${marineBrands[b].length.toLocaleString()} genuine parts`) : (lang==="ar"?"كتالوج قطع أصلية":"Genuine parts catalog")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MARINE  PARTS LIST FOR ONE BRAND (searchable + paginated) */}
        {activeSub && isBrandHub && activeBrand && (
          <div>
            <button onClick={()=>setActiveBrand(null)} className="btn-navy" style={{borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:600,marginBottom:20}}>
              {lang==="ar"?"← جميع الماركات":"← All Brands"}
            </button>
            <div style={{position:"relative",maxWidth:480,marginBottom:18}}>
              <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:15,color:C.textDim}}>🔍</span>
              <input value={brandSearch} onChange={e=>setBrandSearch(e.target.value)} placeholder={lang==="ar"?`ابحث في قطع ${activeBrand} بالاسم أو رقم القطعة...`:`Search ${activeBrand} parts by name or part number...`} className="input-field" style={{paddingLeft:38}}/>
            </div>
            {brandsLoading && !marineBrands ? (
              <div style={{padding:"40px 0",textAlign:"center",color:C.textMuted}}>{lang==="ar"?`جارٍ تحميل كتالوج قطع ${activeBrand}…`:`Loading ${activeBrand} parts catalog…`}</div>
            ) : (
              <>
                <div style={{fontSize:12,color:"#4B5563",marginBottom:10}}>
                  {lang==="ar"
                    ? `عرض ${brandPageItems.length ? ((brandPage-1)*BRAND_PAGE_SIZE+1) : 0}–${Math.min(brandPage*BRAND_PAGE_SIZE,brandItemsFiltered.length)} من ${brandItemsFiltered.length.toLocaleString()} قطعة`
                    : `Showing ${brandPageItems.length ? ((brandPage-1)*BRAND_PAGE_SIZE+1) : 0}–${Math.min(brandPage*BRAND_PAGE_SIZE,brandItemsFiltered.length)} of ${brandItemsFiltered.length.toLocaleString()} parts`}
                </div>
                <div style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden"}}>
                  {brandPageItems.map((it,idx)=>(
                    <div key={idx} onClick={()=>setQuoteModal({name:it.n||"(Unnamed Part)",sku:it.p||"N/A",brand:activeBrand})}
                      style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:14,padding:"13px 18px",borderBottom:idx<brandPageItems.length-1?`1px solid ${C.navyBorder}`:"none",cursor:"pointer"}}
                      className="card">
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,color:C.navyDeep,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.n || "(Unnamed Part)"}</div>
                        <div style={{fontSize:11,color:C.textMuted,fontFamily:"monospace",marginTop:2}}>{lang==="ar"?"رقم القطعة":"PN"}: {it.p || "N/A"} · {activeBrand}</div>
                      </div>
                      <button onClick={(e)=>{e.stopPropagation();setQuoteModal({name:it.n||"(Unnamed Part)",sku:it.p||"N/A",brand:activeBrand});}} className="btn-amber" style={{borderRadius:6,padding:"7px 14px",fontSize:11,flexShrink:0}}>{lang==="ar"?"عرض سعر":"QUOTE"}</button>
                    </div>
                  ))}
                  {brandPageItems.length===0 && <div style={{padding:"30px",textAlign:"center",color:C.textMuted,fontSize:13}}>{lang==="ar"?`لا توجد قطع مطابقة لـ "${brandSearch}".`:`No parts match "${brandSearch}".`}</div>}
                </div>
                {brandTotalPages>1 && (
                  <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:10,marginTop:20}}>
                    <button disabled={brandPage<=1} onClick={()=>setBrandPage(p=>Math.max(1,p-1))} className="btn-navy" style={{borderRadius:6,padding:"7px 14px",fontSize:12,opacity:brandPage<=1?.4:1}}>{lang==="ar"?"→ السابق":"← Prev"}</button>
                    <span style={{fontSize:12,color:C.textMuted}}>{lang==="ar"?`صفحة ${brandPage} من ${brandTotalPages.toLocaleString()}`:`Page ${brandPage} of ${brandTotalPages.toLocaleString()}`}</span>
                    <button disabled={brandPage>=brandTotalPages} onClick={()=>setBrandPage(p=>Math.min(brandTotalPages,p+1))} className="btn-navy" style={{borderRadius:6,padding:"7px 14px",fontSize:12,opacity:brandPage>=brandTotalPages?.4:1}}>{lang==="ar"?"التالي ←":"Next →"}</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* REAL EQUIPMENT SUBCATEGORY (photos + placeholder-logo items) —
            Marine, Solar, Railways, Police & Military all use their own
            static catalog here; items with no photo on file show the
            company logo (RealProdImg's built-in fallback) instead of
            being hidden, per the "show every product, logo if no photo"
            requirement. */}
        {activeSub && hasStaticCatalog && !isBrandHub && (
          <div>
            <button onClick={()=>setActiveSub(null)} className="btn-navy" style={{borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:600,marginBottom:24}}>
              ← {lang==="ar"?(cat?.nameAr||cat?.name):cat?.name}
            </button>
            {!marineCats ? (
              <div style={{padding:"40px 0",textAlign:"center",color:C.textMuted}}>{lang==="ar"?"جارٍ تحميل المنتجات…":"Loading products…"}</div>
            ) : (
              <>
                {marineItemsNoImg.length>0 && marineItemsWithImg.length>0 && (
                  <div style={{fontSize:11,color:"#4B5563",marginBottom:16,display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{width:8,height:8,borderRadius:2,background:"linear-gradient(135deg,#1565C0,#0B3D91)",display:"inline-block"}}/>
                    {lang==="ar"?"المنتجات المصوَّرة تعرض صورتنا الفعلية للمنتج؛ أما المنتجات التي تظهر بشعارنا فهي منتجات أصلية بانتظار صورة.":"Photographed items show our actual product photo; items shown with our logo are genuine catalog items awaiting a photo."}
                  </div>
                )}
                {marineItems.length>0 && (
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
                    {[...marineItemsWithImg, ...marineItemsNoImg].map((p,idx)=>(
                      <div key={idx} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden"}}>
                        <RealProdImg src={p.image} name={p.name} h={150} folder={staticCatalog.folder}/>
                        <div style={{padding:"14px"}}>
                          <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:3}}>{(lang==="ar"?(cat?.nameAr||cat?.name):cat?.name)?.toUpperCase()} · {subLabel(cat,activeSub,lang)}</div>
                          <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,lineHeight:1.35,marginBottom:12}}>{p.name}{p.sku?<span style={{display:"block",fontSize:10,color:C.textMuted,fontFamily:"monospace",fontWeight:400,marginTop:2}}>{lang==="ar"?"رقم القطعة":"PN"}: {p.sku}</span>:null}</div>
                          <div style={{background:"#FFFBEB",border:`1px solid ${C.amberBorder}`,borderRadius:7,padding:"8px 10px",marginBottom:10,display:"flex",gap:5,alignItems:"center"}}>
                            <span style={{fontSize:11}}>💬</span>
                            <span style={{fontSize:11,color:C.amber,fontWeight:600}}>{lang==="ar"?"السعر متوفر عند الطلب":"Price available on request"}</span>
                          </div>
                          <button onClick={()=>setQuoteModal({name:p.name,sku:p.sku||cat.slug.toUpperCase(),brand:`Al-Awama ${cat?.name}`})} className="btn-amber" style={{width:"100%",borderRadius:7,padding:"10px",fontSize:13}}>
                            {lang==="ar"?"اطلب عرض سعر":"REQUEST QUOTE"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {marineItems.length===0 && <div style={{padding:"30px",textAlign:"center",color:C.textMuted,fontSize:13}}>{lang==="ar"?`لا توجد منتجات مطابقة لـ "${search}".`:`No products match "${search}".`}</div>}
              </>
            )}
          </div>
        )}

        {/* PRODUCTS IN SUBCATEGORY  categories with no static catalog yet */}
        {activeSub && !hasStaticCatalog && (
          <div>
            <button onClick={()=>setActiveSub(null)} className="btn-navy" style={{borderRadius:7,padding:"8px 16px",fontSize:13,fontWeight:600,marginBottom:24}}>
              ← {lang==="ar"?(cat?.nameAr||cat?.name):cat?.name}
            </button>
            {subItemsLoading ? (
              <div style={{padding:"40px",textAlign:"center",color:C.textMuted,fontSize:13}}>{lang==="ar"?"جارٍ التحميل...":"Loading..."}</div>
            ) : subItems.length>0 ? (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
                {subItems.map(p=>(
                  <div key={p.id} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden"}}>
                    <ProdImg p={p} h={150}/>
                    <div style={{padding:"14px"}}>
                      <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:3}}>{p.brand}</div>
                      <div style={{fontSize:10,color:C.textMuted,fontFamily:"monospace",marginBottom:6}}>{lang==="ar"?"رقم القطعة":"PN"}: {p.sku}</div>
                      <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,lineHeight:1.35,marginBottom:8}}>{p.name}</div>
                      {p.description && <p style={{fontSize:11,color:"#4B5563",lineHeight:1.6,marginBottom:12}}>{p.description}</p>}
                      {/* NO PRICE  Request Quote */}
                      <div style={{background:"#FFFBEB",border:`1px solid ${C.amberBorder}`,borderRadius:7,padding:"8px 10px",marginBottom:10,display:"flex",gap:5,alignItems:"center"}}>
                        <span style={{fontSize:11}}>💬</span>
                        <span style={{fontSize:11,color:C.amber,fontWeight:600}}>{lang==="ar"?"السعر متوفر عند الطلب":"Price available on request"}</span>
                      </div>
                      <button onClick={()=>setQuoteModal(p)} className="btn-amber" style={{width:"100%",borderRadius:7,padding:"10px",fontSize:13}}>
                        {lang==="ar"?"اطلب عرض سعر":"REQUEST QUOTE"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Honest empty state — this used to show 8 fabricated placeholder
              // products here instead. No photographed catalog exists yet for
              // this subcategory, so say so and offer a direct way to ask,
              // rather than inventing inventory that doesn't exist.
              <div style={{padding:"40px 24px",textAlign:"center",background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12}}>
                <div style={{fontSize:32,marginBottom:10}}>📋</div>
                <p style={{fontSize:14,color:C.text,fontWeight:600,marginBottom:6}}>{lang==="ar"?"لم تتم إضافة منتجات مصورة لهذه الفئة الفرعية بعد.":"No photographed products are listed for this subcategory yet."}</p>
                <p style={{fontSize:12,color:C.textMuted,marginBottom:18}}>{lang==="ar"?"تواصل معنا مباشرة وسيقوم فريقنا بتزويدك بالتوفر والأسعار خلال 24 ساعة.":"Contact us directly and our team will get you availability and pricing within 24 hours."}</p>
                <button onClick={()=>setQuoteModal({name:`${cat?.name||""} — ${activeSub}`, sku:"", brand:`Al-Awama ${cat?.name||""}`})} className="btn-amber" style={{borderRadius:7,padding:"10px 22px",fontSize:13}}>
                  {lang==="ar"?"اطلب عرض سعر":"REQUEST QUOTE"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* QUOTE MODAL */}
      {quoteModal && (
        <div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
          <div onClick={()=>setQuoteModal(null)} style={{position:"absolute",inset:0,background:"rgba(5,10,20,.8)",backdropFilter:"blur(6px)"}}/>
          <div style={{position:"relative",background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:16,width:520,maxWidth:"100%",padding:"30px",boxShadow:"0 24px 64px rgba(0,0,0,.6)",animation:"fadeUp .3s ease",maxHeight:"90vh",overflowY:"auto"}}>
            {submitted ? (
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:56,marginBottom:14}}>✅</div>
                <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:900,color:C.navyDeep,marginBottom:8}}>{lang==="ar"?"تم إرسال طلب عرض السعر!":"QUOTE SUBMITTED!"}</h3>
                <p style={{color:C.textMuted,fontSize:14}}>{lang==="ar"?"سنتواصل معك خلال 24 ساعة بالتسعير.":"We'll contact you within 24 hours with pricing."}</p>
              </div>
            ):(
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
                  <div>
                    <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:4}}>{lang==="ar"?"طلب عرض سعر لـ":"REQUEST A QUOTE FOR"}</div>
                    <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:C.navyDeep,lineHeight:1.2}}>{quoteModal.name}</h3>
                    <div style={{fontSize:10,color:C.textMuted,fontFamily:"monospace",marginTop:3}}>{lang==="ar"?"رقم القطعة":"PN"}: {quoteModal.sku}</div>
                  </div>
                  <button onClick={()=>setQuoteModal(null)} style={{background:C.navyBorder,border:"none",borderRadius:6,width:30,height:30,cursor:"pointer",color:C.text,fontSize:16,flexShrink:0}}>×</button>
                </div>
                <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {(lang==="ar"
                    ? [["الاسم الكامل *","name","text"],["الشركة / السفينة","company","text"],["البريد الإلكتروني *","email","email"],["الهاتف *","phone","tel"],["الكمية المطلوبة","qty","number"]]
                    : [["Full Name *","name","text"],["Company / Vessel","company","text"],["Email *","email","email"],["Phone *","phone","tel"],["Quantity Needed","qty","number"]]
                  ).map(([lbl,k,type])=>(
                    <div key={k}>
                      <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{lbl}</label>
                      <input type={type} value={qForm[k]} onChange={e=>setQForm(f=>({...f,[k]:e.target.value}))} className="input-field" style={{fontSize:13,padding:"10px 12px"}}/>
                    </div>
                  ))}
                  <div style={{gridColumn:"1 / span 2"}}>
                    <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{lang==="ar"?"ملاحظات إضافية":"Additional Notes"}</label>
                    <textarea rows={2} className="input-field" style={{resize:"vertical",fontSize:13,padding:"10px 12px"}} value={qForm.notes} onChange={e=>setQForm(f=>({...f,notes:e.target.value}))}/>
                  </div>
                </div>
                {quoteError && <div style={{color:"#C0392B",fontSize:12,marginTop:10}}>{quoteError}</div>}
                <button onClick={submitQuote} disabled={quoteSubmitting} className="btn-amber" style={{width:"100%",borderRadius:8,padding:"13px",fontSize:15,marginTop:18,opacity:quoteSubmitting?0.7:1,cursor:quoteSubmitting?"default":"pointer"}}>
                  {quoteSubmitting ? (lang==="ar"?"جارٍ الإرسال...":"SENDING...") : (lang==="ar"?"إرسال طلب عرض السعر ←":"SUBMIT QUOTE REQUEST →")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================================
// PAGE: SHOP NOW
// ================================================================
const ShopPage = ({ setPage, addToCart, user, lang }) => {
  const ALL_CATS = useAllCategories();
  // Pick up a search term left by the Product Catalogue page's "Buy in
  // Shop Now" link, so a search on one page can jump straight to the other.
  const [search,setSearch] = useState(() => {
    try { const s = sessionStorage.getItem("pendingShopSearch"); if (s) { sessionStorage.removeItem("pendingShopSearch"); return s; } } catch (e) {}
    return "";
  });
  const [cat,setCat] = useState("all");
  const [sort,setSort] = useState("featured");
  const [added,setAdded] = useState({});
  const [products,setProducts] = useState([]);
  const [loading,setLoading] = useState(true);
  const [loadErr,setLoadErr] = useState("");
  const [catalogMatches,setCatalogMatches] = useState(0); // matches found in the (unpriced) Product Catalogue, when Shop has none

  useEffect(() => {
    setLoading(true); setLoadErr("");
    const params = new URLSearchParams({ limit: "60", priced: "true" }); // Shop Now only lists buyable (priced) items
    if (cat !== "all") params.set("category", cat);
    if (search) params.set("search", search);
    if (sort === "price-asc") params.set("sort", "price");
    else if (sort === "price-desc") params.set("sort", "-price");
    else if (sort === "featured") params.set("sort", "-featured");
    const timer = setTimeout(() => {
      fetch(`${API_BASE}/products?${params}`)
        .then(r => r.json())
        .then(d => {
          if (d.success) setProducts(d.products || []); else setLoadErr(d.message || "Could not load products");
          // No buyable matches? Check whether the same search matches items
          // in our Product Catalogue instead (quote-based, no fixed price).
          if (d.success && (d.products||[]).length===0 && search.trim()) {
            fetch(`${API_BASE}/products?search=${encodeURIComponent(search)}&priced=false&limit=1`)
              .then(r=>r.json()).then(d2=>setCatalogMatches(d2.total||0)).catch(()=>setCatalogMatches(0));
          } else setCatalogMatches(0);
        })
        .catch(() => setLoadErr("Could not reach the store. Is the backend running?"))
        .finally(() => setLoading(false));
    }, 250); // small debounce while typing a search term
    return () => clearTimeout(timer);
  }, [cat, search, sort]);

  const searchInCatalog = () => {
    try { sessionStorage.setItem("pendingProductSearch", search); } catch (e) {}
    setPage("products");
  };

  const handleAdd = p => {
    if(!user){setPage("login");return;}
    addToCart({ id:p._id, _id:p._id, name:p.name, price:p.price, sku:p.sku, brand:p.brand, category:p.category, stock:p.stock, image:p.images?.[0]||null });
    setAdded(a=>({...a,[p._id]:true}));
    setTimeout(()=>setAdded(a=>({...a,[p._id]:false})),1800);
  };

  const cats=[{slug:"all",name:"All",nameAr:"الكل"},...ALL_CATS.map(c=>({slug:c.name,name:c.name,nameAr:c.nameAr}))];

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}}>
      <div style={{background:`linear-gradient(135deg,#0D1B2E,#1A2E4A)`,borderBottom:`1px solid #BAD6F0`,padding:"28px 24px",color:C.white}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",marginBottom:8}}><span style={{cursor:"pointer",color:"#FBC74A"}} onClick={()=>setPage("home")}>{lang==="ar"?"الرئيسية":"Home"}</span> › {lang==="ar"?"تسوق الآن":"Shop Now"}</div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16}}>
            <div>
              <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:38,fontWeight:900,color:C.navyDeep,lineHeight:1}}>{lang==="ar"?"تسوق الآن":"SHOP NOW"}</h1>
              <p style={{color:'rgba(255,255,255,0.78)',fontSize:13,marginTop:5}}>{lang==="ar"?"متوفر بالمخزون · أسعار ثابتة · توصيل لجميع أنحاء المملكة العربية السعودية 🇸🇦":"In-stock · Fixed prices · Delivery across Saudi Arabia 🇸🇦"}</p>
            </div>
            {!user && (
              <div style={{background:"#FFFBEB",border:`1px solid ${C.amberBorder}`,borderRadius:10,padding:"10px 16px",display:"flex",alignItems:"center",gap:8}}>
                <span>🔐</span>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:C.amber}}>{lang==="ar"?"يلزم تسجيل الدخول لتقديم الطلبات":"Login required to place orders"}</div>
                  <button onClick={()=>setPage("login")} style={{fontSize:11,color:C.text,background:"none",border:"none",cursor:"pointer",padding:0,textDecoration:"underline"}}>{lang==="ar"?"تسجيل الدخول أو إنشاء حساب ←":"Login or Register →"}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{background:C.navyMid||C.navyLight,borderBottom:`1px solid ${C.navyBorder}`,padding:"12px 24px"}}>
        <div style={{maxWidth:1300,margin:"0 auto",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"7px 12px",gap:6,maxWidth:260}}>
            <span style={{fontSize:12,color:C.textMuted}}>🔍</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={lang==="ar"?"ابحث بالاسم أو رقم القطعة...":"Search name or SKU..."} className="input-field" style={{padding:0,border:"none",background:"none",fontSize:12}}/>
          </div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {cats.map(c=>(
              <button key={c.slug} onClick={()=>setCat(c.slug)} style={{background:cat===c.slug?C.amber:C.navyLight,border:`1px solid ${cat===c.slug?C.amber:C.navyBorder}`,borderRadius:6,padding:"6px 12px",fontSize:11,fontWeight:600,color:cat===c.slug?"#FFFFFF":C.textMuted,cursor:"pointer",transition:"all .22s",transform:cat===c.slug?"scale(1.05)":"scale(1)",boxShadow:cat===c.slug?"0 4px 14px rgba(232,160,32,.3)":""}}>
                {lang==="ar"?(c.nameAr||c.name):c.name}
              </button>
            ))}
          </div>
          <select value={sort} onChange={e=>setSort(e.target.value)} style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:7,padding:"7px 12px",color:C.text,fontSize:12,outline:"none",marginLeft:"auto"}}>
            <option value="featured">{lang==="ar"?"مميز":"Featured"}</option>
            <option value="price-asc">{lang==="ar"?"السعر ↑":"Price ↑"}</option>
            <option value="price-desc">{lang==="ar"?"السعر ↓":"Price ↓"}</option>
            <option value="rating">{lang==="ar"?"الأعلى تقييمًا":"Top Rated"}</option>
          </select>
        </div>
      </div>

      <div style={{maxWidth:1300,margin:"0 auto",padding:"28px 24px"}}>
        {loadErr ? (
          <div style={{background:"rgba(239,68,68,.08)",border:`1px solid rgba(239,68,68,.3)`,borderRadius:10,padding:"18px 22px",color:C.red,fontSize:13}}>⚠️ {loadErr}</div>
        ) : loading ? (
          <div style={{textAlign:"center",padding:"60px 0",color:C.textMuted,fontSize:13}}>{lang==="ar"?"جارٍ تحميل المنتجات…":"Loading products…"}</div>
        ) : products.length===0 ? (
          <div style={{textAlign:"center",padding:"60px 0",color:C.textMuted,fontSize:13}}>
            <div>{lang==="ar"?"لا توجد منتجات مطابقة لبحثك/تصفيتك حتى الآن.":"No products match your search/filter yet."}</div>
            {catalogMatches>0 && (
              <div style={{marginTop:14}}>
                <div style={{fontSize:13,color:C.text,marginBottom:8}}>{lang==="ar"?`وجدنا ${catalogMatches} عنصرًا مطابقًا في كتالوج منتجاتنا الكامل (بناءً على طلب عرض سعر، بدون سعر ثابت).`:`We found ${catalogMatches} matching item${catalogMatches!==1?"s":""} in our full Product Catalogue (quote-based, no fixed price).`}</div>
                <button onClick={searchInCatalog} className="btn-amber" style={{borderRadius:8,padding:"10px 22px",fontSize:13}}>{lang==="ar"?"ابحث في كتالوج المنتجات ←":"Search the Product Catalogue →"}</button>
              </div>
            )}
          </div>
        ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:18}}>
          {products.map(p=>{
            const isAdded=added[p._id];
            // Stock entered but no price yet — still listed here per spec
            // ("price to be confirmed"), shown as "000" instead of a price.
            const priceTBD = !(p.price>0);
            const disc=(p.originalPrice && !priceTBD)?Math.round((1-p.price/p.originalPrice)*100):null;
            const displayName = (lang==="ar" && p.nameAr) ? p.nameAr : p.name;
            const shopP = { image:p.images?.[0]||null, category:p.category, sku:p.sku, name:displayName };
            return(
              <div key={p._id} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,overflow:"hidden",display:"flex",flexDirection:"column"}}>
                <div style={{position:"relative"}}>
                  <ProdImg p={shopP} h={170}/>
                  {disc && <span style={{position:"absolute",top:8,right:8,background:C.red,color:C.white,fontSize:10,fontWeight:800,padding:"2px 7px",borderRadius:4}}>-{disc}%</span>}
                  {p.featured && <span style={{position:"absolute",top:8,left:8,background:C.amber,color:"#FFF",fontSize:10,fontWeight:800,padding:"2px 7px",borderRadius:4}}>★ FEATURED</span>}
                </div>
                <div style={{padding:"14px",flex:1,display:"flex",flexDirection:"column",gap:6}}>
                  <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px"}}>{p.brand||catNameAr(p.category,lang)}</div>
                  <div style={{fontSize:10,color:"#6B7280",fontFamily:"monospace",marginBottom:4}}>{lang==="ar"?"رقم القطعة":"PN"}: {p.sku}</div>
                  <div style={{fontSize:13,fontWeight:700,color:C.navyDeep,lineHeight:1.35,flex:1}}>{displayName}</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:7}}>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:C.amber}}>{priceTBD ? "000" : fmt(p.price)}</span>
                    {!priceTBD && p.originalPrice && <span style={{fontSize:11,color:C.textMuted,textDecoration:"line-through"}}>{fmt(p.originalPrice)}</span>}
                  </div>
                  {!priceTBD && <div style={{fontSize:10,color:"#6B7280",marginBottom:4}}>≈ {fmtSAR(p.price)}</div>}
                  <StockBadge stock={p.stock} lang={lang}/>
                  <button onClick={()=>handleAdd(p)} disabled={p.stock===0} style={{
                    background:p.stock===0?"#E2E8F0":isAdded?C.green:C.amber,
                    border:"none",borderRadius:7,padding:"10px",width:"100%",
                    fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:13,
                    color:p.stock===0?C.textDim:"#FFFFFF",cursor:p.stock===0?"not-allowed":"pointer",
                    transition:"all .25s cubic-bezier(.175,.885,.32,1.275)",letterSpacing:".5px",
                    boxShadow:isAdded?"0 4px 14px rgba(22,163,74,.35)":p.stock!==0?"0 4px 14px rgba(232,160,32,.25)":"",
                  }}>
                    {p.stock===0?(lang==="ar"?"نفد المخزون":"OUT OF STOCK"):isAdded?(lang==="ar"?"✓ تمت الإضافة!":"✓ ADDED!"):!user?(lang==="ar"?"🔐 سجّل الدخول للشراء":"🔐 LOGIN TO BUY"):(lang==="ar"?"أضف إلى السلة":"ADD TO CART")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
      <Footer setPage={setPage} lang={lang}/>
    </div>
  );
};

// ================================================================
// CART DRAWER
// ================================================================
const CartDrawer = ({ cart, setCart, onClose, setPage, user }) => {
  const total = cart.reduce((a,i)=>a+i.price*i.qty,0);
  const upd = (id,q) => q<=0 ? setCart(c=>c.filter(i=>i.id!==id)) : setCart(c=>c.map(i=>i.id===id?{...i,qty:q}:i));
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000}}>
      <div onClick={onClose} style={{position:"absolute",inset:0,background:"rgba(4,10,20,.78)",backdropFilter:"blur(5px)",animation:"fadeIn .2s"}}/>
      <div style={{position:"absolute",right:0,top:0,bottom:0,width:460,background:C.navyLight,borderLeft:`1px solid ${C.navyBorder}`,display:"flex",flexDirection:"column",animation:"slideDown .25s ease",boxShadow:"-8px 0 40px rgba(0,0,0,0.1)"}}>
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${C.navyBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:C.navyDeep}}>YOUR CART</h2>
            <div style={{fontSize:11,color:"#6B7280"}}>{cart.length} item{cart.length!==1?"s":""} · Saudi Arabia 🇸🇦</div>
          </div>
          <button onClick={onClose} style={{background:C.navyBorder,border:"none",borderRadius:7,width:32,height:32,cursor:"pointer",color:C.text,fontSize:18}}>×</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"14px 22px"}}>
          {cart.length===0?(
            <div style={{textAlign:"center",padding:"60px 0"}}>
              <div style={{fontSize:44,marginBottom:12}}>🛒</div>
              <div style={{fontWeight:700,color:C.white,marginBottom:6}}>Cart is empty</div>
              <button onClick={()=>{onClose();setPage("shop");}} className="btn-ghost" style={{border:`1.5px solid ${C.navyBorder}`,borderRadius:7,padding:"9px 18px",fontSize:13,color:C.text,marginTop:10}}>Browse Shop</button>
            </div>
          ):cart.map(item=>(
            <div key={item.id} style={{display:"flex",gap:12,padding:"12px 0",borderBottom:`1px solid ${C.navyBorder}`}}>
              <div style={{width:62,height:52,background:C.navy,borderRadius:7,overflow:"hidden",flexShrink:0}}><ProdImg p={item} h={52}/></div>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:C.amber,fontWeight:700}}>{item.brand}</div>
                <div style={{fontSize:12,fontWeight:600,color:C.navyDeep,lineHeight:1.3,marginBottom:6}}>{item.name}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",border:`1px solid ${C.navyBorder}`,borderRadius:5,overflow:"hidden"}}>
                    <button onClick={()=>upd(item.id,item.qty-1)} style={{background:C.skyLight,border:"none",padding:"4px 9px",cursor:"pointer",color:C.textMuted,fontSize:14,transition:"all .18s"}}>−</button>
                    <span style={{padding:"4px 10px",fontSize:12,fontWeight:700,color:C.navyDeep}}>{item.qty}</span>
                    <button onClick={()=>upd(item.id,item.qty+1)} style={{background:C.skyLight,border:"none",padding:"4px 9px",cursor:"pointer",color:C.textMuted,fontSize:14,transition:"all .18s"}}>+</button>
                  </div>
                  <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:800,color:C.amber}}>{fmt(item.price*item.qty)}</span>
                </div>
              </div>
              <button onClick={()=>setCart(c=>c.filter(i=>i.id!==item.id))} style={{background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:14,alignSelf:"flex-start",padding:2,transition:"color .2s"}} onMouseEnter={e=>e.target.style.color=C.red} onMouseLeave={e=>e.target.style.color='#4B5563'}>✕</button>
            </div>
          ))}
        </div>
        {cart.length>0&&(
          <div style={{padding:"18px 22px",borderTop:`1px solid ${C.navyBorder}`}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
              <span style={{fontSize:12,color:C.textMuted}}>Subtotal</span>
              <span style={{fontSize:14,fontWeight:700}}>{fmt(total)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
              <span style={{fontSize:12,color:C.textMuted}}>Shipping (KSA)</span>
              <span style={{fontSize:12,color:total>=200?C.green:C.textMuted}}>{total>=200?"FREE":"SAR 45"}</span>
            </div>
            {total<200&&<div style={{fontSize:10,color:C.amber,marginBottom:8}}>Add ${(200-total).toFixed(0)} more for free KSA shipping!</div>}
            <div style={{borderTop:`1px solid ${C.navyBorder}`,margin:"8px 0",paddingTop:10,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontWeight:800,color:C.white}}>Total</span>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.amber}}>{fmt(total)}</span>
            </div>
            <button onClick={()=>{onClose();!user?setPage("login"):setPage("checkout");}} className="btn-amber" style={{width:"100%",borderRadius:8,padding:"13px",fontSize:15,marginBottom:8}}>
              {user?"CHECKOUT →":"🔐 LOGIN TO CHECKOUT"}
            </button>
            <button onClick={onClose} style={{width:"100%",border:`1px solid ${C.navyBorder}`,borderRadius:7,padding:"10px",fontSize:13,color:C.textMuted,cursor:"pointer",background:"none",transition:"all .22s",fontFamily:"'DM Sans',sans-serif"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.navyDeep;e.currentTarget.style.color=C.navyDeep;e.currentTarget.style.background=C.skyLight;e.currentTarget.style.transform="translateY(-1px)"}} onMouseLeave={e=>{e.currentTarget.style.borderColor=C.navyBorder;e.currentTarget.style.color=C.textMuted;e.currentTarget.style.background="none";e.currentTarget.style.transform=""}}>Continue Shopping</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================================
// PAGE: CHECKOUT
// ================================================================
const CheckoutPage = ({ cart, setCart, setPage, user, token }) => {
  const [step,setStep]=useState(1);
  const [form,setForm]=useState({firstName:user?.name?.split(" ")[0]||"",lastName:user?.name?.split(" ")[1]||"",email:user?.email||"",phone:"",address:"",city:"Riyadh",district:"",notes:""});
  const [placed,setPlaced]=useState(false);
  const [loading,setLoading]=useState(false);
  const [orderErr,setOrderErr]=useState("");
  const [order,setOrder]=useState(null);
  const total=cart.reduce((a,i)=>a+i.price*i.qty,0);
  const u=(k,v)=>setForm(f=>({...f,[k]:v}));

  const whatsappLink = (o) => {
    const lines = [
      `Hello Al-Awama Trading! I've just placed an order.`,
      ``,
      `Order #: ${o?.orderId||"-"}`,
      `Name: ${form.firstName} ${form.lastName}`.trim(),
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      `Delivery: ${form.address}${form.district?", "+form.district:""}, ${form.city}, KSA`,
      ``,
      `Items:`,
      ...cart.map(i=>`- ${i.qty}x ${i.name}`),
      ``,
      `Total: ${fmt(total*1.15)}`,
      form.notes ? `Notes: ${form.notes}` : "",
      ``,
      `Please confirm payment & delivery details with me. Thank you!`,
    ].filter(Boolean).join("\n");
    return `https://wa.me/966560264386?text=${encodeURIComponent(lines)}`;
  };

  const placeOrder = async () => {
    setLoading(true); setOrderErr("");
    try {
      const payload = {
        customer: {
          name:    `${form.firstName} ${form.lastName}`.trim(),
          email:   form.email,
          phone:   form.phone,
          city:    form.city,
          address: form.district ? `${form.address}, ${form.district}` : form.address,
        },
        items:         cart.map(i => ({ productId: i._id || i.id, qty: i.qty })),
        paymentMethod: "Prepaid",
        notes:         form.notes,
      };
      const d = await apiFetch("/orders", { method:"POST", body:JSON.stringify(payload) }, token);
      setOrder(d.order);
      setPlaced(true);
      setCart([]);
      // Hand off to WhatsApp immediately so the team can confirm payment & delivery.
      setTimeout(()=>{ try{ window.open(whatsappLink(d.order), "_blank"); }catch{} }, 400);
    } catch (e) {
      setOrderErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  if(placed) return (
    <div style={{paddingTop:68,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:"80px 24px",background:"linear-gradient(135deg,#EFF6FF,#F0F5FA)"}}>
      <div style={{textAlign:"center",maxWidth:440,animation:"fadeUp .6s ease"}}>
        <div style={{width:76,height:76,background:C.amberGlow,border:`2px solid ${C.amber}`,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:34,margin:"0 auto 20px"}}>✓</div>
        <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:46,fontWeight:900,color:C.navyDeep,marginBottom:10}}>ORDER PLACED!</h1>
        <p style={{color:C.textMuted,fontSize:14,lineHeight:1.75,marginBottom:8}}>Thank you, <span style={{color:C.amber}}>{form.firstName}</span>! Delivering to <span style={{color:C.amber}}>{form.city}, KSA</span>.</p>
        <p style={{color:"#4B5563",fontSize:13,marginBottom:8}}>Order #{order?.orderId||"-"} · Est. delivery: 2-4 business days</p>
        <p style={{color:C.textMuted,fontSize:12,marginBottom:20}}>We've opened WhatsApp for you — send the message to confirm payment & delivery with our team.</p>
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          <a href={order?whatsappLink(order):"#"} target="_blank" rel="noreferrer"
            style={{background:"#25D366",color:"#fff",borderRadius:8,padding:"13px 24px",fontSize:14,fontWeight:700,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
            📱 CONTINUE ON WHATSAPP
          </a>
          {order?._id && (
            <a href={`${API_BASE}/orders/${order._id}/invoice?email=${encodeURIComponent(form.email)}`} target="_blank" rel="noreferrer"
              className="btn-navy" style={{borderRadius:8,padding:"13px 24px",fontSize:14,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
              📄 DOWNLOAD INVOICE
            </a>
          )}
          <button onClick={()=>setPage("shop")} className="btn-amber" style={{borderRadius:8,padding:"13px 28px",fontSize:15}}>CONTINUE SHOPPING</button>
        </div>
        {user && <button onClick={()=>setPage("orders")} style={{display:"block",margin:"16px auto 0",background:"none",border:"none",cursor:"pointer",color:C.amber,fontSize:12,fontWeight:700,textDecoration:"underline"}}>View My Orders →</button>}
      </div>
    </div>
  );

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}}>
      <div style={{maxWidth:1020,margin:"0 auto",padding:"36px 24px",display:"grid",gridTemplateColumns:"1fr 320px",gap:28}}>
        <div>
          {/* Steps */}
          <div style={{display:"flex",alignItems:"center",marginBottom:24}}>
            {[["1","Delivery"],["2","Notes"],["3","Confirm"]].map(([n,lbl],i)=>(
              <div key={n} style={{display:"flex",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:step>=+n?C.amber:C.navyBorder,color:step>=+n?C.navy:C.textMuted,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:12,transition:"all .3s"}}>{step>+n?"✓":n}</div>
                  <span style={{fontSize:13,fontWeight:step===+n?700:500,color:step>=+n?"#FFFFFF":C.textMuted}}>{lbl}</span>
                </div>
                {i<2&&<div style={{width:32,height:2,background:step>i+1?C.amber:C.navyBorder,margin:"0 8px",transition:"all .3s"}}/>}
              </div>
            ))}
          </div>

          <div style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"24px"}}>
            {step===1&&(
              <>
                <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.navyDeep,marginBottom:18}}>DELIVERY SAUDI ARABIA 🇸🇦</h2>
                <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <FormInp label="First Name *" value={form.firstName} onChange={v=>u("firstName",v)} padding="10px 12px"/>
                  <FormInp label="Last Name *" value={form.lastName} onChange={v=>u("lastName",v)} padding="10px 12px"/>
                  <FormInp label="Email *" value={form.email} onChange={v=>u("email",v)} type="email" padding="10px 12px"/>
                  <FormInp label="Saudi Phone *" value={form.phone} onChange={v=>u("phone",v)} placeholder="+966 5x xxx xxxx" padding="10px 12px"/>
                  <div style={{gridColumn:"1 / span 2"}}><FormInp label="Street Address *" value={form.address} onChange={v=>u("address",v)} padding="10px 12px"/></div>
                  <div>
                    <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>City *</label>
                    <select value={form.city} onChange={e=>u("city",e.target.value)} className="input-field" style={{fontSize:13,padding:"10px 12px",color:C.text}}>
                      {SA_CITIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <FormInp label="District / Area" value={form.district} onChange={v=>u("district",v)} padding="10px 12px"/>
                </div>
                <button onClick={()=>setStep(2)} className="btn-amber" style={{marginTop:18,borderRadius:8,padding:"12px 26px",fontSize:14}}>CONTINUE TO PAYMENT →</button>
              </>
            )}
            {step===2&&(
              <>
                <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.navyDeep,marginBottom:18}}>ORDER NOTES</h2>
                <div style={{background:"rgba(37,211,102,.08)",border:"1px solid rgba(37,211,102,.3)",borderRadius:10,padding:"14px",marginBottom:16,fontSize:13,color:C.text,lineHeight:1.6}}>
                  📱 <strong>Payment &amp; delivery are arranged directly with our team over WhatsApp.</strong> After you place the order, we'll open WhatsApp with your order details ready to send — our team will confirm payment method and delivery from there.
                </div>
                <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>Anything else we should know? (optional)</label>
                <textarea value={form.notes} onChange={e=>u("notes",e.target.value)} placeholder="e.g. preferred delivery time, special instructions..." className="input-field" rows={4} style={{fontSize:13,padding:"11px 12px",width:"100%",resize:"vertical"}}/>
                <div style={{display:"flex",gap:10,marginTop:18}}>
                  <button onClick={()=>setStep(1)} className="btn-navy" style={{borderRadius:8,padding:"11px 18px",fontSize:13,fontWeight:600}}>← Back</button>
                  <button onClick={()=>setStep(3)} className="btn-amber" style={{flex:1,borderRadius:8,padding:"12px",fontSize:14}}>REVIEW ORDER →</button>
                </div>
              </>
            )}
            {step===3&&(
              <>
                <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.navyDeep,marginBottom:18}}>CONFIRM ORDER</h2>
                <div style={{background:C.navy,borderRadius:10,padding:"14px",marginBottom:12}}>
                  <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:6}}>DELIVERING TO</div>
                  <div style={{fontSize:13,color:C.text,lineHeight:1.7}}>{form.firstName} {form.lastName} · {form.phone}<br/>{form.address}, {form.district&&form.district+", "}{form.city}, Saudi Arabia 🇸🇦<br/>{form.email}</div>
                </div>
                <div style={{background:C.navy,borderRadius:10,padding:"14px",marginBottom:14}}>
                  <div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:"1px",marginBottom:6}}>PAYMENT</div>
                  <div style={{fontSize:13,color:C.text}}>📱 Payment is required before delivery — arranged directly with our team over WhatsApp (bank transfer / online payment)</div>
                </div>
                {orderErr && <div style={{background:"rgba(239,68,68,.08)",border:`1px solid rgba(239,68,68,.3)`,borderRadius:8,padding:"9px 13px",fontSize:12,color:C.red,marginBottom:12}}>⚠️ {orderErr}</div>}
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setStep(2)} className="btn-navy" style={{borderRadius:8,padding:"11px 18px",fontSize:13,fontWeight:600}}>← Back</button>
                  <button onClick={placeOrder} disabled={loading} className="btn-amber" style={{flex:1,borderRadius:8,padding:"12px",fontSize:14,opacity:loading?.8:1}}>
                    {loading ? "⏳ Processing..." : `✓ PLACE ORDER ${fmt(total)}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Summary */}
        <div style={{position:"sticky",top:80}}>
          <div style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"18px"}}>
            <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:C.navyDeep,marginBottom:14}}>ORDER SUMMARY</h3>
            <div style={{maxHeight:240,overflowY:"auto",marginBottom:14}}>
              {cart.map(i=>(
                <div key={i.id} style={{display:"flex",gap:8,marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${C.navyBorder}`}}>
                  <div style={{width:40,height:34,background:C.navy,borderRadius:5,overflow:"hidden",flexShrink:0}}><ProdImg p={i} h={34}/></div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:600,color:C.navyDeep,lineHeight:1.3}}>{i.name}</div>
                    <div style={{fontSize:10,color:C.textMuted}}>Qty: {i.qty}</div>
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:C.amber}}>{fmt(i.price*i.qty)}</div>
                </div>
              ))}
            </div>
            {[["Subtotal",fmt(total)],["Shipping",total>=200?"FREE":"SAR 45"],["VAT 15%",fmt(total*.15)]].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:12,color:C.textMuted}}>{l}</span>
                <span style={{fontSize:12,color:l==="Shipping"&&total>=200?C.green:C.text,fontWeight:600}}>{v}</span>
              </div>
            ))}
            <div style={{borderTop:`1px solid ${C.navyBorder}`,marginTop:8,paddingTop:10,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
              <span style={{fontWeight:800,color:C.navyDeep,fontSize:14}}>Total</span>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:C.amber}}>{fmt(total*1.15)}</div>
                <div style={{fontSize:10,color:C.textMuted}}>{fmtSAR(total*1.15)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// PAGE: AUTH
// ================================================================
const AuthPage = ({ setPage, setUser, setToken }) => {
  // modes: login | register | verify | forgot | reset
  const [mode,setMode]=useState("login");
  const [form,setForm]=useState({name:"",email:"",phone:"",address:"",password:"",confirm:"",code:"",newPassword:"",newConfirm:""});
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [info,setInfo]=useState("");
  const [pendingCustomerId,setPendingCustomerId]=useState(null);
  const u=(k,v)=>setForm(f=>({...f,[k]:v}));

  const goVerify = (customerId, email, msg) => {
    setPendingCustomerId(customerId||null);
    if (email) u("email", email);
    setInfo(msg||"We've emailed you a 6-digit verification code.");
    setError("");
    setMode("verify");
  };

  const submit = async () => {
    setError(""); setInfo("");

    if (mode==="login") {
      if(!form.email||!form.password){setError("Please fill all required fields.");return;}
      setLoading(true);
      try {
        const d = await apiFetch("/auth/login", { method:"POST", body:JSON.stringify({ email:form.email, password:form.password }) });
        localStorage.setItem("customerToken", d.token);
        localStorage.setItem("customerUser", JSON.stringify(d.customer));
        setToken(d.token); setUser(d.customer); setPage("shop");
      } catch (e) {
        if (e.data?.needsVerification) { goVerify(e.data.customerId, form.email, e.message); }
        else setError(e.message);
      } finally { setLoading(false); }
      return;
    }

    if (mode==="register") {
      if(!form.name||!form.email||!form.phone||!form.address||!form.password){setError("Name, email, phone, address and password are all required.");return;}
      if(form.password!==form.confirm){setError("Passwords do not match.");return;}
      if(form.password.length<6){setError("Password must be at least 6 characters.");return;}
      setLoading(true);
      try {
        const d = await apiFetch("/auth/register", { method:"POST", body:JSON.stringify({ name:form.name, email:form.email, password:form.password, phone:form.phone, address:form.address }) });
        if (d.needsVerification) { goVerify(d.customerId, form.email, d.message); }
        else {
          localStorage.setItem("customerToken", d.token);
          localStorage.setItem("customerUser", JSON.stringify(d.customer));
          setToken(d.token); setUser(d.customer); setPage("shop");
        }
      } catch (e) { setError(e.message); } finally { setLoading(false); }
      return;
    }

    if (mode==="verify") {
      if(!form.code){setError("Enter the code we emailed you.");return;}
      setLoading(true);
      try {
        const d = await apiFetch("/auth/verify-email", { method:"POST", body:JSON.stringify({ customerId:pendingCustomerId, email:form.email, code:form.code }) });
        localStorage.setItem("customerToken", d.token);
        localStorage.setItem("customerUser", JSON.stringify(d.customer));
        setToken(d.token); setUser(d.customer); setPage("shop");
      } catch (e) { setError(e.message); } finally { setLoading(false); }
      return;
    }

    if (mode==="forgot") {
      if(!form.email){setError("Enter your account email.");return;}
      setLoading(true);
      try {
        const d = await apiFetch("/auth/forgot-password", { method:"POST", body:JSON.stringify({ email:form.email }) });
        setInfo(d.message); setMode("reset");
      } catch (e) { setError(e.message); } finally { setLoading(false); }
      return;
    }

    if (mode==="reset") {
      if(!form.code||!form.newPassword){setError("Enter the code and a new password.");return;}
      if(form.newPassword!==form.newConfirm){setError("Passwords do not match.");return;}
      if(form.newPassword.length<6){setError("Password must be at least 6 characters.");return;}
      setLoading(true);
      try {
        await apiFetch("/auth/reset-password", { method:"POST", body:JSON.stringify({ email:form.email, code:form.code, newPassword:form.newPassword }) });
        setInfo("Password reset! You can log in now.");
        setForm(f=>({...f,password:"",code:"",newPassword:"",newConfirm:""}));
        setMode("login");
      } catch (e) { setError(e.message); } finally { setLoading(false); }
      return;
    }
  };

  const resend = async () => {
    setError(""); setInfo("");
    try {
      const d = await apiFetch("/auth/resend-otp", { method:"POST", body:JSON.stringify({ email:form.email }) });
      setInfo(d.message);
    } catch (e) { setError(e.message); }
  };

  const titles = {
    login:"Customer Portal", register:"Customer Portal",
    verify:"Verify Your Email", forgot:"Reset Password", reset:"Enter New Password",
  };
  const btnLabels = {
    login:"LOGIN →", register:"CREATE ACCOUNT →",
    verify:"VERIFY →", forgot:"SEND RESET CODE →", reset:"RESET PASSWORD →",
  };

  return (
    <div style={{paddingTop:68,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:"80px 24px",background:`linear-gradient(135deg, #EFF6FF 0%, #F0F5FA 50%, #FFF7ED 100%)`}}>
      <div style={{width:"100%",maxWidth:420,animation:"fadeUp .5s ease"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{width:50,height:50,background:"#FFF",border:`1px solid ${C.navyBorder}`,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 10px",boxShadow:`0 0 20px ${C.amberGlow}`,overflow:"hidden",padding:5}}>
            <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt="Al-Awama Trading" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
          </div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:900,color:C.navyDeep}}>AL-AWAMA TRADING</div>
          <div style={{fontSize:11,color:"#6B7280"}}>{titles[mode]}</div>
        </div>
        <div style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"28px",boxShadow:"0 20px 60px rgba(0,0,0,.4)"}}>
          {(mode==="login"||mode==="register")&&(
            <div style={{display:"flex",background:"#F1F5F9",borderRadius:8,padding:4,marginBottom:22,border:`1px solid ${C.navyBorder}`}}>
              {[["login","Login"],["register","Create Account"]].map(([m,lbl])=>(
                <button key={m} onClick={()=>{setMode(m);setError("");setInfo("");}} style={{flex:1,background:mode===m?C.amber:"none",border:"none",borderRadius:6,padding:"9px",fontSize:13,fontWeight:700,color:mode===m?"#FFFFFF":C.textMuted,cursor:"pointer",transition:"all .2s"}}>{lbl}</button>
              ))}
            </div>
          )}

          {mode==="verify"&&(
            <p style={{fontSize:13,color:C.textMuted,marginBottom:16,lineHeight:1.6}}>Enter the 6-digit code we sent to <strong style={{color:C.navyDeep}}>{form.email}</strong>.</p>
          )}
          {mode==="forgot"&&(
            <p style={{fontSize:13,color:C.textMuted,marginBottom:16,lineHeight:1.6}}>Enter your account email — we'll send a reset code.</p>
          )}
          {mode==="reset"&&(
            <p style={{fontSize:13,color:C.textMuted,marginBottom:16,lineHeight:1.6}}>Enter the code from your email and choose a new password.</p>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {mode==="register"&&<FormInp label="Full Name *" value={form.name} onChange={v=>u("name",v)} placeholder="Ahmed Al-Rashid"/>}
            {(mode==="login"||mode==="register"||mode==="forgot")&&<FormInp label="Email *" value={form.email} onChange={v=>u("email",v)} type="email" placeholder="you@example.com"/>}
            {mode==="register"&&<FormInp label="Saudi Phone *" value={form.phone} onChange={v=>u("phone",v)} placeholder="+966 5x xxx xxxx"/>}
            {mode==="register"&&<FormInp label="Delivery Address *" value={form.address} onChange={v=>u("address",v)} placeholder="Street, district, city"/>}
            {mode==="login"&&<FormInp label="Password *" value={form.password} onChange={v=>u("password",v)} type="password"/>}
            {mode==="register"&&<FormInp label="Password *" value={form.password} onChange={v=>u("password",v)} type="password"/>}
            {mode==="register"&&<FormInp label="Confirm Password *" value={form.confirm} onChange={v=>u("confirm",v)} type="password"/>}
            {(mode==="verify"||mode==="reset")&&<FormInp label="Verification Code *" value={form.code} onChange={v=>u("code",v)} placeholder="123456"/>}
            {mode==="reset"&&<FormInp label="New Password *" value={form.newPassword} onChange={v=>u("newPassword",v)} type="password"/>}
            {mode==="reset"&&<FormInp label="Confirm New Password *" value={form.newConfirm} onChange={v=>u("newConfirm",v)} type="password"/>}
          </div>

          {mode==="login"&&(
            <div style={{textAlign:"right",marginTop:8}}>
              <button onClick={()=>{setMode("forgot");setError("");setInfo("");}} style={{background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:11,textDecoration:"underline"}}>Forgot password?</button>
            </div>
          )}

          {info&&<div style={{background:"rgba(37,211,102,.1)",border:"1px solid rgba(37,211,102,.3)",borderRadius:7,padding:"9px 12px",fontSize:12,color:"#0F9D58",marginTop:12}}>{info}</div>}
          {error&&<div style={{background:"rgba(239,68,68,.1)",border:`1px solid rgba(239,68,68,.28)`,borderRadius:7,padding:"9px 12px",fontSize:12,color:C.red,marginTop:12}}>{error}</div>}
          <button onClick={submit} disabled={loading} className="btn-amber" style={{width:"100%",borderRadius:8,padding:"13px",fontSize:15,marginTop:16}}>
            {loading?"⏳ Please wait...":btnLabels[mode]}
          </button>

          {mode==="verify"&&(
            <button onClick={resend} style={{width:"100%",background:"none",border:"none",cursor:"pointer",color:C.amber,fontSize:12,fontWeight:700,marginTop:10,textDecoration:"underline"}}>Resend code</button>
          )}

          <div style={{textAlign:"center",marginTop:14,fontSize:12,color:C.textMuted}}>
            {mode==="login"
              ?<>No account? <button onClick={()=>{setMode("register");setError("");setInfo("");}} style={{background:"none",border:"none",cursor:"pointer",color:C.amber,fontWeight:700,fontSize:12}}>Sign Up</button></>
              :mode==="register"
              ?<>Have account? <button onClick={()=>{setMode("login");setError("");setInfo("");}} style={{background:"none",border:"none",cursor:"pointer",color:C.amber,fontWeight:700,fontSize:12}}>Login</button></>
              :<>Back to <button onClick={()=>{setMode("login");setError("");setInfo("");}} style={{background:"none",border:"none",cursor:"pointer",color:C.amber,fontWeight:700,fontSize:12}}>Login</button></>
            }
          </div>
        </div>
        <div style={{textAlign:"center",marginTop:14,fontSize:11,color:C.textMuted}}>
          <button onClick={()=>setPage("admin-login")} style={{background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:11,textDecoration:"underline"}}>Admin Login →</button>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// PAGE: ADMIN LOGIN
// ================================================================
const AdminLoginPage = ({ setPage }) => {
  // Same fix as API_BASE above — was hardcoded to localhost, now reads a
  // build-time env var and falls back to localhost only in local dev.
  const ADMIN_URL = process.env.REACT_APP_ADMIN_URL || "http://localhost:3001";
  return (
    <div style={{paddingTop:68,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:"80px 24px",background:"linear-gradient(135deg,#EFF6FF,#F0F5FA)"}}>
      <div style={{width:"100%",maxWidth:400,animation:"fadeUp .5s ease"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{width:50,height:50,background:C.navyBorder,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 10px"}}>🔐</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:900,color:C.navyDeep}}>ADMIN ACCESS</div>
          <div style={{fontSize:11,color:"#6B7280"}}>Al-Awama Control Panel</div>
        </div>
        <div style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"26px",boxShadow:"0 20px 60px rgba(0,0,0,.45)",textAlign:"center"}}>
          <p style={{fontSize:13,color:C.textMuted,lineHeight:1.7,marginBottom:18}}>
            The admin control panel is a separate, secure app. Open it to manage products, categories, orders, customers and site content.
          </p>
          <a href={ADMIN_URL} target="_blank" rel="noreferrer" className="btn-amber" style={{display:"block",borderRadius:8,padding:"12px",fontSize:14,textDecoration:"none"}}>
            OPEN ADMIN PANEL →
          </a>
          <div style={{fontSize:10,color:"#9CA3AF",marginTop:10}}>{ADMIN_URL}</div>
          <button onClick={()=>setPage("login")} style={{width:"100%",background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:11,marginTop:16,textDecoration:"underline"}}>← Customer Login</button>
        </div>
      </div>
    </div>
  );
};

// ================================================================
// PAGE: INSPECTION & REPAIR SERVICES
const ServicesPage = ({ setPage, lang="en" }) => {
  const ALL_CATS = useAllCategories();
  const t = STR[lang].services;
  const dir = STR[lang].dir;
  const [form, setForm] = useState({ industry:"", industryOther:"", category:"", categoryOther:"", serviceType:"inspection", description:"", fileName:"" });
  const [submitted, setSubmitted] = useState(false);
  const selectedCat = ALL_CATS.find(c=>c.slug===form.industry);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = () => {
    const industryLabel = form.industry==="other" ? (form.industryOther||"Other") : (selectedCat ? selectedCat.name : "");
    const categoryLabel = form.category==="other" ? (form.categoryOther||"Other") : form.category;
    const subject = `Inspection/Repair Request ${industryLabel}${categoryLabel?` ${categoryLabel}`:""}`;
    const bodyLines = [
      `Industry: ${industryLabel}`,
      `Category / Equipment: ${categoryLabel || "-"}`,
      `Service Type: ${form.serviceType === "repair" ? "Repair" : "Inspection"}`,
      "",
      "Description:",
      form.description || "-",
      "",
      form.fileName ? `(Attachment to include manually: ${form.fileName})` : "(No file attached)",
    ];
    const mailto = `mailto:info@alawamatrading.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}} dir={dir}>
      <div style={{background:"linear-gradient(135deg,#0B3D91,#1565C0)",borderBottom:`1px solid #BAD6F0`,padding:"56px 24px",color:C.white}}>
        <div style={{maxWidth:900,margin:"0 auto",textAlign:"center"}}>
          <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:12}}>{t.kicker}</div>
          <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:44,fontWeight:900,color:"#FFFFFF",lineHeight:1.05,whiteSpace:"pre-line"}}>{t.title}</h1>
        </div>
      </div>

      <div style={{maxWidth:1000,margin:"0 auto",padding:"56px 24px"}}>
        {/* Intro paragraphs + visual */}
        <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:32,marginBottom:44,alignItems:"center"}}>
          <div>
            <p style={{fontSize:14,color:C.text,lineHeight:1.85,marginBottom:16}}>{t.intro1}</p>
            <p style={{fontSize:14,color:C.text,lineHeight:1.85}}>{t.intro2}</p>
          </div>
          <div style={{borderRadius:14,overflow:"hidden",border:`1px solid ${C.navyBorder}`,boxShadow:"0 12px 32px rgba(0,0,0,.12)",background:"#FFFFFF",display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 24px"}}>
            {/* No dedicated services photo on file yet — fall back to the
                company logo, same convention RealProdImg/IllustrativeImg use
                for any other image that isn't available, instead of a
                broken-image icon. */}
            <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt="Inspection, Maintenance & Repair" loading="lazy" style={{width:"55%",maxWidth:220,height:"auto",display:"block",objectFit:"contain",opacity:0.9}}/>
          </div>
        </div>

        {/* Form */}
        <div className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:16,padding:"32px 28px"}}>
          {submitted ? (
            <div style={{textAlign:"center",padding:"30px 0"}}>
              <div style={{fontSize:56,marginBottom:14}}>✅</div>
              <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:C.navyDeep,marginBottom:10}}>{t.successTitle}</h3>
              <p style={{color:C.textMuted,fontSize:14,maxWidth:480,margin:"0 auto"}}>{t.successBody}</p>
              <button onClick={()=>{setSubmitted(false);setForm({industry:"",industryOther:"",category:"",categoryOther:"",serviceType:"inspection",description:"",fileName:""});}} className="btn-navy" style={{borderRadius:8,padding:"10px 20px",fontSize:13,marginTop:20}}>
                {lang==="ar"?"إرسال طلب آخر":"Submit Another Request"}
              </button>
            </div>
          ) : (
            <>
              <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:C.navyDeep,marginBottom:6}}>{t.formTitle}</h2>
              <p style={{fontSize:13,color:C.textMuted,marginBottom:24}}>{t.formSubtitle}</p>

              <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:C.textMuted,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>{t.fields.industry} *</label>
                  <select className="input-field" value={form.industry} onChange={e=>{set("industry",e.target.value);set("category","");}}>
                    <option value="">{t.fields.industryPh}</option>
                    {ALL_CATS.map(c=><option key={c.slug} value={c.slug}>{lang==="ar"?c.nameAr:c.name}</option>)}
                    <option value="other">{t.fields.other}</option>
                  </select>
                  {form.industry==="other" && (
                    <input className="input-field" style={{marginTop:8}} placeholder={lang==="ar"?"يرجى تحديد القطاع":"Please specify the industry"} value={form.industryOther} onChange={e=>set("industryOther",e.target.value)}/>
                  )}
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:C.textMuted,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>{t.fields.category}</label>
                  <select className="input-field" value={form.category} onChange={e=>set("category",e.target.value)} disabled={!form.industry}>
                    <option value="">{t.fields.categoryPh}</option>
                    {(selectedCat?.subs||[]).map(s=><option key={s} value={s}>{s}</option>)}
                    <option value="other">{t.fields.other}</option>
                  </select>
                  {form.category==="other" && (
                    <input className="input-field" style={{marginTop:8}} placeholder={t.fields.categoryOtherPh} value={form.categoryOther} onChange={e=>set("categoryOther",e.target.value)}/>
                  )}
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,fontWeight:700,color:C.textMuted,display:"block",marginBottom:8,textTransform:"uppercase",letterSpacing:".5px"}}>{t.fields.serviceType} *</label>
                <div style={{display:"flex",gap:12}}>
                  {[["inspection",t.fields.inspection],["repair",t.fields.repair]].map(([val,lbl])=>(
                    <button key={val} onClick={()=>set("serviceType",val)} type="button"
                      style={{flex:1,padding:"12px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,
                        border:`2px solid ${form.serviceType===val?C.amber:C.navyBorder}`,
                        background:form.serviceType===val?C.amberGlow:"#FFFFFF",
                        color:form.serviceType===val?C.amberDark:C.textMuted}}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,fontWeight:700,color:C.textMuted,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>{t.fields.description} *</label>
                <textarea rows={4} className="input-field" style={{resize:"vertical"}} placeholder={t.fields.descriptionPh} value={form.description} onChange={e=>set("description",e.target.value)}/>
              </div>

              <div style={{marginBottom:8}}>
                <label style={{fontSize:11,fontWeight:700,color:C.textMuted,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:".5px"}}>{t.fields.upload}</label>
                <input type="file" accept=".pdf,.xls,.xlsx,.doc,.docx,.png,.jpg,.jpeg" onChange={e=>set("fileName",e.target.files?.[0]?.name||"")} style={{fontSize:13,color:C.textMuted}}/>
                <div style={{fontSize:11,color:C.textDim,marginTop:4}}>{t.fields.uploadNote}</div>
              </div>

              <div style={{background:"#FFFBEB",border:`1px solid ${C.amberBorder}`,borderRadius:8,padding:"10px 14px",fontSize:12,color:C.amberDark,lineHeight:1.6,margin:"16px 0"}}>
                💬 {t.fields.emailNote}
              </div>

              <button onClick={submit} disabled={!form.industry || !form.description} className="btn-amber" style={{width:"100%",borderRadius:8,padding:"14px",fontSize:15,opacity:(!form.industry||!form.description)?.5:1}}>
                {t.fields.submit}
              </button>
            </>
          )}
        </div>
      </div>

      <Footer setPage={setPage} lang={lang}/>
    </div>
  );
};

// ================================================================
// PAGE: ABOUT
// ================================================================
const AboutPage = ({ setPage, lang }) => {
  const t = STR[lang].about;
  return (
  <div style={{paddingTop:68,minHeight:"100vh"}} dir={STR[lang].dir}>
    <div style={{background:"linear-gradient(135deg,#0B3D91,#1565C0)",borderBottom:`1px solid #BAD6F0`,padding:"60px 24px",color:C.white}}>
      <div style={{maxWidth:900,margin:"0 auto",textAlign:"center"}}>
        <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:12}}>{t.kicker}</div>
        <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:46,fontWeight:900,color:"#FFFFFF",lineHeight:1.05,marginBottom:18,whiteSpace:"pre-line"}}>{t.title}</h1>
        <p style={{fontSize:15,color:"rgba(255,255,255,0.85)",lineHeight:1.9,maxWidth:700,margin:"0 auto"}}>{t.intro}</p>
      </div>
    </div>
    <div style={{maxWidth:1100,margin:"0 auto",padding:"64px 24px"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(230px,1fr))",gap:22,marginBottom:64}}>
        {t.stats.map((f,i)=>(
          <div key={i} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"30px 24px",textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:14}}>{f.icon}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:C.navyDeep,marginBottom:10}}>{f.title}</div>
            <div style={{fontSize:13,color:C.textMuted,lineHeight:1.75}}>{f.sub}</div>
          </div>
        ))}
      </div>
      <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:48,alignItems:"center",marginBottom:64}}>
        <div>
          <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:12}}>{t.missionKicker}</div>
          <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:C.navyDeep,marginBottom:16,lineHeight:1.05}}>{t.missionTitle}</h2>
          <p style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:14}}>{t.missionP1}</p>
          <p style={{fontSize:14,color:C.text,lineHeight:1.8,marginBottom:24}}>{t.missionP2}</p>
          <button onClick={()=>setPage("contact")} className="btn-amber" style={{borderRadius:8,padding:"12px 22px",fontSize:14}}>{t.ctaBtn}</button>
        </div>
        <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          {t.metrics.map(([v,l])=>(
            <div key={l} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"22px 16px",textAlign:"center"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:C.amber,lineHeight:1,marginBottom:6}}>{v}</div>
              <div style={{fontSize:12,color:C.textMuted}}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Certificates */}
      <div>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:10}}>{t.certsKicker}</div>
          <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:38,fontWeight:900,color:C.navyDeep}}>{t.certsTitle}</h2>
        </div>
        <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          {[
            { key:"reg", img:"registration-certificate.jpg", title:t.certRegTitle, desc:t.certRegDesc },
            { key:"tax", img:"tax-certificate.jpg", title:t.certTaxTitle, desc:t.certTaxDesc },
          ].map(c=>(
            <div key={c.key} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,overflow:"hidden"}}>
              <div style={{aspectRatio:"1 / 1.414",background:"#FFFFFF",display:"flex",alignItems:"center",justifyContent:"center",borderBottom:`1px solid ${C.navyBorder}`,position:"relative"}}>
                <img src={`${process.env.PUBLIC_URL}/images/certificates/${c.img}`} alt={c.title} style={{width:"100%",height:"100%",objectFit:"contain"}}
                  onError={e=>{e.target.style.display="none"; e.target.nextSibling.style.display="flex";}}/>
                <div style={{display:"none",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,position:"absolute",inset:0,color:C.textDim}}>
                  <div style={{fontSize:40}}>📄</div>
                  <div style={{fontSize:12,fontWeight:600}}>{t.certComingSoon}</div>
                </div>
              </div>
              <div style={{padding:"16px 18px"}}>
                <div style={{fontWeight:800,fontSize:14,color:C.navyDeep,marginBottom:4}}>{c.title}</div>
                <div style={{fontSize:12,color:C.textMuted,lineHeight:1.6}}>{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <Footer setPage={setPage} lang={lang}/>
  </div>
  );
};

// ================================================================
// PAGE: CONTACT
// ================================================================
const ContactPage = ({ setPage, lang }) => {
  const [form,setForm]=useState({name:"",company:"",email:"",phone:"",subject:"",message:""});
  const [sent,setSent]=useState(false);
  const [sending,setSending]=useState(false);
  const [error,setError]=useState("");
  const [showWeChat,setShowWeChat]=useState(false);
  const u=(k,v)=>setForm(f=>({...f,[k]:v}));
  // FIX: this previously never sent the message anywhere — it just showed
  // "MESSAGE SENT!" and cleared the form. Now it actually posts to the
  // backend (reusing the existing, working quotes/inquiries endpoint so
  // every message reaches the admin panel) with real validation and error
  // feedback instead of a form that silently discarded everything typed into it.
  const send = async () => {
    if (sending) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError(lang==="ar" ? "الاسم والبريد الإلكتروني والرسالة مطلوبة" : "Name, email and message are required");
      return;
    }
    setError("");
    setSending(true);
    try {
      await apiFetch("/quotes", { method: "POST", body: JSON.stringify({
        customer: { name: form.name, email: form.email, phone: form.phone, company: form.company || "Personal" },
        product: form.subject || "General Inquiry",
        notes: form.message,
      })});
      setSent(true);
      setTimeout(()=>setSent(false),4000);
      setForm({name:"",company:"",email:"",phone:"",subject:"",message:""});
    } catch (e) {
      setError(e.message || (lang==="ar" ? "تعذر إرسال الرسالة. حاول مرة أخرى." : "Couldn't send the message. Please try again."));
    } finally {
      setSending(false);
    }
  };
  const t = STR[lang].contact;

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}} dir={STR[lang].dir}>
      <div style={{background:"linear-gradient(135deg,#0B3D91,#1565C0)",borderBottom:`1px solid #BAD6F0`,padding:"40px 24px",color:C.white}}>
        <div style={{maxWidth:1300,margin:"0 auto"}}>
          <div style={{fontSize:11,letterSpacing:"3px",color:C.amber,fontWeight:700,marginBottom:10}}>{t.kicker}</div>
          <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:46,fontWeight:900,color:"#FFFFFF",lineHeight:1}}>{t.title}</h1>
        </div>
      </div>
      <div className="rgrid-2" style={{maxWidth:1060,margin:"0 auto",padding:"52px 24px",display:"grid",gridTemplateColumns:"1fr 1.4fr",gap:44}}>
        <div>
          <h2 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:900,color:C.navyDeep,marginBottom:8}}>{t.getInTouch}</h2>
          <p style={{fontSize:14,color:"#4B5563",lineHeight:1.7,marginBottom:28}}>{t.subtitle}</p>
          {t.items.map((item,i)=>(
            <div key={i} onClick={item.wechat?()=>setShowWeChat(true):undefined} style={{display:"flex",gap:12,marginBottom:20,cursor:item.wechat?"pointer":"default"}}>
              <div style={{width:42,height:42,background:C.amberGlow,border:`1px solid ${C.amberBorder}`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{item.icon}</div>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:item.wechat?C.amberDark:C.text,marginBottom:2,textDecoration:item.wechat?"underline":"none"}}>{item.title}</div>
                {item.lines.map((l,j)=><div key={j} style={{fontSize:13,color:"#4B5563",lineHeight:1.6}}>{l}</div>)}
              </div>
            </div>
          ))}
        </div>
        {showWeChat && (
          <div onClick={()=>setShowWeChat(false)} style={{position:"fixed",inset:0,zIndex:2100,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",background:"rgba(5,10,20,.8)",backdropFilter:"blur(6px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{position:"relative",background:"#fff",borderRadius:16,width:320,maxWidth:"100%",padding:"28px",boxShadow:"0 24px 64px rgba(0,0,0,.6)",textAlign:"center"}}>
              <button onClick={()=>setShowWeChat(false)} style={{position:"absolute",top:12,right:12,background:"#EEF1F5",border:"none",borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:15}}>×</button>
              <div style={{fontSize:15,fontWeight:800,color:"#0B3D91",marginBottom:4}}>{lang==="ar"?"تابعنا على WeChat":"Add us on WeChat"}</div>
              <div style={{fontSize:12,color:"#6B7280",marginBottom:16}}>{lang==="ar"?"امسح الرمز باستخدام تطبيق WeChat للتواصل معنا":"Scan this code with the WeChat app to reach us directly"}</div>
              <img src={`${process.env.PUBLIC_URL}/images/contact/wechat-qr.jpg`} alt="Al-Awama WeChat QR code" style={{width:"100%",borderRadius:10,border:"1px solid #E5E7EB"}}/>
            </div>
          </div>
        )}
        <div style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:14,padding:"28px"}}>
          {sent?(
            <div style={{textAlign:"center",padding:"28px 0"}}>
              <div style={{fontSize:52,marginBottom:12}}>✅</div>
              <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:C.navyDeep,marginBottom:8}}>MESSAGE SENT!</h3>
              <p style={{color:C.textMuted,fontSize:13}}>We'll respond within 24 hours.</p>
            </div>
          ):(
            <>
              <h3 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:C.navyDeep,marginBottom:20}}>SEND A MESSAGE</h3>
              <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                {[["Full Name *","name","text"],["Company / Vessel","company","text"],["Email *","email","email"],["Phone","phone","tel"]].map(([lbl,k,type])=>(
                  <div key={k}>
                    <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>{lbl}</label>
                    <input type={type} value={form[k]} onChange={e=>u(k,e.target.value)} className="input-field" style={{fontSize:13,padding:"10px 12px"}}/>
                  </div>
                ))}
                <div style={{gridColumn:"1 / span 2"}}>
                  <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>Subject</label>
                  <select value={form.subject} onChange={e=>u("subject",e.target.value)} className="input-field" style={{fontSize:13,padding:"10px 12px"}}>
                    <option value="">Select...</option>
                    {["Request a Quote","Product Inquiry","Order Support","Technical Support","Bulk Order","Other"].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{gridColumn:"1 / span 2"}}>
                  <label style={{fontSize:10,fontWeight:600,color:C.textMuted,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>Message *</label>
                  <textarea rows={4} value={form.message} onChange={e=>u("message",e.target.value)} className="input-field" style={{resize:"vertical",fontSize:13,padding:"10px 12px"}}/>
                </div>
              </div>
              {error && <div style={{color:"#C0392B",fontSize:12,marginTop:10}}>{error}</div>}
              <button onClick={send} disabled={sending} className="btn-amber" style={{width:"100%",borderRadius:8,padding:"12px",fontSize:15,marginTop:16,opacity:sending?0.7:1,cursor:sending?"default":"pointer"}}>{sending?"SENDING...":"SEND MESSAGE →"}</button>
            </>
          )}
        </div>
      </div>
      <Footer setPage={setPage} lang={lang}/>
    </div>
  );
};

// ================================================================
// FOOTER
// ================================================================
const Footer = ({ setPage, lang="en" }) => {
  const ALL_CATS = useAllCategories();
  const t = STR[lang].footer;
  const [showFooterWeChat, setShowFooterWeChat] = useState(false);
  return (
  <footer style={{background:"#FFFFFF",borderTop:`1px solid ${C.navyBorder}`,padding:"52px 24px 28px",boxShadow:"0 -2px 20px rgba(0,0,0,0.05)"}} dir={STR[lang].dir}>
    <div style={{maxWidth:1300,margin:"0 auto"}}>
      <div className="footer-grid" style={{display:"grid",gridTemplateColumns:"1.6fr 0.9fr 1.3fr 0.9fr",gap:40,marginBottom:36}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <img src={`${process.env.PUBLIC_URL}/logo-alawama.png`} alt="Al-Awama Distinctive Trading Company" style={{width:36,height:36,objectFit:"contain"}}/>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:16,fontWeight:900,color:C.navyDeep,lineHeight:1}}>{lang==="ar"?"شركة العوامة المميزة":"AL-AWAMA"}</div>
              <div style={{fontSize:8,letterSpacing:"1.5px",color:C.amber,fontWeight:700}}>{lang==="ar"?"للتجارة":"Distinctive Trading Co."}</div>
            </div>
          </div>
          <p style={{fontSize:12,color:"#4B5563",lineHeight:1.8,maxWidth:260}}>{t.tagline}</p>
          <div style={{display:"flex",gap:7,marginTop:14,flexWrap:"wrap"}}>
            {[["📱 WhatsApp","https://wa.me/966560264386","_blank"],["📧 Email","https://mail.google.com/mail/?view=cm&fs=1&to=info@alawamatrading.com","_blank"],["📞 Call","tel:+966560264386"],["💬 WeChat","wechat"]].map(([c,href,target])=>(
              href==="wechat"
              ? <button key={c} onClick={()=>setShowFooterWeChat(true)} style={{background:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 9px",fontSize:9,color:C.textMuted,cursor:"pointer",transition:"all .2s"}} onMouseEnter={e=>{e.currentTarget.style.background=C.navyDeep;e.currentTarget.style.color="#FFF"}} onMouseLeave={e=>{e.currentTarget.style.background=C.navy;e.currentTarget.style.color=C.textMuted}}>{c}</button>
              : <a key={c} href={href} target={target} rel="noreferrer" style={{background:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 9px",fontSize:9,color:C.textMuted,cursor:"pointer",transition:"all .2s",textDecoration:"none"}} onMouseEnter={e=>{e.currentTarget.style.background=C.navyDeep;e.currentTarget.style.color="#FFF"}} onMouseLeave={e=>{e.currentTarget.style.background=C.navy;e.currentTarget.style.color=C.textMuted}}>{c}</a>
            ))}
          </div>
        </div>
        {[
          {title:t.navigate,items:t.navItems},
        ].map(col=>(
          <div key={col.title}>
            <div style={{fontWeight:700,fontSize:11,letterSpacing:"1.5px",color:C.navyDeep,textTransform:"uppercase",marginBottom:14}}>{col.title}</div>
            {col.items.map(([lbl,p])=>(
              <div key={lbl} onClick={()=>setPage(p)} style={{fontSize:12,color:"#4B5563",marginBottom:9,cursor:"pointer",transition:"color .2s"}}
                onMouseEnter={e=>e.target.style.color='#F59E0B'} onMouseLeave={e=>e.target.style.color='#4B5563'}>{lbl}</div>
            ))}
          </div>
        ))}
        <div>
          <div style={{fontWeight:700,fontSize:11,letterSpacing:"1.5px",color:C.navyDeep,textTransform:"uppercase",marginBottom:14}}>{t.categories}</div>
          {/* All 9 categories, in two columns — this used to only list 5 of the 9. */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px"}}>
            {ALL_CATS.map(c=>(
              <div key={c.slug} onClick={()=>setPage("products")} style={{fontSize:12,color:"#4B5563",marginBottom:9,cursor:"pointer",transition:"color .2s"}}
                onMouseEnter={e=>e.target.style.color='#F59E0B'} onMouseLeave={e=>e.target.style.color='#4B5563'}>{lang==="ar"?(c.nameAr||c.name):c.name}</div>
            ))}
          </div>
        </div>
        {[
          {title:t.support,items:t.supportItems},
        ].map(col=>(
          <div key={col.title}>
            <div style={{fontWeight:700,fontSize:11,letterSpacing:"1.5px",color:C.navyDeep,textTransform:"uppercase",marginBottom:14}}>{col.title}</div>
            {col.items.map(([lbl,p])=>(
              <div key={lbl} onClick={()=>setPage(p)} style={{fontSize:12,color:"#4B5563",marginBottom:9,cursor:"pointer",transition:"color .2s"}}
                onMouseEnter={e=>e.target.style.color='#F59E0B'} onMouseLeave={e=>e.target.style.color='#4B5563'}>{lbl}</div>
            ))}
          </div>
        ))}
      </div>
      {showFooterWeChat && (
        <div onClick={()=>setShowFooterWeChat(false)} style={{position:"fixed",inset:0,zIndex:2100,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",background:"rgba(5,10,20,.8)",backdropFilter:"blur(6px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{position:"relative",background:"#fff",borderRadius:16,width:320,maxWidth:"100%",padding:"28px",boxShadow:"0 24px 64px rgba(0,0,0,.6)",textAlign:"center"}}>
            <button onClick={()=>setShowFooterWeChat(false)} style={{position:"absolute",top:12,right:12,background:"#EEF1F5",border:"none",borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:15}}>×</button>
            <div style={{fontSize:15,fontWeight:800,color:"#0B3D91",marginBottom:4}}>{lang==="ar"?"تابعنا على WeChat":"Add us on WeChat"}</div>
            <div style={{fontSize:12,color:"#6B7280",marginBottom:16}}>{lang==="ar"?"امسح الرمز باستخدام تطبيق WeChat للتواصل معنا":"Scan this code with the WeChat app to reach us directly"}</div>
            <img src={`${process.env.PUBLIC_URL}/images/contact/wechat-qr.jpg`} alt="Al-Awama WeChat QR code" style={{width:"100%",borderRadius:10,border:"1px solid #E5E7EB"}}/>
          </div>
        </div>
      )}
      <div style={{borderTop:`1px solid ${C.navyBorder}`,paddingTop:18,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:11,color:"#9CA3AF"}}>{t.rights}</div>
        <div style={{display:"flex",gap:16}}>
          <span style={{fontSize:10,color:"#9CA3AF"}}>info@alawamatrading.com</span>
          <span style={{fontSize:10,color:"#9CA3AF"}}>+966 560 264 386</span>
        </div>
      </div>
    </div>
  </footer>
  );
};

// ================================================================
// SCROLL TO TOP BUTTON
// ================================================================
const ScrollToTopButton = () => {
  const [show,setShow] = useState(false);
  useEffect(()=>{
    const onScroll = () => setShow(window.scrollY > 420);
    window.addEventListener("scroll", onScroll, {passive:true});
    return () => window.removeEventListener("scroll", onScroll);
  },[]);
  if(!show) return null;
  return (
    <button aria-label="Back to top" onClick={()=>window.scrollTo({top:0,behavior:"smooth"})}
      className="float-fab"
      style={{
        position:"fixed", bottom:24, right:24, zIndex:998,
        width:46, height:46, borderRadius:"50%", border:"none",
        background:C.navyDeep, color:"#fff", fontSize:18, cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center"
      }}>
      ↑
    </button>
  );
};

// ================================================================
// FLOATING CONTACT RAIL (Email / WhatsApp / WeChat)
// ================================================================
const FloatingContactRail = ({ lang="en" }) => {
  const isAr = lang==="ar";
  const side = isAr ? "left" : "right";
  const [showWeChat, setShowWeChat] = useState(false);
  // WhatsApp/Email/WeChat is the main way customers actually reach us, so
  // it needs to stay visible on every screen size — a previous version of
  // this hid the whole rail below ~1360px viewport width to avoid it
  // overlapping card content, which silently removed it for most laptop
  // and all mobile/tablet visitors. Instead it now just shrinks (smaller
  // icons, smaller edge offset) on narrower screens, the same way a
  // WhatsApp chat button behaves on most real sites — always reachable,
  // never gone.
  const [vw, setVw] = useState(typeof window!=="undefined" ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const railSize = vw < 640 ? 38 : vw < 1360 ? 42 : 46;
  const railOffset = vw < 640 ? 8 : vw < 1360 ? 10 : 16;
  const railGap = vw < 640 ? 6 : 10;
  const items = [
    {
      key:"whatsapp", label:"WhatsApp", bg:"#25D366",
      href:"https://wa.me/966560264386",
      icon:(
        <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M12.04 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.4 1.26 4.83L2 22l5.35-1.28a9.9 9.9 0 0 0 4.69 1.2h.01c5.5 0 9.96-4.46 9.96-9.96S17.54 2 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.75.76-3.03-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.53 3.69-8.22 8.31-8.22 2.22 0 4.3.87 5.87 2.44a8.24 8.24 0 0 1 2.43 5.83c0 4.53-3.7 8.23-8.31 8.23zm4.55-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.4-.12-.56.13-.17.25-.65.8-.79.97-.15.17-.29.19-.54.06-.25-.12-1.05-.38-1.99-1.22-.74-.65-1.23-1.46-1.38-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.24-.42.08-.17.04-.31-.02-.44-.06-.12-.56-1.34-.76-1.83-.2-.48-.4-.42-.56-.42-.14-.01-.31-.01-.48-.01a.9.9 0 0 0-.66.31c-.23.25-.86.84-.86 2.04s.89 2.37 1.01 2.53c.12.17 1.75 2.67 4.24 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.07-.11-.23-.17-.48-.29z"/></svg>
      )
    },
    {
      key:"email", label:"Email", bg:C.navyDeep,
      href:"https://mail.google.com/mail/?view=cm&fs=1&to=info@alawamatrading.com",
      icon:(
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 6l10 7L22 6"/></svg>
      )
    },
    {
      key:"wechat", label:"WeChat", bg:"#07C160",
      onClick:(e)=>{ e.preventDefault(); setShowWeChat(true); },
      title:isAr ? "امسح رمز الاستجابة السريعة لإضافتنا على WeChat" : "Scan the QR code to add us on WeChat",
      icon:(
        <svg width="21" height="21" viewBox="0 0 24 24" fill="#fff"><path d="M8.5 3C4.36 3 1 5.83 1 9.3c0 1.98 1.1 3.75 2.83 4.9l-.7 2.1 2.45-1.23c.65.18 1.34.29 2.06.32-.1-.4-.15-.82-.15-1.24 0-3.6 3.56-6.5 7.96-6.5.27 0 .53.01.79.03C15.68 4.5 12.4 3 8.5 3zm-2.4 3.9a.95.95 0 1 1 0 1.9.95.95 0 0 1 0-1.9zm4.8 0a.95.95 0 1 1 0 1.9.95.95 0 0 1 0-1.9zM16 9.75c-3.86 0-6.9 2.6-6.9 5.8 0 3.2 3.04 5.8 6.9 5.8.6 0 1.19-.07 1.75-.19l2.15 1.08-.6-1.85C20.8 19.1 21.9 17.55 21.9 15.55c0-3.2-3.04-5.8-5.9-5.8zm-2.15 3.35a.8.8 0 1 1 0 1.6.8.8 0 0 1 0-1.6zm4.3 0a.8.8 0 1 1 0 1.6.8.8 0 0 1 0-1.6z"/></svg>
      )
    }
  ];
  return (
    <>
    <div style={{
      position:"fixed", [side]:railOffset, top:"50%", transform:"translateY(-50%)",
      zIndex:997, display:"flex", flexDirection:"column", gap:railGap
    }}>
      {items.map(it=>(
        <a key={it.key} href={it.href||"#"} onClick={it.onClick} target={(it.key==="whatsapp"||it.key==="email")?"_blank":undefined} rel="noreferrer"
          title={it.title || it.label} aria-label={it.label}
          className="float-fab"
          style={{
            width:railSize, height:railSize, borderRadius:"50%", background:it.bg,
            display:"flex", alignItems:"center", justifyContent:"center",
            textDecoration:"none", boxShadow:"0 2px 10px rgba(0,0,0,.25)"
          }}>
          {it.icon}
        </a>
      ))}
    </div>
    {showWeChat && (
      <div onClick={()=>setShowWeChat(false)} style={{position:"fixed",inset:0,zIndex:2100,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",background:"rgba(5,10,20,.8)",backdropFilter:"blur(6px)"}}>
        <div onClick={e=>e.stopPropagation()} style={{position:"relative",background:"#fff",borderRadius:16,width:320,maxWidth:"100%",padding:"28px",boxShadow:"0 24px 64px rgba(0,0,0,.6)",textAlign:"center"}}>
          <button onClick={()=>setShowWeChat(false)} style={{position:"absolute",top:12,right:12,background:"#EEF1F5",border:"none",borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:15}}>×</button>
          <div style={{fontSize:15,fontWeight:800,color:"#0B3D91",marginBottom:4}}>{isAr?"تابعنا على WeChat":"Add us on WeChat"}</div>
          <div style={{fontSize:12,color:"#6B7280",marginBottom:16}}>{isAr?"امسح الرمز باستخدام تطبيق WeChat للتواصل معنا":"Scan this code with the WeChat app to reach us directly"}</div>
          <img src={`${process.env.PUBLIC_URL}/images/contact/wechat-qr.jpg`} alt="Al-Awama WeChat QR code" style={{width:"100%",borderRadius:10,border:"1px solid #E5E7EB"}}/>
        </div>
      </div>
    )}
    </>
  );
};

// ================================================================
// ROOT APP
// ================================================================
// ================================================================
// PAGE: MY ORDERS (logged-in customers)
// ================================================================
const MyOrdersPage = ({ setPage, token, user, cart, setCart }) => {
  const [tab,setTab]=useState("orders");
  const [orders,setOrders]=useState([]);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState("");

  useEffect(() => {
    if (!token) { setPage("login"); return; }
    apiFetch("/orders/mine", {}, token)
      .then(d => setOrders(d.orders||[]))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token, setPage]);

  const statusColor = { Processing:"#1565C0", Shipped:"#7C3AED", Delivered:"#16A34A", Cancelled:"#DC2626" };
  const cartTotal = (cart||[]).reduce((a,i)=>a+i.price*i.qty,0);
  const changeQty = (id,delta) => setCart(prev => prev.map(i => i.id===id ? {...i,qty:Math.max(1,i.qty+delta)} : i));
  const removeItem = (id) => setCart(prev => prev.filter(i=>i.id!==id));

  return (
    <div style={{paddingTop:68,minHeight:"100vh"}}>
      <div style={{background:`linear-gradient(135deg,#0D1B2E,#1A2E4A)`,borderBottom:`1px solid #BAD6F0`,padding:"28px 24px",color:C.white}}>
        <div style={{maxWidth:1000,margin:"0 auto"}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",marginBottom:8}}><span style={{cursor:"pointer",color:"#FBC74A"}} onClick={()=>setPage("home")}>Home</span> › My Account</div>
          <h1 style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:34,fontWeight:900,color:C.navyDeep,lineHeight:1}}>MY ACCOUNT</h1>
          <p style={{color:'rgba(255,255,255,0.78)',fontSize:13,marginTop:5}}>{user?.name?`Signed in as ${user.name}`:""}</p>
        </div>
      </div>
      <div style={{maxWidth:1000,margin:"0 auto",padding:"32px 24px"}}>
        <div style={{display:"flex",gap:8,marginBottom:24,borderBottom:`1px solid ${C.navyBorder}`}}>
          {[["orders",`Orders${orders.length?` (${orders.length})`:""}`],["cart",`Cart${cart?.length?` (${cart.length})`:""}`]].map(([tKey,lbl])=>(
            <button key={tKey} onClick={()=>setTab(tKey)} style={{background:"none",border:"none",cursor:"pointer",padding:"10px 4px",marginRight:20,fontSize:14,fontWeight:700,color:tab===tKey?C.amber:C.textMuted,borderBottom:tab===tKey?`2px solid ${C.amber}`:"2px solid transparent"}}>{lbl}</button>
          ))}
        </div>

        {tab==="orders" && (
          err ? (
            <div style={{background:"rgba(239,68,68,.08)",border:`1px solid rgba(239,68,68,.3)`,borderRadius:10,padding:"18px 22px",color:C.red,fontSize:13}}>⚠️ {err}</div>
          ) : loading ? (
            <div style={{textAlign:"center",padding:"60px 0",color:C.textMuted,fontSize:13}}>Loading your orders…</div>
          ) : orders.length===0 ? (
            <div style={{textAlign:"center",padding:"60px 0"}}>
              <div style={{fontSize:44,marginBottom:12}}>🧾</div>
              <div style={{color:C.textMuted,fontSize:14,marginBottom:16}}>You haven't placed any orders yet.</div>
              <button onClick={()=>setPage("shop")} className="btn-amber" style={{borderRadius:8,padding:"11px 22px",fontSize:13}}>Shop Now</button>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {orders.map(o=>(
                <div key={o._id} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"18px 20px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
                    <div>
                      <div style={{fontSize:15,fontWeight:800,color:C.navyDeep}}>Order #{o.orderId}</div>
                      <div style={{fontSize:11,color:C.textMuted,marginTop:2}}>{new Date(o.createdAt).toLocaleDateString()} · {o.items.length} item{o.items.length!==1?"s":""}</div>
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:statusColor[o.status]||C.textMuted,background:`${statusColor[o.status]||C.textMuted}18`,padding:"4px 10px",borderRadius:100}}>{o.status}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:900,color:C.amber}}>{fmt(o.total)}</div>
                    <a href={`${API_BASE}/orders/${o._id}/invoice?email=${encodeURIComponent(o.customer.email)}`} target="_blank" rel="noreferrer"
                      className="btn-navy" style={{borderRadius:7,padding:"8px 16px",fontSize:12,textDecoration:"none"}}>
                      📄 Invoice
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab==="cart" && (
          (!cart || cart.length===0) ? (
            <div style={{textAlign:"center",padding:"60px 0"}}>
              <div style={{fontSize:44,marginBottom:12}}>🛒</div>
              <div style={{color:C.textMuted,fontSize:14,marginBottom:16}}>Your cart is empty.</div>
              <button onClick={()=>setPage("shop")} className="btn-amber" style={{borderRadius:8,padding:"11px 22px",fontSize:13}}>Shop Now</button>
            </div>
          ) : (
            <>
              <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
                {cart.map(i=>(
                  <div key={i.id} className="card" style={{background:C.navyLight,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"14px 16px",display:"flex",gap:14,alignItems:"center"}}>
                    <div style={{width:56,height:48,background:C.navy,borderRadius:6,overflow:"hidden",flexShrink:0}}><ProdImg p={i} h={48}/></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:700,color:C.navyDeep}}>{i.name}</div>
                      {i.sku && <div style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>Part No: {i.sku}</div>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button onClick={()=>changeQty(i.id,-1)} style={{width:26,height:26,borderRadius:6,border:`1px solid ${C.navyBorder}`,background:"none",cursor:"pointer",color:C.text}}>−</button>
                      <span style={{fontSize:13,fontWeight:700,minWidth:18,textAlign:"center"}}>{i.qty}</span>
                      <button onClick={()=>changeQty(i.id,1)} style={{width:26,height:26,borderRadius:6,border:`1px solid ${C.navyBorder}`,background:"none",cursor:"pointer",color:C.text}}>+</button>
                    </div>
                    <div style={{fontSize:14,fontWeight:800,color:C.amber,width:90,textAlign:"right"}}>{fmt(i.price*i.qty)}</div>
                    <button onClick={()=>removeItem(i.id)} style={{background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:16}}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:14,borderTop:`1px solid ${C.navyBorder}`}}>
                <div>
                  <div style={{fontSize:11,color:C.textMuted}}>Subtotal</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:26,fontWeight:900,color:C.amber}}>{fmt(cartTotal)}</div>
                </div>
                <button onClick={()=>setPage("checkout")} className="btn-amber" style={{borderRadius:8,padding:"13px 28px",fontSize:14}}>PROCEED TO CHECKOUT →</button>
              </div>
            </>
          )
        )}
      </div>
      <Footer setPage={setPage}/>
    </div>
  );
};

// Every value ever passed to setPage("...") across the app. Kept as a
// single list so the URL↔state sync below (and a future sitemap) has one
// source of truth instead of guessing at valid paths.
const PAGE_SLUGS = ["home","about","services","products","shop","contact","checkout","orders","login","admin-login","all-products"];

// The app previously never touched the URL — every "page" was just React
// state, so the address bar always stayed on "/", the browser Back/Forward
// buttons did nothing useful, reloading the page always dumped you back to
// Home, and no individual page had a bookmarkable/shareable link. This
// reads the initial page from the URL and keeps the URL in sync with
// `page` from then on (pushState on navigation, listens for Back/Forward).
function initialPageFromUrl() {
  const slug = window.location.pathname.replace(/^\/+/, "").split("/")[0];
  return PAGE_SLUGS.includes(slug) ? slug : "home";
}

const PAGE_TITLES = {
  home: "Al-Awama Distinctive Trading Company | Marine, Solar, Railway & Defense Equipment — Jeddah, Saudi Arabia",
  about: "About Us | Al-Awama Distinctive Trading Company",
  services: "Inspection, Maintenance & Repair Services | Al-Awama Trading",
  products: "Product Catalogue | Al-Awama Trading",
  shop: "Shop Now | Al-Awama Trading",
  contact: "Contact Us | Al-Awama Trading",
  checkout: "Checkout | Al-Awama Trading",
  orders: "My Orders | Al-Awama Trading",
  login: "Login | Al-Awama Trading",
  "admin-login": "Admin Login | Al-Awama Trading",
  "all-products": "All Products | Al-Awama Trading",
};

export default function App() {
  const [page,setPageState]=useState(initialPageFromUrl);
  // Drop-in replacement for the old useState setter — every existing
  // setPage("x") call site elsewhere in this file keeps working unchanged,
  // it now also updates the URL and scrolls to top like a real page nav.
  const setPage = (next) => {
    setPageState(next);
    const path = next === "home" ? "/" : `/${next}`;
    if (window.location.pathname !== path) window.history.pushState({ page: next }, "", path);
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    const onPopState = () => setPageState(initialPageFromUrl());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  useEffect(() => {
    document.title = PAGE_TITLES[page] || PAGE_TITLES.home;
  }, [page]);
  const [cart,setCart]=useState([]);
  const [showCart,setShowCart]=useState(false);
  const [user,setUser]=useState(()=>{ try{ return JSON.parse(localStorage.getItem("customerUser")); }catch{ return null; } });
  const [token,setToken]=useState(()=>localStorage.getItem("customerToken")||null);
  const [lang,setLang]=useState("en");

  const addToCart = p => setCart(prev => {
    const ex=prev.find(i=>i.id===p.id);
    return ex ? prev.map(i=>i.id===p.id?{...i,qty:i.qty+1}:i) : [...prev,{...p,qty:1}];
  });

  const logout = () => {
    localStorage.removeItem("customerToken");
    localStorage.removeItem("customerUser");
    setToken(null);
    setUser(null);
  };

  // Verify the saved session is still valid on load; clear it silently if not.
  useEffect(() => {
    if (!token) return;
    apiFetch("/auth/me", {}, token).catch(() => logout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cart ⇄ backend sync ──────────────────────────────────────
  // When the customer logs in (or a saved session is restored), pull their
  // saved cart from the backend and merge it with whatever's already in the
  // local (guest) cart, so items added before login aren't lost.
  const cartSynced = useRef(false);
  useEffect(() => {
    if (!token) { cartSynced.current = false; return; }
    apiFetch("/auth/cart", {}, token).then(d => {
      const serverItems = (d.cart||[]).map(it => ({
        id: it.product._id, _id: it.product._id, name: it.product.name, price: it.product.price,
        sku: it.product.sku, brand: it.product.brand, category: it.product.category,
        stock: it.product.stock, image: it.product.images?.[0]||null, qty: it.qty,
      }));
      setCart(prev => {
        const merged = [...serverItems];
        prev.forEach(localItem => {
          const ex = merged.find(m => m.id === localItem.id);
          if (ex) ex.qty += localItem.qty;
          else merged.push(localItem);
        });
        return merged;
      });
      cartSynced.current = true;
    }).catch(() => { cartSynced.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Push cart changes to the backend (debounced) whenever logged in.
  useEffect(() => {
    if (!token || !cartSynced.current) return;
    const t = setTimeout(() => {
      apiFetch("/auth/cart", { method:"PUT", body: JSON.stringify({ cart: cart.map(i=>({ productId: i._id||i.id, qty: i.qty })) }) }, token).catch(()=>{});
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, token]);

  useEffect(()=>{ window.scrollTo(0,0); },[page]);
  useEffect(()=>{
    document.documentElement.lang = lang;
    document.documentElement.dir = STR[lang].dir;
  },[lang]);

  return (
    <div>
      <style>{GLOBAL_CSS}</style>

      {page!=="admin-login" && (
        <Navbar page={page} setPage={setPage} cart={cart} setShowCart={setShowCart} user={user} setUser={setUser} onLogout={logout} lang={lang} setLang={setLang}/>
      )}

      {page==="home"       && <HomePage setPage={setPage} lang={lang}/>}
      {page==="products"   && <ProductsPage setPage={setPage} lang={lang}/>}
      {page==="all-products" && <AllProductsPage setPage={setPage} lang={lang}/>}
      {page==="shop"       && <ShopPage setPage={setPage} addToCart={addToCart} user={user} lang={lang}/>}
      {page==="checkout"   && <CheckoutPage cart={cart} setCart={setCart} setPage={setPage} user={user} token={token}/>}
      {page==="orders"     && <MyOrdersPage setPage={setPage} token={token} user={user} cart={cart} setCart={setCart}/>}
      {page==="services"   && <ServicesPage setPage={setPage} lang={lang}/>}
      {page==="about"      && <AboutPage setPage={setPage} lang={lang}/>}
      {page==="contact"    && <ContactPage setPage={setPage} lang={lang}/>}
      {page==="login"      && <AuthPage setPage={setPage} setUser={setUser} setToken={setToken}/>}
      {page==="admin-login"&& <AdminLoginPage setPage={setPage}/>}

      {showCart && <CartDrawer cart={cart} setCart={setCart} onClose={()=>setShowCart(false)} setPage={setPage} user={user}/>}

      {page!=="admin-login" && <ScrollToTopButton/>}
      {page!=="admin-login" && <FloatingContactRail lang={lang}/>}
    </div>
  );
}